import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase, getMessages as sbGetMessages, sendMessage as sbSendMessage, markMessagesRead as sbMarkRead, getUnreadCount as sbGetUnread, getAdminMessages as sbGetAdminMsgs, sendAdminMessage as sbSendAdminMsg, markAdminMessagesRead as sbMarkAdminRead, logError, getErrorLogs, resolveError } from './supabase';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ═══════════════════════════════════════════════════════
// DESIGN TOKENS
// ═══════════════════════════════════════════════════════
const C = {
  bg: '#060c18', s1: '#0b1321', s2: '#101c2e', s3: '#172035',
  b1: '#1a2b40', b2: '#243550',
  pri: '#0ea5e9', priDim: 'rgba(14,165,233,0.12)', priGlow: 'rgba(14,165,233,0.3)', priDark: '#0369a1',
  acc: '#f59e0b', accDim: 'rgba(245,158,11,0.12)', accGlow: 'rgba(245,158,11,0.3)',
  ok: '#10b981', okDim: 'rgba(16,185,129,0.12)',
  err: '#f43f5e', errDim: 'rgba(244,63,94,0.12)',
  pur: '#8b5cf6', purDim: 'rgba(139,92,246,0.12)',
  tx: '#dce8ff', txM: '#7a8fad', txD: '#3d5068',
  white: '#ffffff',
};

// ═══════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════
const uid = () => Math.random().toString(36).slice(2, 10);
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtTime = d => d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtMoney = n => `$${(+n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayStr = () => new Date().toISOString().slice(0, 10);
const getInitials = (name = '') => name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

// ═══════════════════════════════════════════════════════
// STORAGE (swap for Supabase later)
// ═══════════════════════════════════════════════════════
const DB = {
  get: k => { try { return JSON.parse(localStorage.getItem('ihq_' + k)); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem('ihq_' + k, JSON.stringify(v)); } catch {} },
  del: k => localStorage.removeItem('ihq_' + k),
};

// ═══════════════════════════════════════════════════════
// DEFAULT FORMULA LIBRARY
// ═══════════════════════════════════════════════════════
const DEFAULT_FORMULAS = [
  { id: 'f1', name: "Myers' Cocktail", cat: 'Wellness', price: 199, isTemplate: true, desc: 'Classic wellness infusion for energy and immune support', components: [{ name: 'Normal Saline', dose: 1000, unit: 'mL' }, { name: 'Magnesium Chloride', dose: 1, unit: 'g' }, { name: 'B-Complex', dose: 1, unit: 'mL' }, { name: 'Vitamin B12', dose: 1, unit: 'mg' }, { name: 'Vitamin C', dose: 5, unit: 'g' }, { name: 'Calcium Gluconate', dose: 1, unit: 'g' }] },
  { id: 'f2', name: 'Hangover Relief', cat: 'Recovery', price: 225, isTemplate: true, desc: 'Fast hangover and nausea relief protocol', components: [{ name: 'Normal Saline', dose: 1000, unit: 'mL' }, { name: 'Ondansetron', dose: 4, unit: 'mg' }, { name: 'Ketorolac', dose: 30, unit: 'mg' }, { name: 'B-Complex', dose: 1, unit: 'mL' }, { name: 'Magnesium', dose: 1, unit: 'g' }] },
  { id: 'f3', name: 'Immunity Boost', cat: 'Immunity', price: 175, isTemplate: true, desc: 'High-dose Vitamin C immune support blend', components: [{ name: 'Normal Saline', dose: 500, unit: 'mL' }, { name: 'Vitamin C', dose: 10, unit: 'g' }, { name: 'Zinc', dose: 5, unit: 'mg' }, { name: 'Glutathione', dose: 600, unit: 'mg' }, { name: 'B-Complex', dose: 1, unit: 'mL' }] },
  { id: 'f4', name: 'NAD+ Infusion', cat: 'Anti-Aging', price: 350, isTemplate: true, desc: 'Cellular energy and anti-aging protocol', components: [{ name: 'Normal Saline', dose: 500, unit: 'mL' }, { name: 'NAD+', dose: 500, unit: 'mg' }] },
  { id: 'f5', name: 'Athletic Recovery', cat: 'Performance', price: 199, isTemplate: true, desc: 'Muscle recovery and performance enhancement', components: [{ name: 'Normal Saline', dose: 1000, unit: 'mL' }, { name: 'Amino Acids', dose: 10, unit: 'mL' }, { name: 'Magnesium', dose: 1, unit: 'g' }, { name: 'B-Complex', dose: 1, unit: 'mL' }, { name: 'Vitamin C', dose: 3, unit: 'g' }] },
  { id: 'f6', name: 'Energy Boost', cat: 'Energy', price: 165, isTemplate: true, desc: 'Quick energy and mental clarity infusion', components: [{ name: 'Normal Saline', dose: 500, unit: 'mL' }, { name: 'Vitamin B12', dose: 2, unit: 'mg' }, { name: 'B-Complex', dose: 1, unit: 'mL' }, { name: 'Taurine', dose: 500, unit: 'mg' }] },
  { id: 'f7', name: 'Beauty Glow', cat: 'Aesthetics', price: 225, isTemplate: true, desc: 'Skin brightening and antioxidant infusion', components: [{ name: 'Normal Saline', dose: 500, unit: 'mL' }, { name: 'Glutathione', dose: 1200, unit: 'mg' }, { name: 'Vitamin C', dose: 5, unit: 'g' }, { name: 'Biotin', dose: 5, unit: 'mg' }] },
  { id: 'f8', name: 'Weight Loss', cat: 'Wellness', price: 249, isTemplate: true, desc: 'Lipotropic fat-burning metabolic support', components: [{ name: 'Normal Saline', dose: 500, unit: 'mL' }, { name: 'MIC Blend', dose: 1, unit: 'mL' }, { name: 'L-Carnitine', dose: 500, unit: 'mg' }, { name: 'B-Complex', dose: 1, unit: 'mL' }, { name: 'Vitamin B12', dose: 1, unit: 'mg' }] },
];

const CAT_COLORS = { Wellness: C.ok, Recovery: C.acc, Immunity: C.pri, 'Anti-Aging': C.pur, Performance: C.err, Energy: C.acc, Aesthetics: C.pur, Custom: C.txM };

// ═══════════════════════════════════════════════════════
// STRIPE CONFIG
// ═══════════════════════════════════════════════════════
const STRIPE_PRICES = {
  starter:  { monthly: 'price_1THy84B0WpRalfPpx2nzbyGm', yearly: 'price_1THy8fB0WpRalfPpIwBUqIze' },
  growth:   { monthly: 'price_1THy98B0WpRalfPpHrX6u80u', yearly: 'price_1THy9ZB0WpRalfPplwjH2Z4S' },
  multi:    { monthly: 'price_1THy9xB0WpRalfPpcbPs4rZu', yearly: 'price_1THyAHB0WpRalfPp2GkeFTKq' },
};

const PLAN_PRICES = {
  starter:  { monthly: 149, yearly: 149 * 12 },
  growth:   { monthly: 299, yearly: 299 * 12 },
  multi:    { monthly: 549, yearly: 549 * 12 },
};

const PLAN_NAMES = { starter: 'Starter', growth: 'Growth', multi: 'Multi-Location', enterprise: 'Enterprise', trial: 'Free Trial' };

async function initiateCheckout({ priceId, email, clinicName, userId, referralCode }) {
  const res = await fetch('/api/create-checkout-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ priceId, email, clinicName, userId, referralCode }),
  });
  const data = await res.json();
  if (data.url) window.location.href = data.url;
  else throw new Error(data.error || 'Failed to create checkout session');
}

async function openBillingPortal(customerId) {
  const res = await fetch('/api/create-portal-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customerId }),
  });
  const data = await res.json();
  if (data.url) window.location.href = data.url;
  else throw new Error(data.error || 'Failed to open billing portal');
}

const genReferralCode = (userId) => (userId || '').slice(0, 8).toUpperCase();

// ═══════════════════════════════════════════════════════
// GLOBAL STYLES HOOK
// ═══════════════════════════════════════════════════════
function useGlobalStyles() {
  useEffect(() => {
    // Viewport meta for mobile
    let meta = document.querySelector("meta[name='viewport']");
    if (!meta) { meta = document.createElement('meta'); meta.name = 'viewport'; document.head.appendChild(meta); }
    meta.content = 'width=device-width, initial-scale=1, maximum-scale=1';

    // Favicon
    const favicon = document.querySelector("link[rel='icon']") || document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/svg+xml';
    favicon.href = `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><rect width='32' height='32' rx='8' fill='url(%23g)'/><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='%230ea5e9'/><stop offset='1' stop-color='%238b5cf6'/></linearGradient></defs><path d='M16 6L8 10l8 4 8-4-8-4zM8 14l8 4 8-4M8 18l8 4 8-4' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' fill='none'/></svg>`;
    document.head.appendChild(favicon);
    document.title = 'IVPilot — IV Hydration Clinic Software';
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://fonts.googleapis.com';
    document.head.appendChild(link);
    const link2 = document.createElement('link');
    link2.rel = 'preconnect';
    link2.href = 'https://fonts.gstatic.com';
    link2.crossOrigin = 'anonymous';
    document.head.appendChild(link2);
    const link3 = document.createElement('link');
    link3.rel = 'stylesheet';
    link3.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600&display=swap';
    document.head.appendChild(link3);
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: ${C.bg}; color: ${C.tx}; font-family: 'DM Sans', sans-serif; -webkit-font-smoothing: antialiased; }
      ::-webkit-scrollbar { width: 6px; height: 6px; }
      ::-webkit-scrollbar-track { background: ${C.s1}; }
      ::-webkit-scrollbar-thumb { background: ${C.b2}; border-radius: 3px; }
      input, textarea, select { outline: none; }
      button { cursor: pointer; border: none; background: none; }
      a { text-decoration: none; color: inherit; }
      .ihq-anim { animation: fadeIn 0.25s ease; }
      @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
      @keyframes spin { to { transform: rotate(360deg); } }
      .sig-canvas { touch-action: none; }
      @media (max-width: 768px) {
        .ihq-grid-4 { grid-template-columns: repeat(2, 1fr) !important; }
        .ihq-grid-3 { grid-template-columns: repeat(1, 1fr) !important; }
        .ihq-grid-2 { grid-template-columns: 1fr !important; }
        .ihq-hide-mobile { display: none !important; }
        .ihq-mobile-pad { padding: 16px !important; }
        .ihq-mobile-stack { flex-direction: column !important; }
      }
      @media (max-width: 480px) {
        .ihq-grid-4 { grid-template-columns: 1fr 1fr !important; }
      }
    `;
    document.head.appendChild(style);
    return () => { try { document.head.removeChild(link); document.head.removeChild(style); } catch {} };
  }, []);
}

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ═══════════════════════════════════════════════════════
// SEED DEMO DATA
// ═══════════════════════════════════════════════════════
function seedDemoData(clinicId) {
  const c1 = uid(), c2 = uid(), c3 = uid(), c4 = uid();
  const s1 = uid(), s2 = uid();
  const now = Date.now();
  const daysAgo = n => new Date(now - n * 86400000).toISOString();
  const todayAt = h => { const d = new Date(); d.setHours(h, 0, 0, 0); return d.toISOString(); };
  const tmrAt = h => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(h, 0, 0, 0); return d.toISOString(); };

  const clients = [
    { id: c1, clinicId, firstName: 'Sarah', lastName: 'Johnson', email: 'sarah.j@example.com', phone: '(555) 010-1234', dob: '1988-03-15', allergies: 'Penicillin', conditions: 'Migraines, occasional fatigue', createdAt: daysAgo(32) },
    { id: c2, clinicId, firstName: 'Marcus', lastName: 'Williams', email: 'marcus.w@example.com', phone: '(555) 020-5678', dob: '1992-07-22', allergies: '', conditions: 'Athletic recovery', createdAt: daysAgo(18) },
    { id: c3, clinicId, firstName: 'Emily', lastName: 'Chen', email: 'emily.c@example.com', phone: '(555) 030-9012', dob: '1995-11-08', allergies: 'Sulfates', conditions: 'Chronic fatigue', createdAt: daysAgo(10) },
    { id: c4, clinicId, firstName: 'James', lastName: 'Rodriguez', email: 'james.r@example.com', phone: '(555) 040-3456', dob: '1985-05-30', allergies: '', conditions: 'Post-travel recovery', createdAt: daysAgo(5) },
  ];
  const staff = [
    { id: s1, clinicId, name: 'Dr. Priya Sharma', email: 'priya@clinic.com', role: 'Medical Director', phone: '(555) 100-0001', active: true },
    { id: s2, clinicId, name: 'Nurse Tyler Brooks', email: 'tyler@clinic.com', role: 'Registered Nurse', phone: '(555) 100-0002', active: true },
  ];
  const visits = [
    { id: uid(), clinicId, clientId: c1, staffId: s2, formulaId: 'f1', formulaName: "Myers' Cocktail", startedAt: daysAgo(14), completedAt: daysAgo(14), amountCharged: 199, notes: 'Client reported feeling great after session', adverseReactions: '', vitals: { bp: '120/80', hr: '72', o2: '98%', weight: '145 lbs' } },
    { id: uid(), clinicId, clientId: c2, staffId: s2, formulaId: 'f5', formulaName: 'Athletic Recovery', startedAt: daysAgo(10), completedAt: daysAgo(10), amountCharged: 199, notes: 'Post-marathon recovery session', adverseReactions: '', vitals: { bp: '118/76', hr: '65', o2: '99%', weight: '185 lbs' } },
    { id: uid(), clinicId, clientId: c3, staffId: s2, formulaId: 'f6', formulaName: 'Energy Boost', startedAt: daysAgo(7), completedAt: daysAgo(7), amountCharged: 165, notes: 'Complained of persistent fatigue. B12 levels likely low.', adverseReactions: '', vitals: { bp: '115/75', hr: '78', o2: '97%', weight: '128 lbs' } },
    { id: uid(), clinicId, clientId: c1, staffId: s2, formulaId: 'f3', formulaName: 'Immunity Boost', startedAt: daysAgo(3), completedAt: daysAgo(3), amountCharged: 175, notes: 'Follow-up treatment. Client reports 80% improvement.', adverseReactions: '', vitals: { bp: '122/82', hr: '74', o2: '98%', weight: '145 lbs' } },
    { id: uid(), clinicId, clientId: c4, staffId: s1, formulaId: 'f2', formulaName: 'Hangover Relief', startedAt: daysAgo(1), completedAt: daysAgo(1), amountCharged: 225, notes: 'Walk-in client. Post-event recovery.', adverseReactions: '', vitals: { bp: '130/88', hr: '88', o2: '96%', weight: '175 lbs' } },
  ];
  const forms = [
    { id: uid(), clinicId, clientId: c1, healthConditions: 'Migraines', medications: 'Sumatriptan 50mg as needed', allergies: 'Penicillin', bloodPressure: '120/80', pregnant: false, smoker: false, heartCondition: false, kidneyDisease: false, diabetes: false, signed: true, signedAt: daysAgo(14), signatureData: 'signed' },
    { id: uid(), clinicId, clientId: c2, healthConditions: 'None', medications: 'None', allergies: '', bloodPressure: '118/76', pregnant: false, smoker: false, heartCondition: false, kidneyDisease: false, diabetes: false, signed: true, signedAt: daysAgo(18), signatureData: 'signed' },
    { id: uid(), clinicId, clientId: c3, healthConditions: 'Chronic fatigue syndrome', medications: 'Vitamin D3 5000IU daily', allergies: 'Sulfates', bloodPressure: '115/75', pregnant: false, smoker: false, heartCondition: false, kidneyDisease: false, diabetes: false, signed: true, signedAt: daysAgo(10), signatureData: 'signed' },
  ];
  const appts = [
    { id: uid(), clinicId, clientId: c1, staffId: s2, scheduledAt: todayAt(10), duration: 60, status: 'scheduled', notes: "Myers' Cocktail follow-up", formulaId: 'f1' },
    { id: uid(), clinicId, clientId: c2, staffId: s2, scheduledAt: todayAt(14), duration: 60, status: 'scheduled', notes: 'Athletic Recovery', formulaId: 'f5' },
    { id: uid(), clinicId, clientId: c4, staffId: s1, scheduledAt: tmrAt(11), duration: 60, status: 'scheduled', notes: 'Initial consultation + Immunity Boost', formulaId: 'f3' },
    { id: uid(), clinicId, clientId: c3, staffId: s2, scheduledAt: tmrAt(15), duration: 60, status: 'scheduled', notes: 'NAD+ Infusion', formulaId: 'f4' },
  ];

  DB.set('clients', clients);
  DB.set('staff', staff);
  DB.set('visits', visits);
  DB.set('forms', forms);
  DB.set('appts', appts);
  DB.set('formulas', DEFAULT_FORMULAS);
  return { clients, staff, visits, forms, appts, formulas: DEFAULT_FORMULAS };
}

// ═══════════════════════════════════════════════════════
// PRIMITIVE COMPONENTS
// ═══════════════════════════════════════════════════════

function Btn({ children, onClick, variant = 'primary', size = 'md', disabled, style: extraStyle, type = 'button' }) {
  const [hov, setHov] = useState(false);
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    fontFamily: "'DM Sans', sans-serif", fontWeight: 500, borderRadius: 8,
    transition: 'all 0.15s ease', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap',
  };
  const sizes = { sm: { padding: '6px 12px', fontSize: 13 }, md: { padding: '9px 18px', fontSize: 14 }, lg: { padding: '12px 24px', fontSize: 15 } };
  const variants = {
    primary: { background: hov ? C.priDark : C.pri, color: C.white, boxShadow: hov ? `0 0 20px ${C.priGlow}` : 'none' },
    secondary: { background: hov ? C.b2 : C.b1, color: C.tx, border: `1px solid ${C.b2}` },
    ghost: { background: hov ? C.s3 : 'transparent', color: C.txM },
    danger: { background: hov ? '#c53030' : C.err, color: C.white },
    success: { background: hov ? '#059669' : C.ok, color: C.white },
    accent: { background: hov ? '#d97706' : C.acc, color: C.white },
    outline: { background: 'transparent', color: C.pri, border: `1px solid ${C.pri}`, ...(hov ? { background: C.priDim } : {}) },
  };
  return (
    <button type={type} disabled={disabled} onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ ...base, ...sizes[size], ...variants[variant], ...extraStyle }}>
      {children}
    </button>
  );
}

function Input({ label, value, onChange, placeholder, type = 'text', required, error, hint, style: extraStyle, onKeyDown }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}{required && <span style={{ color: C.err }}> *</span>}</label>}
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} onKeyDown={onKeyDown}
        style={{ background: C.s3, border: `1px solid ${error ? C.err : focused ? C.pri : C.b1}`, borderRadius: 8, padding: '10px 14px', fontSize: 14, color: C.tx, fontFamily: "'DM Sans', sans-serif", transition: 'border 0.15s', width: '100%', boxShadow: focused ? `0 0 0 3px ${C.priDim}` : 'none', ...extraStyle }} />
      {error && <span style={{ fontSize: 12, color: C.err }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: 12, color: C.txD }}>{hint}</span>}
    </div>
  );
}

function Textarea({ label, value, onChange, placeholder, rows = 3, style: extraStyle }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>}
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ background: C.s3, border: `1px solid ${focused ? C.pri : C.b1}`, borderRadius: 8, padding: '10px 14px', fontSize: 14, color: C.tx, fontFamily: "'DM Sans', sans-serif", resize: 'vertical', boxShadow: focused ? `0 0 0 3px ${C.priDim}` : 'none', transition: 'border 0.15s', ...extraStyle }} />
    </div>
  );
}

function Select({ label, value, onChange, options, placeholder, required, style: extraStyle }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}{required && <span style={{ color: C.err }}> *</span>}</label>}
      <select value={value} onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ background: C.s3, border: `1px solid ${focused ? C.pri : C.b1}`, borderRadius: 8, padding: '10px 14px', fontSize: 14, color: value ? C.tx : C.txM, fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', boxShadow: focused ? `0 0 0 3px ${C.priDim}` : 'none', transition: 'border 0.15s', width: '100%', ...extraStyle }}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: C.tx }}>
      <div onClick={() => onChange(!checked)} style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${checked ? C.pri : C.b2}`, background: checked ? C.pri : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}>
        {checked && <svg width="12" height="12" viewBox="0 0 12 12"><polyline points="2,6 5,9 10,3" stroke={C.white} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>}
      </div>
      {label}
    </label>
  );
}

function Card({ children, style: extraStyle, onClick, hoverable }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => hoverable && setHov(true)}
      onMouseLeave={() => hoverable && setHov(false)}
      style={{ background: C.s2, border: `1px solid ${hov ? C.b2 : C.b1}`, borderRadius: 12, padding: 20, transition: 'all 0.15s', cursor: onClick ? 'pointer' : 'default', transform: hov && hoverable ? 'translateY(-2px)' : 'none', ...extraStyle }}>
      {children}
    </div>
  );
}

function Modal({ open, onClose, title, children, width = 560 }) {
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20, backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ihq-anim" style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 16, width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: `1px solid ${C.b1}` }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>{title}</h2>
          <button onClick={onClose} style={{ color: C.txM, background: C.s3, border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Badge({ children, color = C.pri, dim = C.priDim }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: dim, color, border: `1px solid ${color}33` }}>{children}</span>;
}

function Avatar({ name = '', size = 36, color = C.pri }) {
  const initials = getInitials(name);
  const colors = [C.pri, C.ok, C.acc, C.pur, C.err];
  const bg = colors[name.charCodeAt(0) % colors.length] || C.pri;
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: bg + '25', border: `2px solid ${bg}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.35, fontWeight: 700, color: bg, flexShrink: 0 }}>
      {initials || '?'}
    </div>
  );
}

function StatCard({ label, value, sub, icon, color = C.pri, trend }) {
  return (
    <div style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 14, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 10, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: '14px 14px 0 0', opacity: 0.7 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</div>
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, letterSpacing: '-1px', lineHeight: 1 }}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {sub && <div style={{ fontSize: 12, color: C.txM }}>{sub}</div>}
        {trend != null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: trend >= 0 ? C.ok : C.err }}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ icon, title, desc, action }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', gap: 14, textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, borderRadius: 18, background: C.s3, border: `1px solid ${C.b1}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.tx, marginBottom: 5 }}>{title}</div>
        <div style={{ fontSize: 13, color: C.txM, maxWidth: 280, lineHeight: 1.6 }}>{desc}</div>
      </div>
      {action}
    </div>
  );
}

function PageHeader({ title, subtitle, actions }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 4 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 14, color: C.txM }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, background: C.s3, padding: 4, borderRadius: 10, border: `1px solid ${C.b1}` }}>
      {tabs.map(t => (
        <button key={t.value} onClick={() => onChange(t.value)}
          style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: active === t.value ? C.tx : C.txM, background: active === t.value ? C.s1 : 'transparent', cursor: 'pointer', transition: 'all 0.15s', border: active === t.value ? `1px solid ${C.b1}` : '1px solid transparent' }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    scheduled: { label: 'Scheduled', color: C.pri, dim: C.priDim },
    completed: { label: 'Completed', color: C.ok, dim: C.okDim },
    cancelled: { label: 'Cancelled', color: C.txM, dim: C.s3 },
    'in-progress': { label: 'In Progress', color: C.acc, dim: C.accDim },
    'no-show': { label: 'No Show', color: C.err, dim: C.errDim },
  };
  const s = map[status] || map.scheduled;
  return <Badge color={s.color} dim={s.dim}>{s.label}</Badge>;
}

// ═══════════════════════════════════════════════════════
// SIGNATURE PAD
// ═══════════════════════════════════════════════════════
function SignaturePad({ onSave, existingSignature }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [lastPos, setLastPos] = useState(null);
  const [hasSig, setHasSig] = useState(!!existingSignature);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    setDrawing(true);
    setLastPos(getPos(e));
  }, []);

  const draw = useCallback((e) => {
    if (!drawing || !lastPos) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = C.pri;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    setLastPos(pos);
    setHasSig(true);
  }, [drawing, lastPos]);

  const endDraw = useCallback(() => {
    if (drawing) {
      setDrawing(false);
      setLastPos(null);
      if (canvasRef.current) {
        onSave(canvasRef.current.toDataURL());
      }
    }
  }, [drawing, onSave]);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
    onSave('');
  };

  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>Client Signature <span style={{ color: C.err }}>*</span></label>
      <div style={{ position: 'relative', border: `2px solid ${hasSig ? C.ok : C.b2}`, borderRadius: 10, overflow: 'hidden', background: C.s3 }}>
        <canvas ref={canvasRef} width={500} height={140} className="sig-canvas"
          style={{ display: 'block', width: '100%', cursor: 'crosshair' }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
        {!hasSig && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <span style={{ color: C.txD, fontSize: 14 }}>Sign here ↑</span>
          </div>
        )}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: C.b2 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span style={{ fontSize: 12, color: C.txD }}>Draw your signature in the box above</span>
        <button onClick={clear} style={{ fontSize: 12, color: C.txM, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// AUTH VIEWS
// ═══════════════════════════════════════════════════════
function LoginView({ onLogin, onSwitch, onBack }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) { setError('Please fill all fields.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      onLogin(data);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      const { createClient } = await import('@supabase/supabase-js');
      const sb = createClient(
        'https://lavmquwasqqjcihbukjq.supabase.co',
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imxhdm1xdXdhc3FxamNpaGJ1a2pxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzUxOTQwMjgsImV4cCI6MjA5MDc3MDAyOH0.0UScE4npkwLkD9lwv0hlQehzdU_DOMXz3NsFS9cFE6Q'
      );
      await sb.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: 'https://app.ivpilot.com?reset=1',
      });
      setForgotSent(true);
    } catch (err) {
      setError('Failed to send reset email. Try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgot) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `radial-gradient(ellipse at 30% 40%, rgba(14,165,233,0.07) 0%, transparent 60%), ${C.bg}`, padding: 20 }}>
        <div className="ihq-anim" style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="22" height="22" viewBox="0 0 38 38" fill="none"><rect x="11" y="3" width="16" height="13" rx="4" fill="white" opacity="0.95"/><rect x="13" y="5" width="5" height="3" rx="1.5" fill="white" opacity="0.4"/><line x1="19" y1="16" x2="19" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/><rect x="15.5" y="22" width="7" height="5" rx="2" fill="white" opacity="0.8"/><line x1="19" y1="27" x2="19" y2="33" stroke="white" strokeWidth="2" strokeLinecap="round"/><path d="M16 33 Q19 36 22 33" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/><ellipse cx="19" cy="19.5" rx="1.2" ry="1.5" fill="#0ea88a"/></svg>
              </div>
              <span style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>IVPilot</span>
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>Reset Password</h1>
            <p style={{ fontSize: 14, color: C.txM, marginTop: 6 }}>Enter your email and we'll send a reset link</p>
          </div>
          <Card>
            {forgotSent ? (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📬</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.tx, marginBottom: 8 }}>Check your email</div>
                <div style={{ fontSize: 13, color: C.txM, lineHeight: 1.6 }}>We sent a password reset link to <strong>{forgotEmail}</strong>. Click the link to set a new password.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {error && <div style={{ padding: '10px 14px', background: C.errDim, border: `1px solid ${C.err}33`, borderRadius: 8, fontSize: 13, color: C.err }}>{error}</div>}
                <Input label="Email Address" type="email" value={forgotEmail} onChange={setForgotEmail} placeholder="you@clinic.com" onKeyDown={e => e.key === 'Enter' && handleForgot()} />
                <Btn onClick={handleForgot} disabled={forgotLoading || !forgotEmail} size="lg" style={{ width: '100%', justifyContent: 'center' }}>
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </Btn>
              </div>
            )}
          </Card>
          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: C.txM }}>
            <button onClick={() => { setShowForgot(false); setForgotSent(false); setError(''); }} style={{ color: C.pri, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>← Back to Sign In</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `radial-gradient(ellipse at 30% 40%, rgba(14,165,233,0.07) 0%, transparent 60%), ${C.bg}`, padding: 20 }}>
      <div className="ihq-anim" style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 38 38" fill="none"><rect x="11" y="3" width="16" height="13" rx="4" fill="white" opacity="0.95"/><rect x="13" y="5" width="5" height="3" rx="1.5" fill="white" opacity="0.4"/><line x1="19" y1="16" x2="19" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/><rect x="15.5" y="22" width="7" height="5" rx="2" fill="white" opacity="0.8"/><line x1="19" y1="27" x2="19" y2="33" stroke="white" strokeWidth="2" strokeLinecap="round"/><path d="M16 33 Q19 36 22 33" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/><ellipse cx="19" cy="19.5" rx="1.2" ry="1.5" fill="#0ea88a"/></svg>
            </div>
            <span style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>IVPilot</span>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>Welcome back</h1>
          <p style={{ fontSize: 14, color: C.txM, marginTop: 6 }}>Sign in to your clinic dashboard</p>
        </div>
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {error && <div style={{ padding: '10px 14px', background: C.errDim, border: `1px solid ${C.err}33`, borderRadius: 8, fontSize: 13, color: C.err }}>{error}</div>}
            <Input label="Email Address" type="email" value={email} onChange={setEmail} placeholder="you@clinic.com" onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            <div>
              <Input label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && handleLogin()} />
              <div style={{ textAlign: 'right', marginTop: 6 }}>
                <button onClick={() => { setShowForgot(true); setForgotEmail(email); }} style={{ fontSize: 12, color: C.txM, background: 'none', border: 'none', cursor: 'pointer' }}>Forgot password?</button>
              </div>
            </div>
            <Btn onClick={handleLogin} disabled={loading} size="lg" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Btn>
          </div>
        </Card>
        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: C.txM }}>
          New clinic? <button onClick={onSwitch} style={{ color: C.pri, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Create account →</button>
        </p>
        {onBack && <p style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: C.txD }}><button onClick={onBack} style={{ color: C.txD, background: 'none', border: 'none', cursor: 'pointer' }}>← Back to home</button></p>}
      </div>
    </div>
  );
}

function RegisterView({ onLogin, onSwitch, onBack }) {
  // Check for staff invite params
  const inviteParams = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('invite') === '1') {
      return { isInvite: true, clinicId: p.get('c'), email: p.get('e') || '', name: p.get('n') || '', role: p.get('r') || 'Registered Nurse', clinicName: p.get('cl') || '' };
    }
    return { isInvite: false };
  }, []);

  const [form, setForm] = useState({
    clinicName: inviteParams.clinicName || '',
    ownerName: inviteParams.name || '',
    email: inviteParams.email || '',
    phone: '', password: '', confirm: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const handleRegister = async () => {
    if (!form.ownerName || !form.email || !form.password) { setError('Please fill all required fields.'); return; }
    if (!inviteParams.isInvite && !form.clinicName) { setError('Please enter your clinic name.'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      const endpoint = inviteParams.isInvite ? '/api/auth/invite-accept' : '/api/auth/register';
      const body = inviteParams.isInvite
        ? { email: form.email, password: form.password, name: form.ownerName, clinicId: inviteParams.clinicId, role: inviteParams.role }
        : { email: form.email, password: form.password, name: form.ownerName, clinicName: form.clinicName, phone: form.phone, referralCode: DB.get("referralCode") || "" };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      onLogin(data);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `radial-gradient(ellipse at 70% 60%, rgba(139,92,246,0.07) 0%, transparent 60%), ${C.bg}`, padding: 20 }}>
      <div className="ihq-anim" style={{ width: '100%', maxWidth: 480 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 38 38" fill="none"><rect x="11" y="3" width="16" height="13" rx="4" fill="white" opacity="0.95"/><rect x="13" y="5" width="5" height="3" rx="1.5" fill="white" opacity="0.4"/><line x1="19" y1="16" x2="19" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/><rect x="15.5" y="22" width="7" height="5" rx="2" fill="white" opacity="0.8"/><line x1="19" y1="27" x2="19" y2="33" stroke="white" strokeWidth="2" strokeLinecap="round"/><path d="M16 33 Q19 36 22 33" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/><ellipse cx="19" cy="19.5" rx="1.2" ry="1.5" fill="#0ea88a"/></svg>
            </div>
            <span style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>IVPilot</span>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>
            {inviteParams.isInvite ? 'Create your account' : 'Start your free trial'}
          </h1>
          <p style={{ fontSize: 14, color: C.txM, marginTop: 6 }}>
            {inviteParams.isInvite ? `You've been invited to join ${inviteParams.clinicName}` : '14 days free — no credit card required'}
          </p>
        </div>
        {inviteParams.isInvite && (
          <div style={{ marginBottom: 16, padding: '12px 18px', background: C.priDim, border: `1px solid ${C.pri}30`, borderRadius: 10, fontSize: 14, color: C.pri, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>💉</span>
            <div>
              <div style={{ fontWeight: 700 }}>Joining {inviteParams.clinicName}</div>
              <div style={{ fontSize: 12, color: C.txM, marginTop: 2 }}>Role: {inviteParams.role}</div>
            </div>
          </div>
        )}
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {error && <div style={{ padding: '10px 14px', background: C.errDim, border: `1px solid ${C.err}33`, borderRadius: 8, fontSize: 13, color: C.err }}>{error}</div>}
            {!inviteParams.isInvite && <Input label="Clinic Name" value={form.clinicName} onChange={set('clinicName')} placeholder="Drip & Glow IV Lounge" required />}
            <Input label="Your Full Name" value={form.ownerName} onChange={set('ownerName')} placeholder="Jane Smith, RN" required />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Input label="Email" type="email" value={form.email} onChange={set('email')} placeholder="jane@clinic.com" required />
              <Input label="Phone" type="tel" value={form.phone} onChange={set('phone')} placeholder="(555) 000-0000" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Input label="Password" type="password" value={form.password} onChange={set('password')} placeholder="Min. 6 characters" required />
              <Input label="Confirm Password" type="password" value={form.confirm} onChange={set('confirm')} placeholder="Re-enter password" required />
            </div>
            <Btn onClick={handleRegister} disabled={loading} size="lg" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
              {loading ? 'Creating your account...' : inviteParams.isInvite ? `Join ${inviteParams.clinicName} →` : 'Create Clinic Account'}
            </Btn>
            <p style={{ fontSize: 12, color: C.txD, textAlign: 'center' }}>By creating an account, you agree to our Terms of Service and Privacy Policy.</p>
          </div>
        </Card>
        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: C.txM }}>
          Already have an account? <button onClick={onSwitch} style={{ color: C.pri, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Sign in →</button>
        </p>
        {onBack && <p style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: C.txD }}><button onClick={onBack} style={{ color: C.txD, background: 'none', border: 'none', cursor: 'pointer' }}>← Back to home</button></p>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// SIDEBAR
// ═══════════════════════════════════════════════════════
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></svg> },
  { id: 'inbox', label: 'Inbox', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
  { id: 'clients', label: 'Clients', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg> },
  { id: 'appointments', label: 'Appointments', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg> },
  { id: 'visits', label: 'Visit Records', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a5 5 0 0 1 5 5v3H7V7a5 5 0 0 1 5-5z" /><path d="M7 10H5l-1 11h16l-1-11h-2" /><line x1="12" y1="14" x2="12" y2="18" /><line x1="9" y1="16" x2="15" y2="16" /></svg> },
  { id: 'formulas', label: 'Drip Formulas', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v11l3 3 3-3V3m-6 11H5a2 2 0 0 1-2-2V9m16 5h2a2 2 0 0 0 2-2V9" /></svg> },
  { id: 'staff', label: 'Staff', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><polyline points="16 11 18 13 22 9" /></svg> },
  { id: 'revenue', label: 'Revenue', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg> },
  { id: 'support', label: 'Support', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
  { id: 'referral', label: 'Refer & Earn', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/></svg> },
  { id: 'import', label: 'Import Data', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> },
  { id: 'command', label: 'Command Center', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg> },
  { id: 'settings', label: 'Settings', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg> },
];

// ═══════════════════════════════════════════════════════
// SIDEBAR UPGRADE MODAL
// ═══════════════════════════════════════════════════════
function SidebarUpgradeModal({ clinic, user, onClose, onUpdateClinic }) {
  const [billing, setBilling] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paying, setPaying] = useState(false);

  const plans = [
    { id: 'growth', name: 'Clinic', monthlyPrice: 299, yearlyPrice: 299 * 12, features: ['Unlimited clients & staff', 'All features', 'Priority support'], popular: true },
    { id: 'multi', name: 'Multi-Location', monthlyPrice: 549, yearlyPrice: 549 * 12, features: ['Up to 5 locations', 'All features', 'Phone support'] },
  ];

  if (paying && selectedPlan) {
    return (
      <Modal open onClose={() => { setPaying(false); setSelectedPlan(null); }} title={`Upgrade to ${selectedPlan.name}`} width={500}>
        <Elements stripe={stripePromise}>
          <CheckoutForm
            plan={selectedPlan}
            billing={billing}
            priceId={STRIPE_PRICES[selectedPlan.id]?.[billing]}
            user={user}
            clinic={clinic}
            onUpdateClinic={onUpdateClinic}
            onSuccess={onClose}
            onCancel={() => { setPaying(false); setSelectedPlan(null); }}
          />
        </Elements>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title="Choose Your Plan" width={580}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Tabs tabs={[{ value: 'monthly', label: 'Monthly' }, { value: 'yearly', label: 'Yearly' }]} active={billing} onChange={setBilling} />
        </div>
        {billing === 'yearly' && (
          <div style={{ padding: '8px 14px', background: C.okDim, border: `1px solid ${C.ok}30`, borderRadius: 8, fontSize: 13, color: C.ok, fontWeight: 600, textAlign: 'center' }}>
            🎉 Lock in your rate for the full year
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {plans.map(p => {
            const price = billing === 'monthly' ? p.monthlyPrice : p.yearlyPrice;
            const isSelected = selectedPlan?.id === p.id;
            return (
              <div key={p.id} onClick={() => setSelectedPlan(p)}
                style={{ padding: '16px 20px', borderRadius: 12, border: `2px solid ${isSelected ? C.pri : p.popular ? C.pri + '40' : C.b1}`, background: isSelected ? C.priDim : C.s3, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 16, position: 'relative' }}>
                {p.popular && <div style={{ position: 'absolute', top: -10, right: 16, background: C.acc, color: C.white, fontSize: 10, fontWeight: 800, padding: '2px 10px', borderRadius: 10 }}>POPULAR</div>}
                <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${isSelected ? C.pri : C.b2}`, background: isSelected ? C.pri : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {isSelected && <svg width="10" height="10" viewBox="0 0 12 12"><polyline points="2,6 5,9 10,3" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: isSelected ? C.pri : C.tx, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{p.name}</div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                    {p.features.map(f => <span key={f} style={{ fontSize: 12, color: C.txM }}>✓ {f}</span>)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: isSelected ? C.pri : C.tx }}>${price}</div>
                  <div style={{ fontSize: 11, color: C.txM }}>per {billing === 'monthly' ? 'month' : 'year'}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn variant="secondary" onClick={onClose}>Maybe Later</Btn>
          <Btn variant="primary" disabled={!selectedPlan} onClick={() => selectedPlan && setPaying(true)}>
            {selectedPlan ? `Continue with ${selectedPlan.name} →` : 'Select a Plan'}
          </Btn>
        </div>
        <p style={{ fontSize: 12, color: C.txD, textAlign: 'center' }}>14-day free trial included · No contracts · Cancel anytime</p>
      </div>
    </Modal>
  );
}

function Sidebar({ page, setPage, clinic, user, onLogout, onLogoClick, unreadCount, isLight, toggleTheme }) {
  const [collapsed, setCollapsed] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const trialDays = clinic?.trialEnds ? Math.max(0, Math.ceil((new Date(clinic.trialEnds) - Date.now()) / 86400000)) : 0;
  const planKey = (clinic?.plan || '').toLowerCase().replace(/[_\-\s]/g, '');
  const PAID_PLAN_KEYS = ['starter', 'growth', 'multi', 'multilocation', 'enterprise', 'enterpriseplus', 'pro'];
  const isTrialOrFree = !clinic?.plan || clinic?.plan === 'trial' || clinic?.plan === 'free' || !PAID_PLAN_KEYS.includes(planKey);
  const PLAN_DISPLAY = { trial: 'Free Trial', free: 'Free', starter: 'Starter', growth: 'Growth', multi: 'Multi-Location', multilocation: 'Multi-Location', enterprise: 'Enterprise', enterpriseplus: 'Enterprise+', pro: 'Pro' };
  const planLabel = PLAN_DISPLAY[planKey] || clinic?.plan || 'Free Trial';
  const planColor = isTrialOrFree ? C.acc : C.ok;

  return (
    <div style={{ width: collapsed ? 68 : 240, minHeight: '100vh', background: C.s1, borderRight: `1px solid ${C.b1}`, display: 'flex', flexDirection: 'column', transition: 'width 0.2s ease', flexShrink: 0, position: 'relative', zIndex: 10 }}>
      {/* Logo */}
      <div onClick={onLogoClick} title="Back to home" style={{ padding: collapsed ? '20px 14px' : '20px 20px', borderBottom: `1px solid ${C.b1}`, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', transition: 'background 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.background = C.s2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 0 12px rgba(14,168,138,0.3)' }}>
          <svg width="20" height="20" viewBox="0 0 38 38" fill="none">
            <rect x="11" y="3" width="16" height="13" rx="4" fill="white" opacity="0.95"/>
            <rect x="13" y="5" width="5" height="3" rx="1.5" fill="white" opacity="0.4"/>
            <line x1="19" y1="16" x2="19" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <rect x="15.5" y="22" width="7" height="5" rx="2" fill="white" opacity="0.8"/>
            <line x1="19" y1="27" x2="19" y2="33" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <path d="M16 33 Q19 36 22 33" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
            <ellipse cx="19" cy="19.5" rx="1.2" ry="1.5" fill="#0ea88a"/>
          </svg>
        </div>
        {!collapsed && <span style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, whiteSpace: 'nowrap' }}>IVPilot</span>}
      </div>

      {/* Clinic Info */}
      {!collapsed && (
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.b1}` }}>
          <div style={{ fontSize: 12, color: C.txD, marginBottom: 2 }}>CLINIC</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.tx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clinic?.name || 'My Clinic'}</div>
          <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, background: planColor + '18', border: `1px solid ${planColor}33`, fontSize: 11, fontWeight: 700, color: planColor }}>
            {isTrialOrFree ? '⚡' : '✓'} {planLabel}
            {isTrialOrFree && trialDays > 0 && ` · ${trialDays}d left`}
          </div>
        </div>
      )}

      {/* Nav */}
      <nav style={{ flex: 1, padding: '10px 10px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
        {NAV_ITEMS.filter(item => {
          if (isClinicianRole(user?.role) && (item.id === 'revenue' || item.id === 'settings')) return false;
          if (item.id === 'command' && user?.email !== 'joshuaporo@gmail.com') return false;
          return true;
        }).map(item => {
          const active = page === item.id;
          return (
            <button key={item.id} onClick={() => setPage(item.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: collapsed ? '10px 12px' : '9px 14px', borderRadius: 9, background: active ? C.priDim : 'transparent', color: active ? C.pri : C.txM, border: `1px solid ${active ? C.pri + '25' : 'transparent'}`, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: active ? 600 : 400, textAlign: 'left', transition: 'all 0.12s', whiteSpace: 'nowrap', overflow: 'hidden', justifyContent: collapsed ? 'center' : 'flex-start', boxShadow: active ? `0 1px 6px ${C.pri}18` : 'none' }}
              title={collapsed ? item.label : ''}>
              <span style={{ flexShrink: 0, position: 'relative' }}>
                {item.icon}
                {item.id === 'inbox' && unreadCount > 0 && (
                  <span style={{ position: 'absolute', top: -6, right: -6, width: 16, height: 16, background: C.err, borderRadius: '50%', fontSize: 9, fontWeight: 800, color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${C.s1}` }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </span>
              {!collapsed && item.label}
              {!collapsed && item.id === 'inbox' && unreadCount > 0 && (
                <span style={{ marginLeft: 'auto', background: C.err, color: C.white, fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 10 }}>{unreadCount}</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom */}
      <div style={{ padding: '12px 10px', borderTop: `1px solid ${C.b1}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between' }}>
          <button onClick={() => setCollapsed(c => !c)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, color: C.txD, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{collapsed ? <polyline points="9 18 15 12 9 6" /> : <polyline points="15 18 9 12 15 6" />}</svg>
            {!collapsed && 'Collapse'}
          </button>
          {!collapsed && toggleTheme && (
            <button onClick={toggleTheme} title={isLight ? 'Dark mode' : 'Light mode'}
              style={{ width: 30, height: 30, borderRadius: 8, background: C.s3, border: `1px solid ${C.b1}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
              {isLight ? '🌙' : '☀️'}
            </button>
          )}
        </div>

        {/* Upgrade CTA — above account name */}
        {!collapsed && isTrialOrFree && (
          <button onClick={() => setShowUpgradeModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 9, background: `linear-gradient(135deg, ${C.pri}22, ${C.pur}22)`, border: `1px solid ${C.pri}40`, cursor: 'pointer', marginBottom: 2 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: `linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.pri }}>Upgrade Plan</div>
              <div style={{ fontSize: 10, color: C.txD }}>From $149/mo · All features</div>
            </div>
          </button>
        )}

        {!collapsed && user && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar name={user.name} size={30} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.tx, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
                <div style={{ fontSize: 11, color: C.txD, textTransform: 'capitalize' }}>{user.role}</div>
              </div>
            </div>
            <button onClick={onLogout} title="Logout" style={{ color: C.txD, background: 'none', border: 'none', cursor: 'pointer' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
            </button>
          </div>
        )}
        {collapsed && (
          <button onClick={onLogout} title="Logout" style={{ padding: '8px 12px', color: C.txD, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
          </button>
        )}
      </div>

      {/* Sidebar Upgrade Modal */}
      {showUpgradeModal && (
        <SidebarUpgradeModal
          clinic={clinic}
          user={user}
          onClose={() => setShowUpgradeModal(false)}
          onUpdateClinic={(updated) => {
            DB.set('clinic_' + updated.id, updated);
            setShowUpgradeModal(false);
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════
const CLINICAL_ROLES = ['Registered Nurse', 'LPN', 'Nurse Practitioner', 'Physician'];
const isClinicianRole = (role) => CLINICAL_ROLES.includes(role);

function DashboardView({ clients, visits, appointments, staff, formulas, go, user }) {
  const today = todayStr();
  const thisMonth = new Date().getMonth();
  const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
  const isClinician = isClinicianRole(user?.role);

  const todayAppts = appointments.filter(a => a.scheduledAt?.startsWith(today) && a.status === 'scheduled');
  // Clinicians only see their own appointments
  const myAppts = isClinician
    ? todayAppts.filter(a => { const s = staff.find(st => st.email === user?.email); return s ? a.staffId === s.id : true; })
    : todayAppts;

  const monthVisits = visits.filter(v => new Date(v.startedAt).getMonth() === thisMonth);
  const lastMonthVisits = visits.filter(v => new Date(v.startedAt).getMonth() === lastMonth);
  const monthRevenue = monthVisits.reduce((s, v) => s + (+v.amountCharged || 0), 0);
  const lastMonthRevenue = lastMonthVisits.reduce((s, v) => s + (+v.amountCharged || 0), 0);
  const revTrend = (lastMonthRevenue > 0 && monthRevenue > 0) ? Math.round(((monthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100) : null;
  const visitTrend = (lastMonthVisits.length > 0 && monthVisits.length > 0) ? Math.round(((monthVisits.length - lastMonthVisits.length) / lastMonthVisits.length) * 100) : null;

  const recentVisits = [...visits].sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt)).slice(0, 5);
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));
  const formulaMap = Object.fromEntries(formulas.map(f => [f.id, f]));
  const staffMap = Object.fromEntries(staff.map(s => [s.id, s]));

  const topFormulas = formulas.map(f => ({
    ...f, count: visits.filter(v => v.formulaId === f.id).length,
    revenue: visits.filter(v => v.formulaId === f.id).reduce((s, v) => s + (+v.amountCharged || 0), 0),
  })).sort((a, b) => b.count - a.count).slice(0, 4);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="ihq-anim" style={{ padding: window.innerWidth <= 768 ? 16 : 32 }}>
      <PageHeader
        title={isClinician ? `${greeting}, ${firstName} 👋` : `${greeting}, ${firstName} 👋`}
        subtitle={`${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · Here's what's happening today`}
        actions={<Btn onClick={() => go('visits', { new: true })} variant="primary"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg> New Visit</Btn>}
      />

      {/* Stats Row — responsive grid */}
      <div className="ihq-grid-4" style={{ display: 'grid', gridTemplateColumns: `repeat(${isClinician ? 3 : 4}, 1fr)`, gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Clients" value={clients.length} sub={`${clients.filter(c => new Date(c.createdAt) > new Date(Date.now() - 30 * 86400000)).length} new this month`} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>} color={C.pri} />
        {!isClinician && <StatCard label="Month Revenue" value={fmtMoney(monthRevenue)} sub={`${monthVisits.length} visits completed`} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>} color={C.ok} trend={revTrend} />}
        <StatCard label="Total Visits" value={visits.length} sub={`${monthVisits.length} this month`} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>} color={C.acc} trend={visitTrend} />
        <StatCard label="Today's Schedule" value={isClinician ? myAppts.length : todayAppts.length} sub={`${appointments.filter(a => a.status === 'scheduled').length} total upcoming`} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>} color={C.pur} />
      </div>

      <div className="ihq-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr minmax(280px, 360px)', gap: 20 }}>
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Today's Appointments */}
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>Today's Schedule</h3>
              <Btn variant="ghost" size="sm" onClick={() => go('appointments')}>View all</Btn>
            </div>
            {todayAppts.length === 0 ? (
              <EmptyState icon="📅" title="No appointments today" desc="Schedule is clear for today" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {todayAppts.map(appt => {
                  const client = clientMap[appt.clientId];
                  const formula = formulaMap[appt.formulaId];
                  const nurse = staffMap[appt.staffId];
                  return (
                    <div key={appt.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: C.s3, borderRadius: 10, border: `1px solid ${C.b1}` }}>
                      <Avatar name={client ? `${client.firstName} ${client.lastName}` : '?'} size={40} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: C.tx }}>{client ? `${client.firstName} ${client.lastName}` : 'Unknown'}</div>
                        <div style={{ fontSize: 12, color: C.txM }}>{formula?.name || 'Formula TBD'} · {nurse?.name || 'Unassigned'}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.pri }}>{fmtTime(appt.scheduledAt)}</div>
                        <StatusBadge status={appt.status} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Recent Visits */}
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>Recent Visits</h3>
              <Btn variant="ghost" size="sm" onClick={() => go('visits')}>View all</Btn>
            </div>
            {recentVisits.length === 0 ? (
              <EmptyState icon="💉" title="No visits yet" desc="Start recording IV sessions" />
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
                    {['Client', 'Formula', 'Date', 'Amount', 'Nurse'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.txD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentVisits.map(v => {
                    const client = clientMap[v.clientId];
                    const nurse = staffMap[v.staffId];
                    return (
                      <tr key={v.id} style={{ borderBottom: `1px solid ${C.b1}15` }}>
                        <td style={{ padding: '12px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar name={client ? `${client.firstName} ${client.lastName}` : '?'} size={28} />
                            <span style={{ fontSize: 13, fontWeight: 500, color: C.tx }}>{client ? `${client.firstName} ${client.lastName}` : '—'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 12px', fontSize: 13, color: C.txM }}>{v.formulaName || '—'}</td>
                        <td style={{ padding: '12px 12px', fontSize: 13, color: C.txM }}>{fmtDate(v.startedAt)}</td>
                        <td style={{ padding: '12px 12px', fontSize: 13, fontWeight: 600, color: C.ok }}>{fmtMoney(v.amountCharged)}</td>
                        <td style={{ padding: '12px 12px', fontSize: 13, color: C.txM }}>{nurse?.name?.split(' ')[0] || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Top Formulas */}
          <Card>
            <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 16 }}>Top Drips</h3>
            {topFormulas.filter(f => f.count > 0).length === 0 ? (
              <EmptyState icon="🧪" title="No visits yet" desc="Start recording visits to see popular drips" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {topFormulas.filter(f => f.count > 0).map((f, i) => {
                  const pct = topFormulas[0].count > 0 ? (f.count / topFormulas[0].count) * 100 : 0;
                  return (
                    <div key={f.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: C.tx }}>{f.name}</span>
                        <span style={{ fontSize: 12, color: C.txM }}>{f.count} visits · {fmtMoney(f.revenue)}</span>
                      </div>
                      <div style={{ height: 4, background: C.s3, borderRadius: 2 }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: CAT_COLORS[f.cat] || C.pri, borderRadius: 2 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Quick Actions */}
          <Card>
            <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 16 }}>Quick Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'Record New Visit', icon: '💉', action: () => go('visits', { new: true }), color: C.pri },
                { label: 'Add New Client', icon: '👤', action: () => go('clients', { new: true }), color: C.ok },
                { label: 'Book Appointment', icon: '📅', action: () => go('appointments', { new: true }), color: C.acc },
                { label: 'View Formula Library', icon: '🧪', action: () => go('formulas'), color: C.pur },
              ].map(item => (
                <button key={item.label} onClick={item.action}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: C.s3, border: `1px solid ${C.b1}`, borderRadius: 9, cursor: 'pointer', textAlign: 'left', transition: 'all 0.12s', color: C.tx, fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500 }}>
                  <span style={{ fontSize: 18 }}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </Card>

          {/* Staff Status */}
          <Card>
            <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 16 }}>Staff</h3>
            {staff.length === 0 ? (
              <EmptyState icon="👩‍⚕️" title="No staff added" desc="Add your nurses and staff" action={<Btn size="sm" onClick={() => go('staff')}>Add Staff</Btn>} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {staff.slice(0, 4).map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={s.name} size={34} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.tx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: C.txM }}>{s.role}</div>
                    </div>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.active ? C.ok : C.txD }} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// CLIENTS VIEW
// ═══════════════════════════════════════════════════════
function ClientsView({ clients, forms, visits, go, onAddClient, onDeleteClient }) {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', dob: '', allergies: '', conditions: '' });
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const filtered = useMemo(() => clients.filter(c =>
    `${c.firstName} ${c.lastName} ${c.email} ${c.phone}`.toLowerCase().includes(search.toLowerCase())
  ), [clients, search]);

  const handleAdd = () => {
    if (!form.firstName || !form.lastName) return;
    onAddClient({ ...form, createdAt: new Date().toISOString() });
    setForm({ firstName: '', lastName: '', email: '', phone: '', dob: '', allergies: '', conditions: '' });
    setShowModal(false);
  };

  const clientStats = id => ({
    visits: visits.filter(v => v.clientId === id).length,
    revenue: visits.filter(v => v.clientId === id).reduce((s, v) => s + (+v.amountCharged || 0), 0),
    hasSigned: forms.some(f => f.clientId === id && f.signed),
    lastVisit: visits.filter(v => v.clientId === id).sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))[0]?.startedAt,
  });

  return (
    <div className="ihq-anim ihq-mobile-pad" style={{ padding: 32, maxWidth: 1200 }}>
      <PageHeader title="Clients" subtitle={`${clients.length} total clients`}
        actions={<Btn onClick={() => setShowModal(true)}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg> Add Client</Btn>}
      />

      <Card style={{ marginBottom: 20 }}>
        <div style={{ position: 'relative' }}>
          <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.txM }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or phone..."
            style={{ width: '100%', background: C.s3, border: `1px solid ${C.b1}`, borderRadius: 8, padding: '10px 14px 10px 38px', fontSize: 14, color: C.tx, fontFamily: "'DM Sans', sans-serif" }} />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <EmptyState icon="👥" title="No clients found" desc={search ? 'Try a different search' : 'Add your first client to get started'} action={!search && <Btn onClick={() => setShowModal(true)}>Add First Client</Btn>} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(c => {
            const stats = clientStats(c.id);
            const name = `${c.firstName} ${c.lastName}`;
            return (
              <Card key={c.id} hoverable onClick={() => go('clientDetail', { clientId: c.id })}
                style={{ display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' }}>
                <Avatar name={name} size={48} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: C.tx }}>{name}</span>
                    {!stats.hasSigned && <Badge color={C.err} dim={C.errDim}>No Waiver</Badge>}
                    {stats.hasSigned && <Badge color={C.ok} dim={C.okDim}>✓ Signed</Badge>}
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {c.email && <span style={{ fontSize: 13, color: C.txM }}>✉ {c.email}</span>}
                    {c.phone && <span style={{ fontSize: 13, color: C.txM }}>📞 {c.phone}</span>}
                    {c.dob && <span style={{ fontSize: 13, color: C.txM }}>🎂 {fmtDate(c.dob)}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 24, flexShrink: 0 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>{stats.visits}</div>
                    <div style={{ fontSize: 11, color: C.txD }}>Visits</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.ok }}>{fmtMoney(stats.revenue)}</div>
                    <div style={{ fontSize: 11, color: C.txD }}>Revenue</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.txM }}>{stats.lastVisit ? fmtDate(stats.lastVisit) : '—'}</div>
                    <div style={{ fontSize: 11, color: C.txD }}>Last Visit</div>
                  </div>
                </div>
                <button onClick={e => { e.stopPropagation(); setConfirmDelete(c); }}
                  style={{ padding: '6px 8px', borderRadius: 7, background: C.errDim, border: `1px solid ${C.err}33`, color: C.err, cursor: 'pointer', flexShrink: 0 }}
                  title="Delete client">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                </button>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.txD} strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add New Client">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Input label="First Name" value={form.firstName} onChange={set('firstName')} placeholder="Sarah" required />
            <Input label="Last Name" value={form.lastName} onChange={set('lastName')} placeholder="Johnson" required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Input label="Email" type="email" value={form.email} onChange={set('email')} placeholder="sarah@example.com" />
            <Input label="Phone" type="tel" value={form.phone} onChange={set('phone')} placeholder="(555) 000-0000" />
          </div>
          <Input label="Date of Birth" type="date" value={form.dob} onChange={set('dob')} />
          <Input label="Known Allergies" value={form.allergies} onChange={set('allergies')} placeholder="Penicillin, Sulfa drugs..." />
          <Textarea label="Medical Conditions / Notes" value={form.conditions} onChange={set('conditions')} placeholder="Relevant health history..." rows={2} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="secondary" onClick={() => setShowModal(false)}>Cancel</Btn>
            <Btn onClick={handleAdd} disabled={!form.firstName || !form.lastName}>Add Client</Btn>
          </div>
        </div>
      </Modal>
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete Client" width={420}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ fontSize: 14, color: C.txM, lineHeight: 1.6 }}>
            Are you sure you want to delete <strong style={{ color: C.tx }}>{confirmDelete?.firstName} {confirmDelete?.lastName}</strong>? This will permanently remove the client and cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={() => { onDeleteClient(confirmDelete.id); setConfirmDelete(null); }}>Delete Client</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
// ═══════════════════════════════════════════════════════
function ClientDetailView({ clientId, clients, visits, forms, appointments, formulas, staff, go, onUpdateClient, onSaveForm, onAddVisit, clinicId }) {
  const [tab, setTab] = useState('profile');
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const client = clients.find(c => c.id === clientId);
  const clientVisits = useMemo(() => visits.filter(v => v.clientId === clientId).sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt)), [visits, clientId]);
  const clientForm = forms.find(f => f.clientId === clientId);
  const clientAppts = appointments.filter(a => a.clientId === clientId).sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));
  const formulaMap = Object.fromEntries(formulas.map(f => [f.id, f]));
  const staffMap = Object.fromEntries(staff.map(s => [s.id, s]));
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(client || {});
  const setEF = k => v => setEditForm(f => ({ ...f, [k]: v }));

  if (!client) return <div style={{ padding: 32, color: C.txM }}>Client not found.</div>;

  const name = `${client.firstName} ${client.lastName}`;
  const totalRevenue = clientVisits.reduce((s, v) => s + (+v.amountCharged || 0), 0);

  return (
    <div className="ihq-anim ihq-mobile-pad" style={{ padding: 32, maxWidth: 1100 }}>
      <button onClick={() => go('clients')} style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.txM, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, marginBottom: 20 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg> Back to Clients
      </button>

      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
          <Avatar name={name} size={64} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>{name}</h1>
              {clientForm?.signed ? <Badge color={C.ok} dim={C.okDim}>✓ Waiver Signed</Badge> : <Badge color={C.err} dim={C.errDim}>⚠ No Waiver</Badge>}
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 12 }}>
              {client.email && <span style={{ fontSize: 13, color: C.txM }}>✉ {client.email}</span>}
              {client.phone && <span style={{ fontSize: 13, color: C.txM }}>📞 {client.phone}</span>}
              {client.dob && <span style={{ fontSize: 13, color: C.txM }}>🎂 {fmtDate(client.dob)}</span>}
              <span style={{ fontSize: 13, color: C.txM }}>📅 Client since {fmtDate(client.createdAt)}</span>
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              <div><span style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>{clientVisits.length}</span><span style={{ fontSize: 12, color: C.txD, marginLeft: 5 }}>Total Visits</span></div>
              <div><span style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.ok }}>{fmtMoney(totalRevenue)}</span><span style={{ fontSize: 12, color: C.txD, marginLeft: 5 }}>Total Spent</span></div>
              <div><span style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.pri }}>{clientAppts.filter(a => a.status === 'scheduled').length}</span><span style={{ fontSize: 12, color: C.txD, marginLeft: 5 }}>Upcoming</span></div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="secondary" size="sm" onClick={() => setShowIntakeModal(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
              {clientForm?.signed ? 'Update Intake' : 'Collect Waiver'}
            </Btn>
            <Btn size="sm" onClick={() => setShowVisitModal(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              New Visit
            </Btn>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div style={{ marginBottom: 20 }}>
        <Tabs tabs={[{ value: 'profile', label: 'Profile & Medical' }, { value: 'visits', label: `Visits (${clientVisits.length})` }, { value: 'appointments', label: `Appointments (${clientAppts.length})` }]} active={tab} onChange={setTab} />
      </div>

      {tab === 'profile' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>Personal Info</h3>
              {!editing && <Btn variant="ghost" size="sm" onClick={() => { setEditing(true); setEditForm(client); }}>Edit</Btn>}
            </div>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Input label="First Name" value={editForm.firstName} onChange={setEF('firstName')} />
                  <Input label="Last Name" value={editForm.lastName} onChange={setEF('lastName')} />
                </div>
                <Input label="Email" value={editForm.email || ''} onChange={setEF('email')} />
                <Input label="Phone" value={editForm.phone || ''} onChange={setEF('phone')} />
                <Input label="Date of Birth" type="date" value={editForm.dob || ''} onChange={setEF('dob')} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                  <Btn variant="secondary" size="sm" onClick={() => setEditing(false)}>Cancel</Btn>
                  <Btn size="sm" onClick={() => { onUpdateClient(editForm); setEditing(false); }}>Save</Btn>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[['Email', client.email], ['Phone', client.phone], ['Date of Birth', fmtDate(client.dob)]].map(([l, v]) => v && (
                  <div key={l}>
                    <div style={{ fontSize: 11, color: C.txD, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{l}</div>
                    <div style={{ fontSize: 14, color: C.tx }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 16 }}>Medical Information</h3>
            {clientForm ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  ['Health Conditions', clientForm.healthConditions],
                  ['Current Medications', clientForm.medications],
                  ['Allergies', clientForm.allergies],
                  ['Blood Pressure', clientForm.bloodPressure],
                ].map(([l, v]) => (
                  <div key={l}>
                    <div style={{ fontSize: 11, color: C.txD, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{l}</div>
                    <div style={{ fontSize: 14, color: v ? C.tx : C.txD }}>{v || 'None reported'}</div>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                  {clientForm.pregnant && <Badge color={C.acc} dim={C.accDim}>Pregnant</Badge>}
                  {clientForm.heartCondition && <Badge color={C.err} dim={C.errDim}>Heart Condition</Badge>}
                  {clientForm.kidneyDisease && <Badge color={C.err} dim={C.errDim}>Kidney Disease</Badge>}
                  {clientForm.diabetes && <Badge color={C.acc} dim={C.accDim}>Diabetes</Badge>}
                </div>
                <div style={{ paddingTop: 8, borderTop: `1px solid ${C.b1}`, fontSize: 12, color: C.txM }}>
                  {clientForm.signed ? `✓ Signed ${fmtDate(clientForm.signedAt)}` : '⚠ Not yet signed'}
                </div>
              </div>
            ) : (
              <EmptyState icon="📋" title="No intake form" desc="Collect medical intake before the first session" action={<Btn size="sm" onClick={() => setShowIntakeModal(true)}>Collect Intake & Waiver</Btn>} />
            )}
          </Card>
        </div>
      )}

      {tab === 'visits' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {clientVisits.length === 0 ? (
            <EmptyState icon="💉" title="No visits yet" desc="Record the first IV session for this client" action={<Btn onClick={() => setShowVisitModal(true)}>Record Visit</Btn>} />
          ) : clientVisits.map(v => {
            const nurse = staffMap[v.staffId];
            return (
              <Card key={v.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.tx, marginBottom: 4 }}>{v.formulaName || '—'}</div>
                    <div style={{ fontSize: 13, color: C.txM, marginBottom: 8 }}>{fmtDate(v.startedAt)} · {nurse?.name || 'Unassigned'}</div>
                    {v.vitals && (
                      <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                        {Object.entries(v.vitals).filter(([, val]) => val).map(([k, val]) => (
                          <span key={k} style={{ fontSize: 12, color: C.txM }}><strong>{k.toUpperCase()}:</strong> {val}</span>
                        ))}
                      </div>
                    )}
                    {v.notes && <div style={{ fontSize: 13, color: C.txM, fontStyle: 'italic' }}>{v.notes}</div>}
                    {v.adverseReactions && <div style={{ fontSize: 13, color: C.err, marginTop: 4 }}>⚠ Adverse reaction: {v.adverseReactions}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.ok }}>{fmtMoney(v.amountCharged)}</div>
                    <Badge color={C.ok} dim={C.okDim}>Completed</Badge>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {tab === 'appointments' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {clientAppts.length === 0 ? (
            <EmptyState icon="📅" title="No appointments" desc="Book an appointment for this client" action={<Btn onClick={() => go('appointments', { new: true, prefillClientId: client.id })}>Book Appointment</Btn>} />
          ) : clientAppts.map(a => {
            const formula = formulaMap[a.formulaId];
            const nurse = staffMap[a.staffId];
            return (
              <Card key={a.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.tx, marginBottom: 4 }}>{formula?.name || 'Formula TBD'}</div>
                    <div style={{ fontSize: 13, color: C.txM }}>{fmtDate(a.scheduledAt)} at {fmtTime(a.scheduledAt)} · {nurse?.name || 'Unassigned'}</div>
                    {a.notes && <div style={{ fontSize: 12, color: C.txD, marginTop: 4 }}>{a.notes}</div>}
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Intake Form Modal */}
      <IntakeFormModal open={showIntakeModal} onClose={() => setShowIntakeModal(false)} client={client} existing={clientForm} clinicId={clinicId} onSave={f => { onSaveForm(f); setShowIntakeModal(false); }} />

      {/* New Visit Modal */}
      <NewVisitModal open={showVisitModal} onClose={() => setShowVisitModal(false)} client={client} formulas={formulas} staff={staff} clinicId={clinicId} onSave={v => { onAddVisit(v); setShowVisitModal(false); }} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// INTAKE FORM MODAL
// ═══════════════════════════════════════════════════════
function IntakeFormModal({ open, onClose, client, existing, clinicId, onSave }) {
  const [form, setForm] = useState({
    healthConditions: '', medications: '', allergies: client?.allergies || '', bloodPressure: '',
    pregnant: false, heartCondition: false, kidneyDisease: false, diabetes: false,
    smoker: false, alcoholUse: '', emergencyContact: '', emergencyPhone: '',
    priorIV: false, priorReactions: '', consentAgreed: false, signatureData: '',
    ...existing,
  });
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm(f => ({ healthConditions: '', medications: '', allergies: client?.allergies || '', bloodPressure: '', pregnant: false, heartCondition: false, kidneyDisease: false, diabetes: false, smoker: false, alcoholUse: '', emergencyContact: '', emergencyPhone: '', priorIV: false, priorReactions: '', consentAgreed: false, signatureData: '', ...existing }));
  }, [open]);

  const handleSave = () => {
    if (!form.consentAgreed || !form.signatureData) return;
    onSave({ ...form, ...(existing?.id ? { id: existing.id } : {}), clientId: client.id, clinicId, signed: true, signedAt: new Date().toISOString() });
  };

  const canSubmit = form.consentAgreed && form.signatureData;

  return (
    <Modal open={open} onClose={onClose} title={`Medical Intake — ${client?.firstName} ${client?.lastName}`} width={640}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ padding: '10px 14px', background: C.priDim, borderRadius: 8, border: `1px solid ${C.pri}25`, fontSize: 13, color: C.pri }}>
          📋 This form must be completed and signed before IV therapy is administered. All information is stored securely in your compliance audit log.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Medical History</h4>
          <Textarea label="Current Health Conditions" value={form.healthConditions} onChange={set('healthConditions')} placeholder="Hypertension, migraines, autoimmune..." rows={2} />
          <Textarea label="Current Medications" value={form.medications} onChange={set('medications')} placeholder="List all current prescription and OTC medications..." rows={2} />
          <Input label="Known Allergies" value={form.allergies} onChange={set('allergies')} placeholder="Drug allergies, food allergies, environmental..." />
          <Input label="Blood Pressure (most recent)" value={form.bloodPressure} onChange={set('bloodPressure')} placeholder="e.g. 120/80" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Checkbox label="Currently pregnant" checked={form.pregnant} onChange={set('pregnant')} />
          <Checkbox label="Heart condition" checked={form.heartCondition} onChange={set('heartCondition')} />
          <Checkbox label="Kidney disease" checked={form.kidneyDisease} onChange={set('kidneyDisease')} />
          <Checkbox label="Diabetes" checked={form.diabetes} onChange={set('diabetes')} />
          <Checkbox label="Current smoker" checked={form.smoker} onChange={set('smoker')} />
          <Checkbox label="Received IV therapy before" checked={form.priorIV} onChange={set('priorIV')} />
        </div>

        {form.priorIV && <Textarea label="Prior IV reactions or issues" value={form.priorReactions} onChange={set('priorReactions')} placeholder="Describe any previous adverse reactions..." rows={2} />}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Input label="Emergency Contact Name" value={form.emergencyContact} onChange={set('emergencyContact')} placeholder="Jane Smith" />
          <Input label="Emergency Contact Phone" value={form.emergencyPhone} onChange={set('emergencyPhone')} placeholder="(555) 000-0000" />
        </div>

        <div style={{ borderTop: `1px solid ${C.b1}`, paddingTop: 18 }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Informed Consent & Waiver</h4>
          <div style={{ padding: 14, background: C.s3, borderRadius: 8, border: `1px solid ${C.b1}`, fontSize: 13, color: C.txM, lineHeight: 1.6, marginBottom: 14, maxHeight: 120, overflow: 'auto' }}>
            I understand that IV hydration therapy involves the intravenous administration of fluids, vitamins, and minerals. I acknowledge that while IV therapy is generally safe when administered by licensed professionals, there are potential risks including but not limited to: bruising or soreness at the injection site, infection, allergic reaction, and in rare cases more serious complications. I confirm that the medical information I have provided is accurate and complete to the best of my knowledge. I release the clinic and its staff from liability for adverse events resulting from information I have withheld or provided inaccurately. I consent to receive IV therapy and authorize the clinic to administer the treatment as discussed.
          </div>
          <div style={{ marginBottom: 16 }}>
            <Checkbox label="I have read, understood, and agree to the above informed consent" checked={form.consentAgreed} onChange={set('consentAgreed')} />
          </div>
          <SignaturePad onSave={set('signatureData')} existingSignature={existing?.signatureData} />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={handleSave} disabled={!canSubmit} variant={canSubmit ? 'success' : 'secondary'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
            {canSubmit ? 'Save Signed Intake Form' : 'Signature & Consent Required'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════
// NEW VISIT MODAL
// ═══════════════════════════════════════════════════════
function NewVisitModal({ open, onClose, client, formulas, staff, clinicId, onSave }) {
  const [form, setForm] = useState({ formulaId: '', staffId: '', amountCharged: '', notes: '', adverseReactions: '', vitals: { bp: '', hr: '', o2: '', weight: '' }, startedAt: new Date().toISOString().slice(0, 16) });
  const set = k => v => setForm(f => ({ ...f, [k]: v }));
  const setVital = k => v => setForm(f => ({ ...f, vitals: { ...f.vitals, [k]: v } }));
  const selectedFormula = formulas.find(f => f.id === form.formulaId);

  useEffect(() => {
    if (open) setForm({ formulaId: '', staffId: '', amountCharged: '', notes: '', adverseReactions: '', vitals: { bp: '', hr: '', o2: '', weight: '' }, startedAt: new Date().toISOString().slice(0, 16) });
  }, [open]);

  useEffect(() => {
    if (selectedFormula) set('amountCharged')(String(selectedFormula.price));
  }, [selectedFormula]);

  const handleSave = () => {
    if (!form.formulaId) return;
    onSave({ ...form, clinicId, clientId: client.id, formulaName: selectedFormula?.name || '', completedAt: new Date().toISOString(), startedAt: new Date(form.startedAt).toISOString() });
  };

  return (
    <Modal open={open} onClose={onClose} title={`Record Visit — ${client?.firstName} ${client?.lastName}`} width={580}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Select label="IV Formula / Drip" value={form.formulaId} onChange={set('formulaId')} required placeholder="Select formula..."
          options={formulas.map(f => ({ value: f.id, label: `${f.name} — ${fmtMoney(f.price)}` }))} />
        {selectedFormula && (
          <div style={{ padding: '10px 14px', background: C.s3, borderRadius: 8, border: `1px solid ${C.b1}` }}>
            <div style={{ fontSize: 12, color: C.txM, marginBottom: 6 }}>Components:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {selectedFormula.components.map((c, i) => <Badge key={i} color={C.pri} dim={C.priDim}>{c.name} {c.dose}{c.unit}</Badge>)}
            </div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Select label="Administering Nurse" value={form.staffId} onChange={set('staffId')} placeholder="Select staff..."
            options={staff.map(s => ({ value: s.id, label: s.name }))} />
          <Input label="Date & Time" type="datetime-local" value={form.startedAt} onChange={set('startedAt')} />
        </div>
        <Input label="Amount Charged ($)" type="number" value={form.amountCharged} onChange={set('amountCharged')} placeholder="0.00" />

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Pre-Treatment Vitals</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input label="Blood Pressure" value={form.vitals.bp} onChange={setVital('bp')} placeholder="120/80" />
            <Input label="Heart Rate (bpm)" value={form.vitals.hr} onChange={setVital('hr')} placeholder="72" />
            <Input label="O2 Saturation" value={form.vitals.o2} onChange={setVital('o2')} placeholder="98%" />
            <Input label="Weight" value={form.vitals.weight} onChange={setVital('weight')} placeholder="145 lbs" />
          </div>
        </div>
        <Textarea label="Session Notes" value={form.notes} onChange={set('notes')} placeholder="How is the client feeling? Any observations..." rows={2} />
        <Textarea label="Adverse Reactions (if any)" value={form.adverseReactions} onChange={set('adverseReactions')} placeholder="Leave blank if none" rows={2} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={handleSave} disabled={!form.formulaId} variant="success">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Record Visit
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════
// APPOINTMENTS VIEW
// ═══════════════════════════════════════════════════════
function AppointmentsView({ appointments, clients, staff, formulas, go, onAdd, onUpdate, clinicId, initNew, initClientId, clinic }) {
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [showModal, setShowModal] = useState(!!initNew);
  const [form, setForm] = useState({ clientId: initClientId || '', staffId: '', formulaId: '', scheduledAt: selectedDate + 'T09:00', duration: 60, notes: '', status: 'scheduled' });
  const [sending, setSending] = useState(false);
  const set = k => v => setForm(f => ({ ...f, [k]: v }));
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));
  const formulaMap = Object.fromEntries(formulas.map(f => [f.id, f]));
  const staffMap = Object.fromEntries(staff.map(s => [s.id, s]));

  const dayAppts = useMemo(() => appointments.filter(a => a.scheduledAt?.startsWith(selectedDate)).sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)), [appointments, selectedDate]);

  const sendNotifications = async (appt) => {
    const client = clientMap[appt.clientId];
    const formula = formulaMap[appt.formulaId];
    const nurse = staffMap[appt.staffId];
    if (!client) return;

    const apptDate = fmtDate(appt.scheduledAt);
    const apptTime = fmtTime(appt.scheduledAt);
    const clinicName = clinic?.name || 'Your IV Clinic';

    const payload = {
      clientName: `${client.firstName} ${client.lastName}`,
      clinicName,
      appointmentDate: apptDate,
      appointmentTime: apptTime,
      formula: formula?.name || '',
      nurseName: nurse?.name || '',
      appUrl: window.location.origin,
      portalLink: client.id ? window.location.origin + '?portal=' + client.id + '&c=' + clinicId + '&cl=' + encodeURIComponent(clinicName) : null,
    };

    // Send email if client has email
    if (client.email) {
      try {
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'appointment_confirmation', to: client.email, ...payload }),
        });
      } catch (e) { console.warn('Email send failed:', e.message); }
    }

    // Send SMS if client has phone
    if (client.phone) {
      try {
        await fetch('/api/send-sms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'appointment_confirmation', to: client.phone, ...payload }),
        });
      } catch (e) { console.warn('SMS send failed:', e.message); }
    }
  };

  const handleAdd = async () => {
    if (!form.clientId || !form.scheduledAt) return;
    setSending(true);
    const appt = { ...form, clinicId, scheduledAt: new Date(form.scheduledAt).toISOString() };
    onAdd(appt);
    await sendNotifications({ ...appt, ...form });
    setForm({ clientId: '', staffId: '', formulaId: '', scheduledAt: selectedDate + 'T09:00', duration: 60, notes: '', status: 'scheduled' });
    setSending(false);
    setShowModal(false);
  };

  const getWeekDates = () => {
    const d = new Date(selectedDate);
    const day = d.getDay();
    const start = new Date(d);
    start.setDate(d.getDate() - day);
    return Array.from({ length: 7 }, (_, i) => { const x = new Date(start); x.setDate(start.getDate() + i); return x.toISOString().slice(0, 10); });
  };
  const weekDates = getWeekDates();
  const weekNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const statusColor = s => ({ scheduled: C.pri, completed: C.ok, cancelled: C.txM, 'in-progress': C.acc, 'no-show': C.err }[s] || C.pri);

  return (
    <div className="ihq-anim ihq-mobile-pad" style={{ padding: 32, maxWidth: 1100 }}>
      <PageHeader title="Appointments" subtitle="Manage your schedule"
        actions={<Btn onClick={() => setShowModal(true)}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg> Book Appointment</Btn>}
      />

      {/* Week Strip */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() - 7); setSelectedDate(d.toISOString().slice(0, 10)); }} style={{ color: C.txM, background: C.s3, border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <span style={{ fontSize: 15, fontWeight: 600, color: C.tx }}>{new Date(weekDates[0]).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
            <button onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() + 7); setSelectedDate(d.toISOString().slice(0, 10)); }} style={{ color: C.txM, background: C.s3, border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
          <Btn variant="ghost" size="sm" onClick={() => setSelectedDate(todayStr())}>Today</Btn>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {weekDates.map((d, i) => {
            const isSelected = d === selectedDate;
            const isToday = d === todayStr();
            const count = appointments.filter(a => a.scheduledAt?.startsWith(d)).length;
            return (
              <button key={d} onClick={() => setSelectedDate(d)}
                style={{ padding: '10px 6px', borderRadius: 10, textAlign: 'center', background: isSelected ? C.pri : isToday ? C.priDim : 'transparent', border: `1px solid ${isSelected ? C.pri : isToday ? C.pri + '40' : C.b1}`, cursor: 'pointer', transition: 'all 0.12s' }}>
                <div style={{ fontSize: 11, color: isSelected ? 'rgba(255,255,255,0.8)' : C.txD, marginBottom: 4 }}>{weekNames[i]}</div>
                <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: isSelected ? C.white : C.tx }}>{new Date(d + 'T12:00:00').getDate()}</div>
                {count > 0 && <div style={{ marginTop: 4, width: 6, height: 6, borderRadius: '50%', background: isSelected ? 'rgba(255,255,255,0.6)' : C.pri, margin: '4px auto 0' }} />}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Day's Appointments */}
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>
          {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          <span style={{ fontSize: 13, fontWeight: 400, color: C.txM, marginLeft: 8 }}>{dayAppts.length} appointment{dayAppts.length !== 1 ? 's' : ''}</span>
        </h3>
      </div>

      {dayAppts.length === 0 ? (
        <EmptyState icon="📅" title="No appointments" desc="Nothing scheduled for this day" action={<Btn onClick={() => { setForm(f => ({ ...f, scheduledAt: selectedDate + 'T09:00' })); setShowModal(true); }}>Book Appointment</Btn>} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {dayAppts.map(appt => {
            const client = clientMap[appt.clientId];
            const formula = formulaMap[appt.formulaId];
            const nurse = staffMap[appt.staffId];
            return (
              <Card key={appt.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 4, alignSelf: 'stretch', background: statusColor(appt.status), borderRadius: 2, flexShrink: 0 }} />
                  <div style={{ textAlign: 'center', minWidth: 56 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.pri, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{fmtTime(appt.scheduledAt)}</div>
                    <div style={{ fontSize: 11, color: C.txD }}>{appt.duration || 60} min</div>
                  </div>
                  <Avatar name={client ? `${client.firstName} ${client.lastName}` : '?'} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: C.tx, marginBottom: 2 }}>{client ? `${client.firstName} ${client.lastName}` : 'Unknown Client'}</div>
                    <div style={{ fontSize: 13, color: C.txM }}>{formula?.name || 'Formula TBD'}{nurse ? ` · ${nurse.name}` : ''}</div>
                    {appt.notes && <div style={{ fontSize: 12, color: C.txD, marginTop: 2 }}>{appt.notes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <StatusBadge status={appt.status} />
                    <Select value={appt.status} onChange={v => onUpdate({ ...appt, status: v })}
                      options={[{ value: 'scheduled', label: 'Scheduled' }, { value: 'in-progress', label: 'In Progress' }, { value: 'completed', label: 'Completed' }, { value: 'cancelled', label: 'Cancelled' }, { value: 'no-show', label: 'No Show' }]}
                      style={{ fontSize: 12, padding: '5px 10px', minWidth: 130 }} />
                    <button onClick={() => go('clientDetail', { clientId: appt.clientId })} style={{ color: C.pri, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>View Client</button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Book Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Book Appointment">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Select label="Client" value={form.clientId} onChange={set('clientId')} required placeholder="Select client..."
            options={clients.map(c => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Select label="Nurse / Staff" value={form.staffId} onChange={set('staffId')} placeholder="Select staff..."
              options={staff.map(s => ({ value: s.id, label: s.name }))} />
            <Select label="IV Formula" value={form.formulaId} onChange={set('formulaId')} placeholder="Select formula..."
              options={formulas.map(f => ({ value: f.id, label: f.name }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Input label="Date & Time" type="datetime-local" value={form.scheduledAt} onChange={set('scheduledAt')} />
            <Select label="Duration" value={String(form.duration)} onChange={v => set('duration')(+v)}
              options={[{ value: '30', label: '30 minutes' }, { value: '45', label: '45 minutes' }, { value: '60', label: '1 hour' }, { value: '90', label: '90 minutes' }, { value: '120', label: '2 hours' }]} />
          </div>
          <Textarea label="Notes" value={form.notes} onChange={set('notes')} placeholder="Any special instructions or notes..." rows={2} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={() => setShowModal(false)}>Cancel</Btn>
            <Btn onClick={handleAdd} disabled={!form.clientId || sending}>{sending ? 'Booking & Sending...' : 'Book Appointment'}</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// VISITS VIEW (All Visits)
// ═══════════════════════════════════════════════════════
function VisitsView({ visits, clients, staff, formulas, go, onAdd, clinicId, initNew }) {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(!!initNew);
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));
  const staffMap = Object.fromEntries(staff.map(s => [s.id, s]));
  const [newVisitClient, setNewVisitClient] = useState(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return [...visits].sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt)).filter(v => {
      const client = clientMap[v.clientId];
      return !q || (client && `${client.firstName} ${client.lastName}`.toLowerCase().includes(q)) || (v.formulaName || '').toLowerCase().includes(q);
    });
  }, [visits, search, clientMap]);

  const [addForm, setAddForm] = useState({ clientId: '', formulaId: '', staffId: '', amountCharged: '', notes: '', adverseReactions: '', vitals: { bp: '', hr: '', o2: '', weight: '' }, startedAt: new Date().toISOString().slice(0, 16) });
  const setAF = k => v => setAddForm(f => ({ ...f, [k]: v }));
  const setVital = k => v => setAddForm(f => ({ ...f, vitals: { ...f.vitals, [k]: v } }));
  const selectedFormula = formulas.find(f => f.id === addForm.formulaId);
  useEffect(() => { if (selectedFormula) setAF('amountCharged')(String(selectedFormula.price)); }, [selectedFormula]);

  const handleAdd = () => {
    if (!addForm.clientId || !addForm.formulaId) return;
    onAdd({ ...addForm, clinicId, formulaName: selectedFormula?.name || '', completedAt: new Date().toISOString(), startedAt: new Date(addForm.startedAt).toISOString() });
    setAddForm({ clientId: '', formulaId: '', staffId: '', amountCharged: '', notes: '', adverseReactions: '', vitals: { bp: '', hr: '', o2: '', weight: '' }, startedAt: new Date().toISOString().slice(0, 16) });
    setShowModal(false);
  };

  return (
    <div className="ihq-anim ihq-mobile-pad" style={{ padding: 32, maxWidth: 1100 }}>
      <PageHeader title="Visit Records" subtitle={`${visits.length} total IV sessions recorded`}
        actions={<Btn onClick={() => setShowModal(true)}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg> Record New Visit</Btn>}
      />

      <Card style={{ marginBottom: 20 }}>
        <div style={{ position: 'relative' }}>
          <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.txM }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by client name or formula..." style={{ width: '100%', background: C.s3, border: `1px solid ${C.b1}`, borderRadius: 8, padding: '10px 14px 10px 38px', fontSize: 14, color: C.tx, fontFamily: "'DM Sans', sans-serif" }} />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <EmptyState icon="💉" title="No visits found" desc="Record your first IV session" action={<Btn onClick={() => setShowModal(true)}>Record First Visit</Btn>} />
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.s3, borderBottom: `1px solid ${C.b1}` }}>
                {['Client', 'Formula', 'Nurse', 'Date', 'Vitals', 'Amount', ''].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.txD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((v, idx) => {
                const client = clientMap[v.clientId];
                const nurse = staffMap[v.staffId];
                return (
                  <tr key={v.id} style={{ borderBottom: `1px solid ${C.b1}30`, background: idx % 2 === 0 ? 'transparent' : C.s1 + '60' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={client ? `${client.firstName} ${client.lastName}` : '?'} size={32} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.tx }}>{client ? `${client.firstName} ${client.lastName}` : 'Unknown'}</div>
                          {v.adverseReactions && <div style={{ fontSize: 11, color: C.err }}>⚠ Adverse reaction noted</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.tx }}>{v.formulaName || '—'}</td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.txM }}>{nurse?.name?.split(' ')[0] || '—'}</td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.txM }}>{fmtDate(v.startedAt)}</td>
                    <td style={{ padding: '14px 16px' }}>
                      {v.vitals?.bp ? (
                        <div style={{ fontSize: 11, color: C.txM }}>
                          {v.vitals.bp && <div>BP: {v.vitals.bp}</div>}
                          {v.vitals.hr && <div>HR: {v.vitals.hr}</div>}
                        </div>
                      ) : <span style={{ fontSize: 12, color: C.txD }}>—</span>}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 14, fontWeight: 700, color: C.ok }}>{fmtMoney(v.amountCharged)}</td>
                    <td style={{ padding: '14px 16px' }}>
                      {client && <button onClick={() => go('clientDetail', { clientId: client.id })} style={{ color: C.pri, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>View</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* New Visit Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Record New Visit" width={580}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Select label="Client" value={addForm.clientId} onChange={setAF('clientId')} required placeholder="Select client..."
            options={clients.map(c => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))} />
          <Select label="IV Formula" value={addForm.formulaId} onChange={setAF('formulaId')} required placeholder="Select formula..."
            options={formulas.map(f => ({ value: f.id, label: `${f.name} — ${fmtMoney(f.price)}` }))} />
          {selectedFormula && (
            <div style={{ padding: '10px 14px', background: C.s3, borderRadius: 8, border: `1px solid ${C.b1}` }}>
              <div style={{ fontSize: 12, color: C.txM, marginBottom: 6 }}>Components:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedFormula.components.map((c, i) => <Badge key={i} color={C.pri} dim={C.priDim}>{c.name} {c.dose}{c.unit}</Badge>)}
              </div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Select label="Administering Nurse" value={addForm.staffId} onChange={setAF('staffId')} placeholder="Select staff..."
              options={staff.map(s => ({ value: s.id, label: s.name }))} />
            <Input label="Date & Time" type="datetime-local" value={addForm.startedAt} onChange={setAF('startedAt')} />
          </div>
          <Input label="Amount Charged ($)" type="number" value={addForm.amountCharged} onChange={setAF('amountCharged')} placeholder="0.00" />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Pre-Treatment Vitals</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Input label="Blood Pressure" value={addForm.vitals.bp} onChange={setVital('bp')} placeholder="120/80" />
              <Input label="Heart Rate" value={addForm.vitals.hr} onChange={setVital('hr')} placeholder="72 bpm" />
              <Input label="O2 Saturation" value={addForm.vitals.o2} onChange={setVital('o2')} placeholder="98%" />
              <Input label="Weight" value={addForm.vitals.weight} onChange={setVital('weight')} placeholder="145 lbs" />
            </div>
          </div>
          <Textarea label="Session Notes" value={addForm.notes} onChange={setAF('notes')} placeholder="How is the client feeling? Observations..." rows={2} />
          <Textarea label="Adverse Reactions" value={addForm.adverseReactions} onChange={setAF('adverseReactions')} placeholder="Leave blank if none" rows={2} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={() => setShowModal(false)}>Cancel</Btn>
            <Btn onClick={handleAdd} disabled={!addForm.clientId || !addForm.formulaId} variant="success">Record Visit</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// FORMULAS VIEW
// ═══════════════════════════════════════════════════════
function FormulasView({ formulas, visits, onAdd, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', cat: 'Custom', price: '', desc: '', components: [{ name: '', dose: '', unit: 'mL' }] });
  const set = k => v => setForm(f => ({ ...f, [k]: v }));
  const [filter, setFilter] = useState('All');

  const categories = ['All', ...new Set(formulas.map(f => f.cat))];
  const filtered = filter === 'All' ? formulas : formulas.filter(f => f.cat === filter);

  const addComponent = () => setForm(f => ({ ...f, components: [...f.components, { name: '', dose: '', unit: 'mL' }] }));
  const removeComponent = i => setForm(f => ({ ...f, components: f.components.filter((_, idx) => idx !== i) }));
  const setComp = (i, k) => v => setForm(f => { const comps = [...f.components]; comps[i] = { ...comps[i], [k]: v }; return { ...f, components: comps }; });

  const handleAdd = () => {
    if (!form.name || !form.price) return;
    onAdd({ ...form, isTemplate: false });
    setForm({ name: '', cat: 'Custom', price: '', desc: '', components: [{ name: '', dose: '', unit: 'mL' }] });
    setShowModal(false);
  };

  const visitCounts = Object.fromEntries(formulas.map(f => [f.id, visits.filter(v => v.formulaId === f.id).length]));

  return (
    <div className="ihq-anim ihq-mobile-pad" style={{ padding: 32, maxWidth: 1100 }}>
      <PageHeader title="Drip Formula Library" subtitle={`${formulas.length} formulas · ${visits.length} total sessions administered`}
        actions={<Btn onClick={() => setShowModal(true)}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg> Add Custom Formula</Btn>}
      />

      {/* Category Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)}
            style={{ padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", background: filter === c ? C.pri : C.s3, color: filter === c ? C.white : C.txM, border: `1px solid ${filter === c ? C.pri : C.b1}`, cursor: 'pointer', transition: 'all 0.12s' }}>
            {c}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {filtered.map(f => {
          const catColor = CAT_COLORS[f.cat] || C.pri;
          const usageCount = visitCounts[f.id] || 0;
          return (
            <Card key={f.id} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>{f.name}</span>
                    {f.isTemplate && <Badge color={C.pri} dim={C.priDim}>Template</Badge>}
                  </div>
                  <Badge color={catColor} dim={catColor + '18'}>{f.cat}</Badge>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.ok }}>${f.price}</div>
                  <div style={{ fontSize: 11, color: C.txD }}>{usageCount} uses</div>
                </div>
              </div>
              {f.desc && <p style={{ fontSize: 13, color: C.txM, lineHeight: 1.5 }}>{f.desc}</p>}
              <div>
                <div style={{ fontSize: 11, color: C.txD, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Components</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {f.components.map((c, i) => <Badge key={i} color={catColor} dim={catColor + '15'}>{c.name} {c.dose}{c.unit}</Badge>)}
                </div>
              </div>
              {!f.isTemplate && (
                <div style={{ paddingTop: 10, borderTop: `1px solid ${C.b1}`, display: 'flex', justifyContent: 'flex-end' }}>
                  <Btn variant="ghost" size="sm" onClick={() => onDelete(f.id)} style={{ color: C.err }}>Delete</Btn>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Custom Formula" width={580}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Input label="Formula Name" value={form.name} onChange={set('name')} placeholder="Immune Warrior" required />
            <Input label="Price ($)" type="number" value={form.price} onChange={set('price')} placeholder="199" required />
          </div>
          <Select label="Category" value={form.cat} onChange={set('cat')}
            options={['Wellness', 'Recovery', 'Immunity', 'Anti-Aging', 'Performance', 'Energy', 'Aesthetics', 'Custom'].map(c => ({ value: c, label: c }))} />
          <Textarea label="Description" value={form.desc} onChange={set('desc')} placeholder="What does this drip do?" rows={2} />
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Components</label>
              <Btn variant="ghost" size="sm" onClick={addComponent}>+ Add</Btn>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {form.components.map((comp, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
                  <Input placeholder="Component name" value={comp.name} onChange={setComp(i, 'name')} />
                  <Input placeholder="Dose" value={comp.dose} onChange={setComp(i, 'dose')} />
                  <Select value={comp.unit} onChange={setComp(i, 'unit')} options={['mL', 'g', 'mg', 'mcg', 'IU', '%'].map(u => ({ value: u, label: u }))} />
                  {form.components.length > 1 && (
                    <button onClick={() => removeComponent(i)} style={{ color: C.err, background: C.errDim, border: 'none', borderRadius: 6, width: 32, height: 38, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={() => setShowModal(false)}>Cancel</Btn>
            <Btn onClick={handleAdd} disabled={!form.name || !form.price}>Save Formula</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// STAFF VIEW
// ═══════════════════════════════════════════════════════
function StaffView({ staff, visits, onAdd, onUpdate, onDelete, clinicId, clinic }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'Registered Nurse', active: true });
  const [sending, setSending] = useState(false);
  const [inviteSent, setInviteSent] = useState('');
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const handleAdd = async () => {
    if (!form.name || !form.email) return;
    setSending(true);
    onAdd({ ...form, clinicId });

    // Send invite email
    const inviteLink = `${window.location.origin}/?invite=1&c=${clinicId}&e=${encodeURIComponent(form.email)}&n=${encodeURIComponent(form.name)}&r=${encodeURIComponent(form.role)}&cl=${encodeURIComponent(clinic?.name || 'Your IV Clinic')}`;
    try {
      await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'staff_invite',
          to: form.email,
          clientName: form.name,
          clinicName: clinic?.name || 'Your IV Clinic',
          role: form.role,
          inviteLink,
        }),
      });
      setInviteSent(form.email);
    } catch (e) { console.warn('Invite email failed:', e.message); }

    setForm({ name: '', email: '', phone: '', role: 'Registered Nurse', active: true });
    setSending(false);
    setShowModal(false);
    setTimeout(() => setInviteSent(''), 5000);
  };

  const staffStats = id => ({
    visits: visits.filter(v => v.staffId === id).length,
    revenue: visits.filter(v => v.staffId === id).reduce((s, v) => s + (+v.amountCharged || 0), 0),
    lastVisit: visits.filter(v => v.staffId === id).sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))[0]?.startedAt,
  });

  return (
    <div className="ihq-anim ihq-mobile-pad" style={{ padding: 32, maxWidth: 900 }}>
      <PageHeader title="Staff Management" subtitle={`${staff.length} team members`}
        actions={<Btn onClick={() => setShowModal(true)}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg> Add Staff</Btn>}
      />

      {inviteSent && (
        <div style={{ marginBottom: 20, padding: '12px 18px', background: C.okDim, border: `1px solid ${C.ok}40`, borderRadius: 10, fontSize: 14, color: C.ok, display: 'flex', alignItems: 'center', gap: 8 }}>
          ✉ Invite email sent to <strong>{inviteSent}</strong> — they'll receive a link to create their account
        </div>
      )}

      {staff.length === 0 ? (
        <EmptyState icon="👩‍⚕️" title="No staff members yet" desc="Add your nurses, doctors, and front desk team" action={<Btn onClick={() => setShowModal(true)}>Add First Staff Member</Btn>} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {staff.map(s => {
            const stats = staffStats(s.id);
            return (
              <Card key={s.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar name={s.name} size={48} />
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.tx }}>{s.name}</div>
                      <div style={{ fontSize: 13, color: C.txM }}>{s.role}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.active ? C.ok : C.txD }} />
                    <span style={{ fontSize: 12, color: s.active ? C.ok : C.txD }}>{s.active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                  {s.email && <div style={{ fontSize: 13, color: C.txM }}>✉ {s.email}</div>}
                  {s.phone && <div style={{ fontSize: 13, color: C.txM }}>📞 {s.phone}</div>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', padding: '12px 0', borderTop: `1px solid ${C.b1}`, marginTop: 4, borderBottom: `1px solid ${C.b1}` }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>{stats.visits}</div>
                    <div style={{ fontSize: 11, color: C.txD }}>Visits</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.ok }}>{fmtMoney(stats.revenue)}</div>
                    <div style={{ fontSize: 11, color: C.txD }}>Revenue</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.txM }}>{stats.lastVisit ? fmtDate(stats.lastVisit) : '—'}</div>
                    <div style={{ fontSize: 11, color: C.txD }}>Last Visit</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                  <Btn variant="ghost" size="sm" onClick={() => onUpdate({ ...s, active: !s.active })}>{s.active ? 'Deactivate' : 'Activate'}</Btn>
                  <Btn variant="ghost" size="sm" onClick={() => onDelete(s.id)} style={{ color: C.err }}>Remove</Btn>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Staff Member">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input label="Full Name" value={form.name} onChange={set('name')} placeholder="Dr. Jane Smith" required />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Input label="Email" type="email" value={form.email} onChange={set('email')} placeholder="jane@clinic.com" required />
            <Input label="Phone" type="tel" value={form.phone} onChange={set('phone')} placeholder="(555) 000-0000" />
          </div>
          <Select label="Role" value={form.role} onChange={set('role')}
            options={['Medical Director', 'Physician', 'Nurse Practitioner', 'Registered Nurse', 'LPN', 'Front Desk', 'Manager'].map(r => ({ value: r, label: r }))} />
          <Checkbox label="Active (can be assigned to visits)" checked={form.active} onChange={set('active')} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={() => setShowModal(false)}>Cancel</Btn>
            <Btn onClick={handleAdd} disabled={!form.name || !form.email || sending}>
              {sending ? 'Adding & Sending Invite...' : 'Add Staff & Send Invite'}
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// REVENUE VIEW
// ═══════════════════════════════════════════════════════
function RevenueView({ visits, clients, formulas }) {
  const [period, setPeriod] = useState('month');
  const now = new Date();

  const filteredVisits = useMemo(() => {
    if (period === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 86400000);
      return visits.filter(v => new Date(v.startedAt) > weekAgo);
    }
    if (period === 'month') {
      return visits.filter(v => new Date(v.startedAt).getMonth() === now.getMonth() && new Date(v.startedAt).getFullYear() === now.getFullYear());
    }
    if (period === 'year') {
      return visits.filter(v => new Date(v.startedAt).getFullYear() === now.getFullYear());
    }
    return visits;
  }, [visits, period]);

  const totalRevenue = filteredVisits.reduce((s, v) => s + (+v.amountCharged || 0), 0);
  const avgPerVisit = filteredVisits.length > 0 ? totalRevenue / filteredVisits.length : 0;
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));

  const byFormula = formulas.map(f => ({
    name: f.name, count: filteredVisits.filter(v => v.formulaId === f.id).length,
    revenue: filteredVisits.filter(v => v.formulaId === f.id).reduce((s, v) => s + (+v.amountCharged || 0), 0),
  })).filter(f => f.count > 0).sort((a, b) => b.revenue - a.revenue);

  const topClients = clients.map(c => ({
    ...c, visits: filteredVisits.filter(v => v.clientId === c.id).length,
    revenue: filteredVisits.filter(v => v.clientId === c.id).reduce((s, v) => s + (+v.amountCharged || 0), 0),
  })).filter(c => c.visits > 0).sort((a, b) => b.revenue - a.revenue).slice(0, 5);

  const periodLabel = { week: 'This Week', month: 'This Month', year: 'This Year', all: 'All Time' };

  return (
    <div className="ihq-anim ihq-mobile-pad" style={{ padding: 32, maxWidth: 1100 }}>
      <PageHeader title="Revenue" subtitle="Financial overview and analytics"
        actions={
          <Tabs tabs={[{ value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }, { value: 'year', label: 'Year' }, { value: 'all', label: 'All' }]} active={period} onChange={setPeriod} />
        }
      />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        <StatCard label={`Revenue (${periodLabel[period]})`} value={fmtMoney(totalRevenue)} sub={`${filteredVisits.length} sessions`} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>} color={C.ok} />
        <StatCard label="Avg per Session" value={fmtMoney(avgPerVisit)} sub="Revenue per visit" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>} color={C.pri} />
        <StatCard label="Total Sessions" value={filteredVisits.length} sub="IV sessions administered" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>} color={C.acc} />
        <StatCard label="Active Clients" value={new Set(filteredVisits.map(v => v.clientId)).size} sub="Unique clients seen" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>} color={C.pur} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* By Formula */}
        <Card>
          <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 18 }}>Revenue by Formula</h3>
          {byFormula.length === 0 ? <EmptyState icon="🧪" title="No data" desc="Record visits to see revenue breakdown" /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {byFormula.map((f, i) => {
                const pct = byFormula[0].revenue > 0 ? (f.revenue / byFormula[0].revenue) * 100 : 0;
                return (
                  <div key={f.name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 20, height: 20, borderRadius: 5, background: C.pri + '25', color: C.pri, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                        <span style={{ fontSize: 14, fontWeight: 500, color: C.tx }}>{f.name}</span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: C.ok }}>{fmtMoney(f.revenue)}</span>
                        <span style={{ fontSize: 12, color: C.txM, marginLeft: 8 }}>({f.count} visits)</span>
                      </div>
                    </div>
                    <div style={{ height: 6, background: C.s3, borderRadius: 3 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${C.pri}, ${C.ok})`, borderRadius: 3, transition: 'width 0.3s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Top Clients */}
        <Card>
          <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 18 }}>Top Clients</h3>
          {topClients.length === 0 ? <EmptyState icon="👥" title="No data" desc="Record visits to see top clients" /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {topClients.map((c, i) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 24, height: 24, borderRadius: '50%', background: C.pri + '20', color: C.pri, fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                  <Avatar name={`${c.firstName} ${c.lastName}`} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.tx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.firstName} {c.lastName}</div>
                    <div style={{ fontSize: 12, color: C.txM }}>{c.visits} visits</div>
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.ok, flexShrink: 0 }}>{fmtMoney(c.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* All Transactions */}
      <Card style={{ marginTop: 20, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.b1}` }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>All Transactions</h3>
        </div>
        {filteredVisits.length === 0 ? (
          <EmptyState icon="💰" title="No transactions" desc="No visits in this time period" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.s3 }}>
                {['Date', 'Client', 'Formula', 'Amount'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.txD, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${C.b1}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...filteredVisits].sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt)).map((v, i) => {
                const client = clientMap[v.clientId];
                return (
                  <tr key={v.id} style={{ borderBottom: `1px solid ${C.b1}20`, background: i % 2 === 0 ? 'transparent' : C.s1 + '40' }}>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: C.txM }}>{fmtDate(v.startedAt)}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 500, color: C.tx }}>{client ? `${client.firstName} ${client.lastName}` : '—'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: C.txM }}>{v.formulaName || '—'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, color: C.ok }}>{fmtMoney(v.amountCharged)}</td>
                  </tr>
                );
              })}
              <tr style={{ background: C.s3, borderTop: `1px solid ${C.b1}` }}>
                <td colSpan={3} style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, color: C.tx }}>Total</td>
                <td style={{ padding: '12px 16px', fontSize: 16, fontWeight: 800, color: C.ok, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{fmtMoney(totalRevenue)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// PAYMENT MODAL — In-app Stripe card form
// ═══════════════════════════════════════════════════════
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#dce8ff',
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '15px',
      fontSmoothing: 'antialiased',
      '::placeholder': { color: '#3d5068' },
      iconColor: '#0ea5e9',
    },
    invalid: { color: '#f43f5e', iconColor: '#f43f5e' },
  },
};

function CheckoutForm({ plan, billing, priceId, user, clinic, onSuccess, onCancel, onUpdateClinic }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardFocused, setCardFocused] = useState(false);
  const [name, setName] = useState(user?.name || '');

  const price = billing === 'monthly'
    ? PLAN_PRICES[plan?.id]?.monthly
    : PLAN_PRICES[plan?.id]?.yearly;

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    if (!name.trim()) { setError('Please enter the name on your card.'); return; }
    setLoading(true);
    setError('');

    try {
      // 1. Create subscription on backend
      const res = await fetch('/api/create-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          email: user?.email || clinic?.email,
          clinicName: clinic?.name,
          userId: user?.id,
          referralCode: DB.get('referralCode') || '',
          customerId: clinic?.stripeCustomerId || null,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      // 2. If trial — no payment needed yet
      if (!data.clientSecret) {
        onUpdateClinic({ ...clinic, plan: plan?.id, stripeCustomerId: data.customerId, stripeSubscriptionId: data.subscriptionId });
        onSuccess();
        return;
      }

      // 3. Confirm card payment
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { name, email: user?.email || clinic?.email },
        },
      });

      if (stripeError) throw new Error(stripeError.message);

      if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
        onUpdateClinic({ ...clinic, plan: plan?.id, stripeCustomerId: data.customerId, stripeSubscriptionId: data.subscriptionId });
        onSuccess();
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Plan summary */}
      <div style={{ padding: '16px 20px', background: C.priDim, border: `1px solid ${C.pri}30`, borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.pri, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{plan?.name} Plan</div>
          <div style={{ fontSize: 13, color: C.txM, marginTop: 2 }}>14-day free trial included · Cancel anytime</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>${price}</div>
          <div style={{ fontSize: 12, color: C.txM }}>per {billing === 'monthly' ? 'month' : 'year'}</div>
        </div>
      </div>

      {/* Name on card */}
      <Input label="Name on Card" value={name} onChange={setName} placeholder="Sarah Johnson" required />

      {/* Card element */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: C.txM, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>Card Details <span style={{ color: C.err }}>*</span></label>
        <div style={{ padding: '13px 14px', background: C.s3, border: `1px solid ${cardFocused ? C.pri : C.b1}`, borderRadius: 8, transition: 'border 0.15s', boxShadow: cardFocused ? `0 0 0 3px ${C.priDim}` : 'none' }}>
          <CardElement options={CARD_ELEMENT_OPTIONS}
            onFocus={() => setCardFocused(true)}
            onBlur={() => setCardFocused(false)} />
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: C.errDim, border: `1px solid ${C.err}33`, borderRadius: 8, fontSize: 13, color: C.err }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ padding: '10px 14px', background: C.s3, borderRadius: 8, fontSize: 12, color: C.txD, display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.ok} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        Secured by Stripe · Your card data never touches our servers
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn variant="secondary" onClick={onCancel} disabled={loading}>Cancel</Btn>
        <Btn variant="success" onClick={handleSubmit} disabled={!stripe || loading}>
          {loading ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
              Processing...
            </span>
          ) : `Start Free Trial → Pay $${price}/${billing === 'monthly' ? 'mo' : 'yr'}`}
        </Btn>
      </div>
    </div>
  );
}

function PaymentModal({ open, onClose, plan, billing, user, clinic, onUpdateClinic, onSuccess }) {
  if (!open || !plan) return null;
  const priceId = STRIPE_PRICES[plan?.id]?.[billing];

  return (
    <Modal open={open} onClose={onClose} title={`Upgrade to ${plan?.name}`} width={520}>
      <Elements stripe={stripePromise}>
        <CheckoutForm
          plan={plan} billing={billing} priceId={priceId}
          user={user} clinic={clinic}
          onUpdateClinic={onUpdateClinic}
          onSuccess={() => { onClose(); onSuccess(); }}
          onCancel={onClose}
        />
      </Elements>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════
// SETTINGS VIEW
// ═══════════════════════════════════════════════════════
function SettingsView({ clinic, user, onUpdateClinic, clients, visits, forms, staff }) {
  const [form, setForm] = useState({ name: clinic?.name || '', email: clinic?.email || '', phone: clinic?.phone || '', address: clinic?.address || '', website: clinic?.website || '' });
  const [saved, setSaved] = useState(false);
  const [billing, setBilling] = useState('monthly');
  const [payModal, setPayModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paySuccess, setPaySuccess] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showHipaa, setShowHipaa] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const handleExport = () => {
    const data = { clinic, clients, visits, forms, staff, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ivpilot-export-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSave = () => { onUpdateClinic(form); setSaved(true); setTimeout(() => setSaved(false), 2500); };

  const trialDays = clinic?.trialEnds ? Math.max(0, Math.ceil((new Date(clinic.trialEnds) - Date.now()) / 86400000)) : 0;
  const currentPlan = clinic?.plan || 'trial';

  const handleUpgrade = (planId, plans) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan || !STRIPE_PRICES[planId]) return;
    setSelectedPlan(plan);
    setPayModal(true);
  };

  const handlePortal = async () => {
    if (!clinic?.stripeCustomerId) return alert('No billing account found. Please subscribe first.');
    setPortalLoading(true);
    try { await openBillingPortal(clinic.stripeCustomerId); }
    catch (err) { alert('Could not open portal: ' + err.message); }
    setPortalLoading(false);
  };

  const plans = [
    { id: 'growth', name: 'Clinic', monthlyPrice: 299, yearlyPrice: 299 * 12, features: ['Unlimited clients & staff', 'All features', 'Membership programs', 'Priority support'], popular: true },
    { id: 'multi', name: 'Multi-Location', monthlyPrice: 549, yearlyPrice: 549 * 12, features: ['Up to 5 locations', 'Unlimited everything', 'Cross-location reports', 'Dedicated onboarding', 'Phone support'] },
    { id: 'enterprise', name: 'Enterprise', monthlyPrice: null, yearlyPrice: null, features: ['Unlimited locations', 'White label', 'Custom integrations', 'SLA support', 'Franchise tools'] },
  ];

  return (
    <div className="ihq-anim ihq-mobile-pad" style={{ padding: 32, maxWidth: 960 }}>
      <PageHeader title="Settings" subtitle="Manage your clinic and subscription" />

      {/* Payment success inline banner */}
      {paySuccess && (
        <div style={{ marginBottom: 20, padding: '14px 20px', background: C.okDim, border: `1px solid ${C.ok}40`, borderRadius: 10, fontSize: 14, fontWeight: 600, color: C.ok, display: 'flex', alignItems: 'center', gap: 10 }}>
          🎉 Payment successful! Your plan has been activated.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Trial / Plan Banner */}
        {trialDays > 0 && (
          <div style={{ padding: '16px 20px', background: `linear-gradient(135deg, ${C.accDim}, ${C.priDim})`, border: `1px solid ${C.acc}40`, borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.acc }}>⚡ Free Trial — {trialDays} days remaining</div>
              <div style={{ fontSize: 13, color: C.txM, marginTop: 2 }}>Add a payment method now to keep access after your trial ends</div>
            </div>
            <Btn variant="accent" onClick={() => handleUpgrade('growth', plans)}>Upgrade Now</Btn>
          </div>
        )}

        {/* Clinic Info */}
        <Card>
          <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 18 }}>Clinic Information</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Clinic Name" value={form.name} onChange={set('name')} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Input label="Contact Email" type="email" value={form.email} onChange={set('email')} />
              <Input label="Phone Number" type="tel" value={form.phone} onChange={set('phone')} />
            </div>
            <Input label="Address" value={form.address} onChange={set('address')} placeholder="123 Wellness Ave, Suite 100, Miami FL 33101" />
            <Input label="Website" value={form.website} onChange={set('website')} placeholder="https://yoursite.com" />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              {saved && <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.ok, fontSize: 14 }}>✓ Saved!</div>}
              <Btn onClick={handleSave}>Save Changes</Btn>
            </div>
          </div>
        </Card>

        {/* Billing — compact, upgrade lives in sidebar */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>Subscription</h3>
              <div style={{ fontSize: 13, color: C.txM, marginTop: 4 }}>
                Current plan: <strong style={{ color: C.pri }}>{PLAN_NAMES[currentPlan] || 'Free Trial'}</strong>
                {trialDays > 0 && <span style={{ color: C.acc, marginLeft: 10 }}>⚡ {trialDays} trial days left</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {clinic?.stripeCustomerId ? (
                <Btn variant="secondary" onClick={handlePortal} disabled={portalLoading}>
                  {portalLoading ? 'Loading...' : 'Manage Billing'}
                </Btn>
              ) : (
                <Btn variant="primary" onClick={() => handleUpgrade('growth', plans)}>
                  Upgrade Plan →
                </Btn>
              )}
            </div>
          </div>
          <p style={{ fontSize: 12, color: C.txD, marginTop: 12 }}>All plans include every feature — no add-on fees. Cancel anytime. Click <strong>Upgrade Plan</strong> in the left sidebar to see all plans.</p>
        </Card>

        {/* Account */}
        <Card>
          <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 16 }}>Account</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: `1px solid ${C.b1}`, marginBottom: 14 }}>
            <Avatar name={user?.name || ''} size={44} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.tx }}>{user?.name}</div>
              <div style={{ fontSize: 13, color: C.txM }}>{user?.email} · {user?.role}</div>
            </div>
            {clinic?.stripeCustomerId && (
              <Badge color={C.ok} dim={C.okDim}>✓ Billing Active</Badge>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'HIPAA Compliance', desc: 'All data stored with HIPAA-grade encryption', action: () => setShowHipaa(true), badge: '✓ Active', badgeColor: C.ok },
              { label: 'Audit Trail', desc: `${forms?.filter(f => f.signed).length || 0} signed consent forms on record`, action: () => setShowAudit(true), badge: 'View Log', badgeColor: C.pri },
              { label: 'Data Export', desc: 'Download all clinic data as JSON', action: handleExport, badge: 'Download', badgeColor: C.acc },
            ].map(item => (
              <button key={item.label} onClick={item.action}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: C.s3, borderRadius: 8, border: `1px solid ${C.b1}`, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', width: '100%' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.b2}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.b1}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.tx }}>{item.label}</div>
                  <div style={{ fontSize: 12, color: C.txM }}>{item.desc}</div>
                </div>
                <Badge color={item.badgeColor} dim={item.badgeColor + '18'}>{item.badge}</Badge>
              </button>
            ))}
          </div>

          {/* HIPAA Modal */}
          <Modal open={showHipaa} onClose={() => setShowHipaa(false)} title="HIPAA Compliance" width={520}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ padding: '14px 18px', background: C.okDim, border: `1px solid ${C.ok}30`, borderRadius: 10, fontSize: 14, color: C.ok, fontWeight: 600 }}>
                ✓ Your clinic is operating with HIPAA-grade data practices
              </div>
              {[
                { title: 'Data Encryption', desc: 'All client records, medical history, and consent forms are stored with AES-256 encryption.' },
                { title: 'Access Controls', desc: 'Role-based access ensures only authorized staff can view sensitive client data.' },
                { title: 'Signed Consent Forms', desc: 'Every consent form is timestamped and signed — creating an immutable legal record.' },
                { title: 'Audit Logging', desc: 'All data access and changes are logged with timestamps and user identification.' },
                { title: 'Data Retention', desc: 'Client records are retained per your state\'s medical record retention requirements.' },
                { title: 'Business Associate Agreement', desc: 'Email hello@ivpilot.com to request a signed BAA for your clinic files.' },
              ].map(item => (
                <div key={item.title} style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.ok, marginTop: 6, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.tx }}>{item.title}</div>
                    <div style={{ fontSize: 13, color: C.txM, marginTop: 2 }}>{item.desc}</div>
                  </div>
                </div>
              ))}
              <p style={{ fontSize: 12, color: C.txD, marginTop: 4 }}>Need a BAA or compliance documentation for your state board? Email <a href="mailto:hello@ivpilot.com" style={{ color: C.pri }}>hello@ivpilot.com</a></p>
            </div>
          </Modal>

          {/* Audit Trail Modal */}
          <Modal open={showAudit} onClose={() => setShowAudit(false)} title="Consent Form Audit Trail" width={620}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ padding: '10px 14px', background: C.priDim, border: `1px solid ${C.pri}25`, borderRadius: 8, fontSize: 13, color: C.pri }}>
                📋 {forms?.filter(f => f.signed).length || 0} signed consent forms on record · All timestamps are immutable
              </div>
              {(!forms || forms.filter(f => f.signed).length === 0) ? (
                <EmptyState icon="📋" title="No signed forms yet" desc="Consent forms will appear here once clients sign their intake forms" />
              ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: C.s3, borderBottom: `1px solid ${C.b1}` }}>
                        {['Client ID', 'Signed At', 'Status', 'Consent'].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.txD, textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {forms.filter(f => f.signed).map((f, i) => (
                        <tr key={f.id} style={{ borderBottom: `1px solid ${C.b1}20` }}>
                          <td style={{ padding: '10px 14px', fontSize: 12, color: C.txM, fontFamily: 'monospace' }}>{f.clientId?.slice(0, 8)}...</td>
                          <td style={{ padding: '10px 14px', fontSize: 13, color: C.tx }}>{fmtDate(f.signedAt)} {fmtTime(f.signedAt)}</td>
                          <td style={{ padding: '10px 14px' }}><Badge color={C.ok} dim={C.okDim}>✓ Signed</Badge></td>
                          <td style={{ padding: '10px 14px' }}><Badge color={C.pri} dim={C.priDim}>Agreed</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Modal>
        </Card>
      </div>

      {/* In-app Payment Modal */}
      <PaymentModal
        open={payModal}
        onClose={() => setPayModal(false)}
        plan={selectedPlan}
        billing={billing}
        user={user}
        clinic={clinic}
        onUpdateClinic={onUpdateClinic}
        onSuccess={() => { setPaySuccess(true); setTimeout(() => setPaySuccess(false), 6000); }}
      />
    </div>
  );
}
// ═══════════════════════════════════════════════════════


function CommandCenterView({ user, clinic }) {
  const JOSHUA_EMAIL = 'joshuaporo@gmail.com';
  const [activeTab, setActiveTab] = useState('clinics');
  const [clinics, setClinics] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReplies, setTicketReplies] = useState([]);
  const [adminReply, setAdminReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [msgTarget, setMsgTarget] = useState(null);
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [sending, setSending] = useState(false);
  const [msgSent, setMsgSent] = useState(false);

  if (user.email !== JOSHUA_EMAIL) {
    return (
      <div style={{ maxWidth: 500, margin: '80px auto', textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.tx }}>Access Restricted</div>
        <div style={{ fontSize: 14, color: C.txM, marginTop: 8 }}>Command Center is only accessible to IVPilot administrators.</div>
      </div>
    );
  }

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    const [{ data: clinicsData }, { data: ticketsData }] = await Promise.all([
      supabase.from('clinics').select('*').order('created_at', { ascending: false }),
      supabase.from('support_tickets').select('*').order('created_at', { ascending: false }),
    ]);
    if (clinicsData) setClinics(clinicsData);
    if (ticketsData) setTickets(ticketsData);
    setLoading(false);
  }

  async function loadReplies(ticketId) {
    const { data } = await supabase.from('ticket_replies').select('*').eq('ticket_id', ticketId).order('created_at', { ascending: true });
    if (data) setTicketReplies(data);
  }

  async function sendReply() {
    if (!adminReply || !selectedTicket) return;
    const { data } = await supabase.from('ticket_replies').insert({
      ticket_id: selectedTicket.id, message: adminReply, sender: 'admin', sender_name: 'IVPilot Support'
    }).select().single();
    if (data) { setTicketReplies(prev => [...prev, data]); setAdminReply(''); }
    await supabase.from('support_tickets').update({ status: 'in_progress', updated_at: new Date().toISOString() }).eq('id', selectedTicket.id);
    if (selectedTicket.user_email) {
      await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'ticket_reply', to: selectedTicket.user_email, clientName: selectedTicket.user_name, clinicName: 'IVPilot Support', subject: selectedTicket.subject, message: adminReply }) });
    }
  }

  async function sendMessage() {
    if (!msgSubject || !msgBody || !msgTarget) return;
    setSending(true);
    await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'client_message', to: msgTarget.email, clientName: msgTarget.name, clinicName: 'IVPilot', message: msgBody, portalLink: null }) });
    setSending(false); setMsgSent(true);
    setTimeout(() => { setMsgSent(false); setMsgTarget(null); setMsgSubject(''); setMsgBody(''); }, 2000);
  }

  async function updatePlan(clinicId, newPlan) {
    await supabase.from('clinics').update({ plan: newPlan }).eq('id', clinicId);
    setClinics(prev => prev.map(c => c.id === clinicId ? { ...c, plan: newPlan } : c));
  }

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const statusColor = s => s === 'resolved' ? C.ok : s === 'in_progress' ? C.pri : C.acc;
  const statusBg = s => s === 'resolved' ? C.okDim : s === 'in_progress' ? C.priDim : C.accDim;

  const PLAN_PRICES = { starter: 149, growth: 299, multi: 549 };
  const activePaid = clinics.filter(c => c.plan && c.plan !== 'trial');
  const mrr = activePaid.reduce((sum, c) => sum + (PLAN_PRICES[c.plan] || 0), 0);

  const TABS = [
    { id: 'clinics', label: 'Clinics' },
    { id: 'tickets', label: 'Support Tickets' },
    { id: 'revenue', label: 'Revenue' },
    { id: 'messages', label: 'Messages' },
    { id: 'errors', label: 'Errors' },
  ];
  const [errorLogs, setErrorLogs] = React.useState([]);
  const [errorsLoading, setErrorsLoading] = React.useState(false);

  async function loadErrors() {
    setErrorsLoading(true);
    const logs = await getErrorLogs();
    setErrorLogs(logs);
    setErrorsLoading(false);
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.tx, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>⚡ Command Center</h1>
        <p style={{ color: C.txM, fontSize: 13, margin: '4px 0 0' }}>IVPilot admin dashboard</p>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: C.s2, borderRadius: 10, padding: 4 }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => { setSelectedTicket(null); setActiveTab(tab.id); }}
            style={{ flex: 1, padding: '8px 0', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 500,
              background: activeTab === tab.id ? C.pri : 'transparent', color: activeTab === tab.id ? '#fff' : C.txM }}>{tab.label}</button>
        ))}
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: C.txD }}>Loading...</div> : (
        <>
          {activeTab === 'clinics' && (
            <div>
              <div style={{ fontSize: 13, color: C.txD, marginBottom: 12 }}>{clinics.length} total clinics</div>
              {clinics.map(cl => (
                <div key={cl.id} style={{ background: C.s1, border: '1px solid ' + C.b1, borderRadius: 10, padding: '14px 18px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.tx }}>{cl.name || '—'}</div>
                    <div style={{ fontSize: 12, color: C.txD }}>{cl.email} · Joined {fmtDate(cl.created_at)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select defaultValue={cl.plan || 'trial'} onChange={e => updatePlan(cl.id, e.target.value)}
                      style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: C.s2, border: '1px solid ' + C.b1, color: C.pri, cursor: 'pointer' }}>
                      {['trial', 'starter', 'growth', 'multi'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <button onClick={() => { setMsgTarget(cl); setActiveTab('messages'); }}
                      style={{ background: 'none', border: '1px solid ' + C.b1, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: C.txM }}>Message</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'tickets' && !selectedTicket && (
            <div>
              <div style={{ fontSize: 13, color: C.txD, marginBottom: 12 }}>{tickets.length} total tickets</div>
              {tickets.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: C.txD }}>No tickets yet.</div> :
                tickets.map(t => (
                  <div key={t.id} onClick={async () => { setSelectedTicket(t); await loadReplies(t.id); }}
                    style={{ background: C.s1, border: '1px solid ' + C.b1, borderRadius: 10, padding: '14px 18px', marginBottom: 8, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.tx, marginBottom: 2 }}>{t.subject}</div>
                        <div style={{ fontSize: 12, color: C.txD }}>{t.user_name || 'Unknown'} · {fmtDate(t.created_at)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {t.priority === 'urgent' && <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: C.errDim, color: C.err }}>URGENT</span>}
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: statusBg(t.status), color: statusColor(t.status) }}>{t.status.replace('_', ' ')}</span>
                        <span style={{ fontSize: 12, color: C.pri, fontWeight: 600 }}>View →</span>
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          )}

          {activeTab === 'tickets' && selectedTicket && (
            <div>
              <button onClick={() => { setSelectedTicket(null); setTicketReplies([]); }}
                style={{ background: 'none', border: '1px solid ' + C.b1, borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer', color: C.txM, marginBottom: 16 }}>← Back to tickets</button>
              <Card>
                <h2 style={{ fontSize: 17, fontWeight: 800, color: C.tx, margin: '0 0 8px' }}>{selectedTicket.subject}</h2>
                <div style={{ fontSize: 12, color: C.txD, marginBottom: 16 }}>From: {selectedTicket.user_name || 'Unknown'} · {fmtDate(selectedTicket.created_at)}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 400, overflowY: 'auto', marginBottom: 20 }}>
                  {ticketReplies.map(r => {
                    const isAdmin = r.sender === 'admin';
                    return (
                      <div key={r.id} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                        <div style={{ maxWidth: '75%', padding: '12px 16px', borderRadius: 12, background: isAdmin ? C.pri : C.s3, border: '1px solid ' + (isAdmin ? C.pri + '50' : C.b1) }}>
                          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6, color: isAdmin ? 'rgba(255,255,255,0.7)' : C.txD }}>{r.sender_name} · {fmtDate(r.created_at?.split('T')[0])}</div>
                          <div style={{ fontSize: 14, lineHeight: 1.6, color: isAdmin ? '#fff' : C.tx, whiteSpace: 'pre-wrap' }}>{r.message}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <textarea value={adminReply} onChange={e => setAdminReply(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder="Reply as IVPilot Support... (Enter to send)" rows={4}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid ' + C.b1, fontSize: 14, color: C.tx, background: C.s2, outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 10, fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Btn onClick={sendReply} disabled={!adminReply}>Send Reply</Btn>
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'revenue' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                {[
                  { label: 'MRR', value: '$' + mrr.toLocaleString() },
                  { label: 'ARR', value: '$' + (mrr * 12).toLocaleString() },
                  { label: 'Paid Clinics', value: activePaid.length },
                ].map(stat => (
                  <div key={stat.label} style={{ background: C.s1, border: '1px solid ' + C.b1, borderRadius: 12, padding: 20, textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: C.tx }}>{stat.value}</div>
                    <div style={{ fontSize: 12, color: C.txD, marginTop: 4 }}>{stat.label}</div>
                  </div>
                ))}
              </div>
              {['trial', 'starter', 'growth', 'multi'].map(plan => {
                const count = clinics.filter(c => c.plan === plan).length;
                const revenue = count * (PLAN_PRICES[plan] || 0);
                return (
                  <div key={plan} style={{ background: C.s1, border: '1px solid ' + C.b1, borderRadius: 10, padding: '14px 18px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: C.priDim, color: C.pri }}>{plan}</span>
                      <span style={{ fontSize: 14, color: C.tx }}>{count} clinics</span>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: C.tx }}>${revenue}/mo</span>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'errors' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ fontSize: 15, fontWeight: 800, color: C.tx, margin: 0 }}>Error Logs</h3>
                <button onClick={loadErrors} style={{ background: C.pri, color: '#fff', border: 'none', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {errorsLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              {errorLogs.length === 0
                ? <div style={{ textAlign: 'center', padding: 40, color: C.txD }}>
                    {errorsLoading ? 'Loading...' : 'No errors logged. Click Refresh to load.'}
                  </div>
                : errorLogs.map(log => (
                    <div key={log.id} style={{ background: log.resolved ? C.s1 : C.errDim, border: '1px solid ' + (log.resolved ? C.b1 : C.err + '40'), borderRadius: 10, padding: '14px 18px', marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: log.resolved ? C.okDim : C.errDim, color: log.resolved ? C.ok : C.err }}>
                              {log.resolved ? 'RESOLVED' : 'UNRESOLVED'}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: C.tx }}>{log.action}</span>
                          </div>
                          <div style={{ fontSize: 13, color: C.tx, marginBottom: 4 }}>{log.errorMessage}</div>
                          {log.errorCode && <div style={{ fontSize: 11, color: C.txD }}>Code: {log.errorCode}</div>}
                          <div style={{ fontSize: 11, color: C.txD, marginTop: 4 }}>{fmtDate(log.createdAt?.split('T')[0])} · Clinic: {log.clinicId || '—'}</div>
                        </div>
                          <button onClick={async () => {
                            await resolveError(log.id);
                            setErrorLogs(p => p.map(x => x.id === log.id ? {...x, resolved: true} : x));
                          }} style={{ background: C.ok, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginLeft: 12, whiteSpace: 'nowrap' }}>
                            Mark Resolved
                          </button>
                        )}
                      </div>
                    </div>
                  ))
              }
            </div>
          )}

          {activeTab === 'messages' && (
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: C.tx, marginBottom: 16 }}>Send Message to Clinic</h3>
              {msgSent && <div style={{ background: C.okDim, border: '1px solid ' + C.ok + '50', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.ok, marginBottom: 16 }}>Message sent successfully.</div>}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.txM, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Recipient</label>
                <select value={msgTarget?.id || ''} onChange={e => setMsgTarget(clinics.find(c => c.id === e.target.value) || null)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid ' + C.b1, fontSize: 14, color: C.tx, background: C.s2, outline: 'none', fontFamily: 'inherit' }}>
                  <option value=''>Select a clinic...</option>
                  {clinics.map(c => <option key={c.id} value={c.id}>{c.name} — {c.email}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.txM, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Subject</label>
                <input value={msgSubject} onChange={e => setMsgSubject(e.target.value)} placeholder='Message subject'
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid ' + C.b1, fontSize: 14, color: C.tx, background: C.s2, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.txM, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Message</label>
                <textarea value={msgBody} onChange={e => setMsgBody(e.target.value)} placeholder='Type your message...' rows={6}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid ' + C.b1, fontSize: 14, color: C.tx, background: C.s2, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Btn onClick={sendMessage} disabled={sending || !msgTarget || !msgSubject || !msgBody}>{sending ? 'Sending...' : 'Send Message'}</Btn>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SupportView({ user, clinic }) {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [reply, setReply] = useState('');
  const [priority, setPriority] = useState('normal');
  const [showNewModal, setShowNewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTickets(); }, []);

  async function loadTickets() {
    const { data } = await supabase.from('support_tickets').select('*').eq('clinic_id', clinic.id).order('created_at', { ascending: false });
    if (data) setTickets(data);
    setLoading(false);
  }

  async function loadReplies(ticketId) {
    const { data } = await supabase.from('ticket_replies').select('*').eq('ticket_id', ticketId).order('created_at', { ascending: true });
    if (data) setReplies(data);
  }

  async function submitTicket() {
    if (!subject || !message) return;
    setSubmitting(true);
    const { data } = await supabase.from('support_tickets').insert({
      clinic_id: clinic.id, user_id: user.id, user_name: user.name, user_email: user.email,
      subject, message, status: 'open', priority
    }).select().single();
    if (data) {
      await supabase.from('ticket_replies').insert({ ticket_id: data.id, message, sender: 'user', sender_name: user.name || 'Clinic Owner' });
      setTickets(prev => [data, ...prev]);
      setSubject(''); setMessage(''); setPriority('normal'); setShowNewModal(false);
      try {
        await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'new_support_ticket', to: 'joshuaporo@gmail.com', clientName: user.name, clinicName: clinic.name, subject, message, priority }) });
      } catch(e) {}
    }
    setSubmitting(false);
  }

  async function sendReply() {
    if (!reply || !selectedTicket) return;
    const { data } = await supabase.from('ticket_replies').insert({
      ticket_id: selectedTicket.id, message: reply, sender: 'user', sender_name: user.name || 'Clinic Owner'
    }).select().single();
    if (data) { setReplies(prev => [...prev, data]); setReply(''); }
    await supabase.from('support_tickets').update({ updated_at: new Date().toISOString() }).eq('id', selectedTicket.id);
  }

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const statusColor = s => s === 'resolved' ? C.ok : s === 'in_progress' ? C.pri : C.acc;
  const statusBg = s => s === 'resolved' ? C.okDim : s === 'in_progress' ? C.priDim : C.accDim;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.tx, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Support</h1>
          <p style={{ color: C.txM, fontSize: 13, margin: '4px 0 0' }}>Get help from the IVPilot team</p>
        </div>
        {!selectedTicket && <Btn onClick={() => setShowNewModal(true)}>+ New Ticket</Btn>}
      </div>

      {selectedTicket ? (
        <div>
          <button onClick={() => { setSelectedTicket(null); setReplies([]); }} style={{ background: 'none', border: '1px solid ' + C.b1, borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer', color: C.txM, marginBottom: 16 }}>← Back</button>
          <Card>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: C.tx, margin: '0 0 8px' }}>{selectedTicket.subject}</h2>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: statusBg(selectedTicket.status), color: statusColor(selectedTicket.status) }}>{selectedTicket.status.replace('_', ' ')}</span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: selectedTicket.priority === 'urgent' ? C.errDim : C.s2, color: selectedTicket.priority === 'urgent' ? C.err : C.txM }}>{selectedTicket.priority}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 400, overflowY: 'auto', marginBottom: 20 }}>
              {replies.map(r => {
                const isAdmin = r.sender === 'admin';
                return (
                  <div key={r.id} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-start' : 'flex-end' }}>
                    <div style={{ maxWidth: '75%', padding: '12px 16px', borderRadius: 12, background: isAdmin ? C.s3 : C.priDim, border: '1px solid ' + (isAdmin ? C.b1 : C.pri + '30') }}>
                      <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6, color: C.txD }}>{r.sender_name} · {fmtDate(r.created_at?.split('T')[0])}</div>
                      <div style={{ fontSize: 14, lineHeight: 1.6, color: C.tx, whiteSpace: 'pre-wrap' }}>{r.message}</div>
                    </div>
                  </div>
                );
              })}
              {replies.length === 0 && <div style={{ color: C.txD, fontSize: 13, textAlign: 'center', padding: 20 }}>No replies yet. Our team will respond within 24 hours.</div>}
            </div>
            {selectedTicket.status !== 'resolved' && (
              <div>
                <textarea value={reply} onChange={e => setReply(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder="Type your message... (Enter to send, Shift+Enter for new line)" rows={3}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid ' + C.b1, fontSize: 14, color: C.tx, background: C.s2, outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 10, fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Btn onClick={sendReply} disabled={!reply}>Send Reply</Btn>
                </div>
              </div>
            )}
          </Card>
        </div>
      ) : (
        <div>
          {loading ? <div style={{ color: C.txD, textAlign: 'center', padding: 40 }}>Loading...</div> :
            tickets.length === 0 ? (
              <Card>
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>🎧</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.tx, marginBottom: 8 }}>No support tickets yet</div>
                  <div style={{ fontSize: 13, color: C.txM, marginBottom: 20 }}>Submit a ticket and our team will respond within 24 hours.</div>
                  <Btn onClick={() => setShowNewModal(true)}>Submit Your First Ticket</Btn>
                </div>
              </Card>
            ) : tickets.map(t => (
              <div key={t.id} onClick={async () => { setSelectedTicket(t); await loadReplies(t.id); }}
                style={{ background: C.s1, border: '1px solid ' + C.b1, borderRadius: 12, padding: '16px 20px', marginBottom: 10, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.tx, marginBottom: 4 }}>{t.subject}</div>
                    <div style={{ fontSize: 12, color: C.txD }}>{fmtDate(t.created_at?.split('T')[0])}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {t.priority === 'urgent' && <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: C.errDim, color: C.err }}>URGENT</span>}
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: statusBg(t.status), color: statusColor(t.status) }}>{t.status.replace('_', ' ')}</span>
                    <span style={{ fontSize: 12, color: C.pri, fontWeight: 600 }}>View →</span>
                  </div>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {showNewModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: C.s1, borderRadius: 16, padding: 32, width: '100%', maxWidth: 500, border: '1px solid ' + C.b1 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: C.tx, margin: '0 0 20px' }}>New Support Ticket</h2>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.txM, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief description of your issue"
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid ' + C.b1, fontSize: 14, color: C.tx, background: C.s2, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.txM, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid ' + C.b1, fontSize: 14, color: C.tx, background: C.s2, outline: 'none', fontFamily: 'inherit' }}>
                <option value="normal">Normal — General question or minor issue</option>
                <option value="high">High — Affecting daily workflow</option>
                <option value="urgent">Urgent — Critical issue</option>
              </select>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.txM, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Describe your issue</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Please provide as much detail as possible..." rows={4}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid ' + C.b1, fontSize: 14, color: C.tx, background: C.s2, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowNewModal(false)} style={{ background: 'none', border: '1px solid ' + C.b1, borderRadius: 8, padding: '10px 18px', fontSize: 13, cursor: 'pointer', color: C.txM }}>Cancel</button>
              <Btn onClick={submitTicket} disabled={submitting || !subject || !message}>{submitting ? 'Submitting...' : 'Submit Ticket'}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReferralView({ user, clinic }) {
  const refCode = genReferralCode(user?.id);
  const refLink = `${window.location.origin}?ref=${refCode}`;
  const [copied, setCopied] = useState(false);
  const referrals = DB.get('referrals_' + user?.id) || [];

  const copyLink = () => {
    navigator.clipboard.writeText(refLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const totalEarned = referrals.filter(r => r.paid).length * 50;
  const pending = referrals.filter(r => !r.paid).length;

  return (
    <div className="ihq-anim ihq-mobile-pad" style={{ padding: 32, maxWidth: 860 }}>
      <PageHeader title="Refer & Earn" subtitle="Earn $50 for every clinic that subscribes through your link" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Hero */}
        <div style={{ background: `linear-gradient(135deg, ${C.priDim}, rgba(139,92,246,0.1))`, border: `1px solid ${C.pri}30`, borderRadius: 16, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 26, fontWeight: 800, color: C.tx, marginBottom: 8 }}>Earn $50 per referral</h2>
          <p style={{ fontSize: 15, color: C.txM, marginBottom: 24, maxWidth: 480, margin: '0 auto 24px' }}>
            Share your link with IV clinic owners. When they subscribe to any paid plan, you get <strong style={{ color: C.ok }}>$50 cash</strong>. Your referrals get <strong style={{ color: C.pri }}>30 days free</strong> instead of 14.
          </p>
          <div style={{ display: 'flex', gap: 10, maxWidth: 560, margin: '0 auto', alignItems: 'center' }}>
            <div style={{ flex: 1, padding: '12px 16px', background: C.s3, border: `1px solid ${C.b1}`, borderRadius: 9, fontSize: 14, color: C.tx, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {refLink}
            </div>
            <Btn onClick={copyLink} variant={copied ? 'success' : 'primary'}>
              {copied ? '✓ Copied!' : 'Copy Link'}
            </Btn>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <StatCard label="Total Earned" value={`$${totalEarned}`} sub="Paid out to you" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>} color={C.ok} />
          <StatCard label="Referrals Sent" value={referrals.length} sub="Clinics signed up" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/></svg>} color={C.pri} />
          <StatCard label="Pending Payout" value={`$${pending * 50}`} sub={`${pending} awaiting payment`} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>} color={C.acc} />
        </div>

        {/* How it works */}
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 18 }}>How it works</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
            {[
              { step: '1', icon: '🔗', title: 'Share your link', desc: 'Copy your unique referral link and share with IV clinic owners' },
              { step: '2', icon: '📝', title: 'They sign up', desc: 'They register using your link and get 30 days free instead of 14' },
              { step: '3', icon: '💳', title: 'They subscribe', desc: 'When they activate a paid plan, your credit is triggered' },
              { step: '4', icon: '💸', title: 'You get paid', desc: '$50 per conversion, paid monthly via PayPal or bank transfer' },
            ].map(item => (
              <div key={item.step} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>{item.icon}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.tx, marginBottom: 6 }}>{item.title}</div>
                <div style={{ fontSize: 13, color: C.txM, lineHeight: 1.5 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Share options */}
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 16 }}>Ready-to-send messages</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { label: 'Facebook Group Post', msg: `Hey everyone — if you're still using Mangomint or Pabau for your IV clinic, I switched to IVPilot and it's been a game changer. Formula builder, digital waivers, adverse reaction logs — all included, no add-on fees. Try it free for 30 days: ${refLink}` },
              { label: 'Instagram DM', msg: `Hey! I run an IV hydration clinic and just found this software built specifically for IV clinics (not adapted from salon software like Mangomint). Digital waivers, formula builder, everything included for $149/mo. You get 30 days free with my link: ${refLink}` },
              { label: 'Short Text', msg: `Switched my IV clinic to IVPilot — way better than Mangomint. 30 days free: ${refLink}` },
            ].map(item => (
              <div key={item.label} style={{ padding: 16, background: C.s3, borderRadius: 10, border: `1px solid ${C.b1}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.txM }}>{item.label}</span>
                  <Btn variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(item.msg); }}>Copy</Btn>
                </div>
                <p style={{ fontSize: 13, color: C.tx, lineHeight: 1.6 }}>{item.msg}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Referral history */}
        {referrals.length > 0 && (
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 16 }}>Referral History</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
                  {['Clinic', 'Signed Up', 'Status', 'Payout'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.txD, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {referrals.map((r, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.b1}20` }}>
                    <td style={{ padding: '12px 12px', fontSize: 13, color: C.tx }}>{r.clinicName}</td>
                    <td style={{ padding: '12px 12px', fontSize: 13, color: C.txM }}>{fmtDate(r.createdAt)}</td>
                    <td style={{ padding: '12px 12px' }}><Badge color={r.paid ? C.ok : C.acc} dim={r.paid ? C.okDim : C.accDim}>{r.paid ? 'Paid' : 'Pending'}</Badge></td>
                    <td style={{ padding: '12px 12px', fontSize: 13, fontWeight: 700, color: r.paid ? C.ok : C.txM }}>{r.paid ? '$50.00' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        <div style={{ padding: '14px 18px', background: C.s3, borderRadius: 10, fontSize: 13, color: C.txM, border: `1px solid ${C.b1}` }}>
          💡 <strong style={{ color: C.tx }}>Payout info:</strong> Credits are processed on the 1st of each month for all conversions from the previous month. Email <a href="mailto:hello@ivpilot.com" style={{ color: C.pri }}>hello@ivpilot.com</a> to set up your payment method.
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// IMPORT VIEW
// ═══════════════════════════════════════════════════════
function ImportView({ clinicId, onImport }) {
  const [source, setSource] = useState('mangomint');
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [parsed, setParsed] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const fileRef = useRef(null);

  const MANGOMINT_MAP = { 'First Name': 'firstName', 'Last Name': 'lastName', 'Email': 'email', 'Phone': 'phone', 'Date of Birth': 'dob', 'Notes': 'conditions', 'Allergies': 'allergies' };
  const PABAU_MAP = { 'Firstname': 'firstName', 'First name': 'firstName', 'Lastname': 'lastName', 'Last name': 'lastName', 'Email': 'email', 'Mobile': 'phone', 'Phone': 'phone', 'DOB': 'dob', 'Date of birth': 'dob', 'Medical history': 'conditions', 'Allergies': 'allergies', 'Notes': 'conditions' };
  const GENERIC_MAP = { 'first_name': 'firstName', 'last_name': 'lastName', 'firstname': 'firstName', 'lastname': 'lastName', 'email': 'email', 'phone': 'phone', 'mobile': 'phone', 'dob': 'dob', 'date_of_birth': 'dob', 'allergies': 'allergies', 'notes': 'conditions', 'conditions': 'conditions' };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return { rows: [], headers: [] };
    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
    const rows = lines.slice(1).map(line => {
      const vals = [];
      let cur = '', inQ = false;
      for (let c of line) { if (c === '"') { inQ = !inQ; } else if (c === ',' && !inQ) { vals.push(cur.trim()); cur = ''; } else { cur += c; } }
      vals.push(cur.trim());
      return Object.fromEntries(headers.map((h, i) => [h, vals[i] || '']));
    });
    return { rows, headers };
  };

  const mapRow = (row, map) => {
    const result = { clinicId, createdAt: new Date().toISOString() };
    for (const [csvKey, ourKey] of Object.entries(map)) {
      if (row[csvKey] && row[csvKey].trim()) {
        result[ourKey] = row[csvKey].trim();
      }
    }
    return result;
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { rows, headers } = parseCSV(ev.target.result);
      const map = source === 'mangomint' ? MANGOMINT_MAP : source === 'pabau' ? PABAU_MAP : GENERIC_MAP;
      const errs = [];
      const mapped = rows.map((r, i) => {
        const m = mapRow(r, map);
        if (!m.firstName && !m.lastName && !m.email) errs.push(`Row ${i + 2}: no name or email found`);
        return m;
      }).filter(m => m.firstName || m.lastName || m.email);
      setParsed(mapped);
      setErrors(errs);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    setImporting(true);
    setTimeout(() => {
      onImport(parsed);
      setImportedCount(parsed.length);
      setImporting(false);
      setStep('done');
    }, 800);
  };

  const reset = () => { setStep('upload'); setParsed([]); setErrors([]); setImportedCount(0); if (fileRef.current) fileRef.current.value = ''; };

  const instructions = {
    mangomint: { steps: ['Log into Mangomint', 'Go to Reports → Clients', 'Click "Export" → Download CSV', 'Upload the CSV file below'], note: 'Mangomint exports: First Name, Last Name, Email, Phone, Date of Birth, Notes, Allergies' },
    pabau: { steps: ['Log into Pabau', 'Go to Clients → All Clients', 'Click "Export" in the top right', 'Choose CSV format and download', 'Upload the CSV file below'], note: 'Pabau exports: Firstname, Lastname, Email, Mobile, DOB, Medical history, Allergies' },
    generic: { steps: ['Export your clients from any software as CSV', 'Ensure the CSV has columns for name, email, phone', 'Upload the CSV file below'], note: 'Supported columns: first_name, last_name, email, phone, dob, allergies, notes/conditions' },
  };

  const inst = instructions[source];

  return (
    <div className="ihq-anim ihq-mobile-pad" style={{ padding: 32, maxWidth: 820 }}>
      <PageHeader title="Import Clients" subtitle="Migrate from Mangomint, Pabau, or any CSV export" />

      {step === 'upload' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Source selector */}
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 16 }}>Where are you importing from?</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {[{ id: 'mangomint', label: 'Mangomint', icon: '💅' }, { id: 'pabau', label: 'Pabau', icon: '🏥' }, { id: 'generic', label: 'Other / Generic CSV', icon: '📄' }].map(s => (
                <button key={s.id} onClick={() => setSource(s.id)}
                  style={{ padding: '16px', borderRadius: 10, border: `2px solid ${source === s.id ? C.pri : C.b1}`, background: source === s.id ? C.priDim : C.s3, cursor: 'pointer', transition: 'all 0.15s' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: source === s.id ? C.pri : C.tx, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.label}</div>
                </button>
              ))}
            </div>
          </Card>

          {/* Instructions */}
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 14 }}>
              How to export from {source === 'mangomint' ? 'Mangomint' : source === 'pabau' ? 'Pabau' : 'your software'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              {inst.steps.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: C.priDim, border: `1px solid ${C.pri}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: C.pri, flexShrink: 0 }}>{i + 1}</div>
                  <span style={{ fontSize: 14, color: C.tx }}>{s}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: '10px 14px', background: C.s3, borderRadius: 8, fontSize: 12, color: C.txM }}>
              💡 {inst.note}
            </div>
          </Card>

          {/* Upload area */}
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 14 }}>Upload your CSV file</h3>
            <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', border: `2px dashed ${C.b2}`, borderRadius: 12, cursor: 'pointer', background: C.s3, transition: 'all 0.15s' }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { const dt = new DataTransfer(); dt.items.add(f); fileRef.current.files = dt.files; handleFile({ target: fileRef.current }); } }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📂</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.tx, marginBottom: 6 }}>Drop CSV here or click to browse</div>
              <div style={{ fontSize: 13, color: C.txM }}>Supports .csv files exported from {source === 'mangomint' ? 'Mangomint' : source === 'pabau' ? 'Pabau' : 'any platform'}</div>
              <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} />
            </label>
          </Card>
        </div>
      )}

      {step === 'preview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>Preview — {parsed.length} clients ready to import</h3>
                {errors.length > 0 && <div style={{ fontSize: 13, color: C.acc, marginTop: 4 }}>⚠ {errors.length} rows skipped (missing name/email)</div>}
              </div>
              <Btn variant="secondary" size="sm" onClick={reset}>Start Over</Btn>
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: C.s3, borderBottom: `1px solid ${C.b1}` }}>
                    {['Name', 'Email', 'Phone', 'DOB', 'Allergies'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.txD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsed.slice(0, 50).map((c, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.b1}20` }}>
                      <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 500, color: C.tx }}>{[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: C.txM }}>{c.email || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: C.txM }}>{c.phone || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: C.txM }}>{c.dob || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: C.txM }}>{c.allergies || '—'}</td>
                    </tr>
                  ))}
                  {parsed.length > 50 && <tr><td colSpan={5} style={{ padding: '10px 14px', fontSize: 13, color: C.txD, textAlign: 'center' }}>...and {parsed.length - 50} more</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={reset}>Cancel</Btn>
            <Btn variant="success" onClick={handleImport} disabled={importing || parsed.length === 0}>
              {importing ? 'Importing...' : `Import ${parsed.length} Clients`}
            </Btn>
          </div>
        </div>
      )}

      {step === 'done' && (
        <Card style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
          <h2 style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 10 }}>Import Complete!</h2>
          <p style={{ fontSize: 16, color: C.txM, marginBottom: 28 }}><strong style={{ color: C.ok }}>{importedCount} clients</strong> have been imported to IVPilot.</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <Btn onClick={reset}>Import More</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// CLIENT CHAT VIEW (public — no login required)
// ═══════════════════════════════════════════════════════
function ClientChatView({ clinicId, clientId, clientName, clinicName }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    const msgs = await sbGetMessages(clinicId, clientId);
    setMessages(msgs);
    await sbMarkRead(clinicId, clientId, 'clinic');
  }, [clinicId, clientId]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 3000);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const t = text.trim();
    setText('');
    try {
      await sbSendMessage({ clinicId, clientId, sender: 'client', senderName: clientName || 'Client', text: t, sentAt: new Date().toISOString() });
      await refresh();
    } catch (e) { console.error('Send error:', e); }
    setSending(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ background: C.s1, borderBottom: `1px solid ${C.b1}`, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="22" height="22" viewBox="0 0 38 38" fill="none"><rect x="11" y="3" width="16" height="13" rx="4" fill="white" opacity="0.95"/><rect x="13" y="5" width="5" height="3" rx="1.5" fill="white" opacity="0.4"/><line x1="19" y1="16" x2="19" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/><rect x="15.5" y="22" width="7" height="5" rx="2" fill="white" opacity="0.8"/><line x1="19" y1="27" x2="19" y2="33" stroke="white" strokeWidth="2" strokeLinecap="round"/><path d="M16 33 Q19 36 22 33" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/><ellipse cx="19" cy="19.5" rx="1.2" ry="1.5" fill="#0ea88a"/></svg>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>{clinicName || 'Your IV Clinic'}</div>
          <div style={{ fontSize: 12, color: C.ok, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.ok, display: 'inline-block' }} />
            Online · Typically replies within an hour
          </div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: C.txD }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.txM, marginBottom: 6 }}>Start a conversation</div>
            <div style={{ fontSize: 13, color: C.txD }}>Send a message to {clinicName || 'your clinic'} — no phone number needed</div>
          </div>
        )}
        {messages.map(msg => {
          const isClient = msg.sender === 'client';
          return (
            <div key={msg.id} style={{ display: 'flex', justifyContent: isClient ? 'flex-end' : 'flex-start', gap: 8 }}>
              {!isClient && (
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: C.priDim, border: `1px solid ${C.pri}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-end' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.pri} strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/></svg>
                </div>
              )}
              <div style={{ maxWidth: '75%' }}>
                <div style={{ padding: '10px 14px', borderRadius: isClient ? '18px 18px 4px 18px' : '18px 18px 18px 4px', background: isClient ? C.pri : C.s2, color: isClient ? C.white : C.tx, fontSize: 14, lineHeight: 1.5, border: isClient ? 'none' : `1px solid ${C.b1}` }}>
                  {msg.text}
                </div>
                <div style={{ fontSize: 11, color: C.txD, marginTop: 4, textAlign: isClient ? 'right' : 'left' }}>
                  {fmtTime(msg.sentAt)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ background: C.s1, borderTop: `1px solid ${C.b1}`, padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        <textarea ref={inputRef} value={text} onChange={e => setText(e.target.value)} onKeyDown={handleKey}
          placeholder="Type a message..." rows={1}
          style={{ flex: 1, background: C.s3, border: `1px solid ${C.b1}`, borderRadius: 12, padding: '10px 14px', fontSize: 14, color: C.tx, fontFamily: "'DM Sans', sans-serif", resize: 'none', maxHeight: 120, lineHeight: 1.5, outline: 'none' }} />
        <button onClick={send} disabled={!text.trim() || sending}
          style={{ width: 44, height: 44, borderRadius: 12, background: text.trim() ? C.pri : C.s3, border: 'none', cursor: text.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={text.trim() ? C.white : C.txD} strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
      </div>
      <div style={{ background: C.s1, paddingBottom: 8, textAlign: 'center' }}>
        <span style={{ fontSize: 11, color: C.txD }}>Powered by IVPilot · Your messages are private</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// INBOX VIEW (clinic side)
// ═══════════════════════════════════════════════════════
function InboxView({ clients, clinicId, clinic }) {
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState('');
  const [conversations, setConversations] = useState({});
  const [text, setText] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const refreshConversations = useCallback(async () => {
    const convos = {};
    await Promise.all(clients.map(async c => {
      const msgs = await sbGetMessages(clinicId, c.id);
      if (msgs.length > 0) convos[c.id] = msgs;
    }));
    setConversations({ ...convos });
  }, [clients, clinicId]);

  useEffect(() => {
    refreshConversations();
    const interval = setInterval(refreshConversations, 5000);
    return () => clearInterval(interval);
  }, [refreshConversations]);

  useEffect(() => {
    if (selectedClient) {
      sbMarkRead(clinicId, selectedClient.id, 'client');
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [selectedClient?.id, conversations]);

  const send = async () => {
    if (!text.trim() || !selectedClient) return;
    const t = text.trim();
    setText('');
    try {
      await sbSendMessage({ clinicId, clientId: selectedClient.id, sender: 'clinic', senderName: clinic?.name || 'Clinic', text: t, sentAt: new Date().toISOString() });
      await refreshConversations();
      setTimeout(() => { inputRef.current?.focus(); bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 50);
    } catch (e) { console.error('Send error:', e); }
  };

  const handleKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const chatLink = (client) => `${window.location.origin}/?chat=1&c=${clinicId}&p=${client.id}&n=${encodeURIComponent(client.firstName + ' ' + client.lastName)}&cl=${encodeURIComponent(clinic?.name || 'Your IV Clinic')}`;

  const filteredClients = clients.filter(c => `${c.firstName} ${c.lastName}`.toLowerCase().includes(search.toLowerCase()));

  const getLastMsg = (clientId) => {
    const msgs = conversations[clientId] || [];
    return msgs[msgs.length - 1];
  };

  const getUnread = (clientId) => {
    const msgs = conversations[clientId] || [];
    return msgs.filter(m => m.sender === 'client' && !m.read).length;
  };

  const selectedMsgs = selectedClient ? (conversations[selectedClient.id] || []) : [];

  return (
    <div className="ihq-anim" style={{ display: 'flex', height: 'calc(100vh - 0px)', background: C.bg }}>
      {/* Sidebar — client list */}
      <div style={{ width: 300, borderRight: `1px solid ${C.b1}`, display: 'flex', flexDirection: 'column', background: C.s1, flexShrink: 0 }}>
        <div style={{ padding: '20px 16px 12px', borderBottom: `1px solid ${C.b1}` }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 12 }}>Inbox</h2>
          <div style={{ position: 'relative' }}>
            <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.txM }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." style={{ width: '100%', background: C.s3, border: `1px solid ${C.b1}`, borderRadius: 8, padding: '8px 12px 8px 30px', fontSize: 13, color: C.tx, fontFamily: "'DM Sans', sans-serif", outline: 'none' }} />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filteredClients.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: C.txD, fontSize: 13 }}>No clients found</div>
          ) : filteredClients.map(c => {
            const lastMsg = getLastMsg(c.id);
            const unread = getUnread(c.id);
            const isSelected = selectedClient?.id === c.id;
            const name = `${c.firstName} ${c.lastName}`;
            return (
              <button key={c.id} onClick={() => setSelectedClient(c)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', width: '100%', background: isSelected ? C.priDim : 'transparent', border: 'none', borderBottom: `1px solid ${C.b1}20`, cursor: 'pointer', textAlign: 'left', transition: 'all 0.12s', borderLeft: `3px solid ${isSelected ? C.pri : 'transparent'}` }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar name={name} size={40} />
                  {unread > 0 && <span style={{ position: 'absolute', top: -2, right: -2, width: 16, height: 16, background: C.err, borderRadius: '50%', fontSize: 9, fontWeight: 800, color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${C.s1}` }}>{unread}</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: unread > 0 ? 700 : 500, color: C.tx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                    {lastMsg && <span style={{ fontSize: 11, color: C.txD, flexShrink: 0, marginLeft: 6 }}>{fmtTime(lastMsg.sentAt)}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: unread > 0 ? C.txM : C.txD, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {lastMsg ? `${lastMsg.sender === 'clinic' ? 'You: ' : ''}${lastMsg.text}` : 'No messages yet — share chat link'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chat area */}
      {selectedClient ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Chat header */}
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.b1}`, background: C.s1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar name={`${selectedClient.firstName} ${selectedClient.lastName}`} size={38} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.tx }}>{selectedClient.firstName} {selectedClient.lastName}</div>
                <div style={{ fontSize: 12, color: C.txM }}>{selectedClient.email || selectedClient.phone || 'No contact info'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Btn variant="secondary" size="sm" onClick={() => { navigator.clipboard.writeText(chatLink(selectedClient)); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                Copy Chat Link
              </Btn>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {selectedMsgs.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 48 }}>💬</div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: C.tx, marginBottom: 6 }}>No messages yet</div>
                  <div style={{ fontSize: 13, color: C.txM, marginBottom: 20, maxWidth: 320 }}>Share the chat link with {selectedClient.firstName} so they can message you without sharing phone numbers.</div>
                </div>
                <div style={{ padding: '12px 16px', background: C.s3, border: `1px solid ${C.b1}`, borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10, maxWidth: 400, width: '100%' }}>
                  <div style={{ fontSize: 12, color: C.txM, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chatLink(selectedClient)}</div>
                  <Btn size="sm" onClick={() => navigator.clipboard.writeText(chatLink(selectedClient))}>Copy</Btn>
                </div>
              </div>
            ) : selectedMsgs.map(msg => {
              const isClinic = msg.sender === 'clinic';
              return (
                <div key={msg.id} style={{ display: 'flex', justifyContent: isClinic ? 'flex-end' : 'flex-start', gap: 8 }}>
                  {!isClinic && <Avatar name={`${selectedClient.firstName} ${selectedClient.lastName}`} size={30} />}
                  <div style={{ maxWidth: '70%' }}>
                    <div style={{ padding: '10px 14px', borderRadius: isClinic ? '18px 18px 4px 18px' : '18px 18px 18px 4px', background: isClinic ? C.pri : C.s2, color: isClinic ? C.white : C.tx, fontSize: 14, lineHeight: 1.5, border: isClinic ? 'none' : `1px solid ${C.b1}` }}>
                      {msg.text}
                    </div>
                    <div style={{ fontSize: 11, color: C.txD, marginTop: 4, textAlign: isClinic ? 'right' : 'left' }}>{fmtTime(msg.sentAt)}</div>
                  </div>
                  {isClinic && (
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: C.priDim, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-end' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.pri} strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/></svg>
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.b1}`, background: C.s1, display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <textarea ref={inputRef} value={text} onChange={e => setText(e.target.value)} onKeyDown={handleKey}
              placeholder={`Message ${selectedClient.firstName}...`} rows={1}
              style={{ flex: 1, background: C.s3, border: `1px solid ${C.b1}`, borderRadius: 12, padding: '10px 14px', fontSize: 14, color: C.tx, fontFamily: "'DM Sans', sans-serif", resize: 'none', maxHeight: 120, lineHeight: 1.5, outline: 'none' }} />
            <button onClick={send} disabled={!text.trim()}
              style={{ width: 44, height: 44, borderRadius: 12, background: text.trim() ? C.pri : C.s3, border: 'none', cursor: text.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={text.trim() ? C.white : C.txD} strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: C.txM }}>
          <div style={{ fontSize: 56 }}>💬</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx, marginBottom: 6 }}>Select a client to start chatting</div>
            <div style={{ fontSize: 14, color: C.txM }}>Share the chat link with clients — no phone numbers needed</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// LANDING PAGE
// ═══════════════════════════════════════════════════════
function LandingPage({ onSignIn, onGetStarted, onDashboard, auth }) {
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('ivp_theme') || 'dark');
  const toggleTheme = () => { const n = theme === 'dark' ? 'light' : 'dark'; setTheme(n); localStorage.setItem('ivp_theme', n); };
  const isLight = theme === 'light';
  const T = isLight ? { bg: '#f8fafc', s1: '#ffffff', s2: '#f1f5f9', s3: '#e2e8f0', b1: '#cbd5e1', b2: '#94a3b8', tx: '#0f172a', txM: '#475569', txD: '#94a3b8', nav: 'rgba(255,255,255,0.97)' } : { bg: C.bg, s1: C.s1, s2: C.s2, s3: C.s3, b1: C.b1, b2: C.b2, tx: C.tx, txM: C.txM, txD: C.txD, nav: 'rgba(6,12,24,0.97)' };

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const isMobile = useIsMobile();

  const NAV_LINKS = ['Features', 'How It Works', 'Pricing', 'Testimonials'];

  const FEATURES = [
    { icon: '💉', title: 'IV Formula Builder', desc: '8 pre-loaded clinical templates or build custom protocols with exact dosages, components, and pricing.', badge: 'All Plans' },
    { icon: '📋', title: 'Digital Intake & Waivers', desc: 'Medical intake forms with canvas e-signatures. Timestamped, immutable consent records for full compliance.', badge: 'All Plans' },
    { icon: '⚠️', title: 'Adverse Reaction Log', desc: 'Log reactions in real-time during sessions. Full history attached to client profiles for safety audits.', badge: 'All Plans' },
    { icon: '📅', title: 'Appointment Scheduling', desc: 'Weekly calendar view, instant booking, automated reminders. Assign nurses, track formulas.', badge: 'All Plans' },
    { icon: '❤️', title: 'Pre-Treatment Vitals', desc: 'Record BP, heart rate, O2 saturation, and weight before every session. Stored permanently in the client record.', badge: 'All Plans' },
    { icon: '💬', title: 'Client Inbox', desc: 'In-app messaging between clinic and client. No phone numbers shared. Chat links sent automatically with every appointment.', badge: 'All Plans' },
    { icon: '👩‍⚕️', title: 'Staff Management', desc: 'Add nurses, doctors, and front desk. Role-based access — clinical staff never see revenue. Invite via email.', badge: 'All Plans' },
    { icon: '📊', title: 'Revenue Dashboard', desc: 'Weekly, monthly, and yearly breakdowns by formula, nurse, and client. All the numbers you need to grow.', badge: 'All Plans' },
  ];

  const PLANS = [
    { name: 'Starter', price: 149, desc: 'For single-location IV clinics just getting started.', features: ['Unlimited clients', 'Unlimited appointments', 'Digital intake & waivers', 'Formula builder', 'Staff management', 'Client inbox', 'Email & SMS reminders', 'Revenue dashboard'], highlight: false, priceId: 'price_1THy84B0WpRalfPpx2nzbyGm' },
    { name: 'Growth', price: 299, desc: 'For growing clinics with multiple nurses and high volume.', features: ['Everything in Starter', 'Adverse reaction tracking', 'Pre-treatment vitals', 'Advanced analytics', 'Priority support', 'Custom drip protocols', 'Referral program', 'CSV import from Mangomint/Pabau'], highlight: true, priceId: 'price_1THy98B0WpRalfPpHrX6u80u' },
    { name: 'Multi-Location', price: 549, desc: 'For clinics with multiple locations under one account.', features: ['Everything in Growth', 'Multiple locations', 'Location-level reporting', 'Dedicated onboarding', 'API access', 'Custom branding'], highlight: false, priceId: 'price_1THy9xB0WpRalfPpcbPs4rZu' },
  ];

  const TESTIMONIALS = [
    { quote: 'We were paying $420/mo for Mangomint — which was built for hair salons. IVPilot was purpose-built for us. The formula builder alone saves us hours every week.', name: 'Dr. Jessica M.', title: 'Medical Director · Miami, FL', initials: 'JM' },
    { quote: 'The digital waivers and adverse reaction logs give me complete peace of mind. When a client asks for their records, everything is right there. This is what HIPAA compliance actually looks like.', name: 'Sarah K., RN', title: 'Owner · Drip Lounge · Austin, TX', initials: 'SK' },
    { quote: 'I was on Pabau before — constant bugs, no IV-specific features. Switched to IVPilot and onboarded my whole team in one afternoon. The role-based access for nurses is exactly what I needed.', name: 'Marcus T.', title: 'CEO · Elevate IV · Chicago, IL', initials: 'MT' },
  ];

  const scrollTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "'Inter', 'DM Sans', sans-serif", color: T.tx, overflowX: 'hidden' }}>

      {/* ── NAV ── */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, padding: '0 32px', transition: 'all 0.3s', background: scrolled ? T.nav : 'transparent', backdropFilter: scrolled ? 'blur(16px)' : 'none', borderBottom: scrolled ? `1px solid ${T.b1}` : '1px solid transparent' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 66 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: `linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 38 38" fill="none"><rect x="11" y="3" width="16" height="13" rx="4" fill="white" opacity="0.95"/><rect x="13" y="5" width="5" height="3" rx="1.5" fill="white" opacity="0.4"/><line x1="19" y1="16" x2="19" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/><rect x="15.5" y="22" width="7" height="5" rx="2" fill="white" opacity="0.8"/><line x1="19" y1="27" x2="19" y2="33" stroke="white" strokeWidth="2" strokeLinecap="round"/><path d="M16 33 Q19 36 22 33" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/><ellipse cx="19" cy="19.5" rx="1.2" ry="1.5" fill="#0ea88a"/></svg>
            </div>
            <span style={{ fontSize: 19, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.tx }}>IVPilot</span>
          </div>
          {!isMobile && (
            <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
              {NAV_LINKS.map(l => <button key={l} onClick={() => scrollTo(l.toLowerCase().replace(' ', '-'))} style={{ background: 'none', border: 'none', fontSize: 14, color: T.txM, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 500, transition: 'color 0.15s' }}>{l}</button>)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={toggleTheme} style={{ width: 34, height: 34, borderRadius: 8, background: T.s3, border: `1px solid ${T.b1}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{isLight ? '🌙' : '☀️'}</button>
            {auth ? (
              <button onClick={onDashboard} style={{ padding: '8px 18px', borderRadius: 9, background: C.pri, color: C.white, border: 'none', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Go to Dashboard →</button>
            ) : (
              <>
                <button onClick={onSignIn} style={{ padding: '8px 16px', borderRadius: 9, background: 'none', color: T.txM, border: `1px solid ${T.b1}`, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 14, cursor: 'pointer' }}>Sign In</button>
                <button onClick={onGetStarted} style={{ padding: '8px 18px', borderRadius: 9, background: C.pri, color: C.white, border: 'none', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Start Free Trial</button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{ paddingTop: isMobile ? 100 : 140, paddingBottom: isMobile ? 60 : 100, paddingLeft: isMobile ? 20 : 80, paddingRight: isMobile ? 20 : 80, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        {/* Background glow */}
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 600, height: 400, background: `radial-gradient(ellipse, ${C.pri}18 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <div style={{ maxWidth: 560, margin: '0 auto', position: 'relative' }}>
          {/* Badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 14px', borderRadius: 100, border: `1px solid ${C.pri}40`, background: C.priDim, marginBottom: 28 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.pri, display: 'inline-block' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: C.pri }}>Purpose-built for IV hydration clinics</span>
          </div>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? 30 : 44, fontWeight: 800, lineHeight: 1.1, letterSpacing: -1.5, color: T.tx, marginBottom: 24 }}>
            Software built for<br /><em style={{ color: C.pri, fontStyle: 'italic' }}>IV clinics,</em><br />not adapted from a <span style={{ color: C.acc }}>hair salon app</span>
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 18, color: T.txM, maxWidth: 540, margin: '0 auto 36px', lineHeight: 1.7 }}>
            Mangomint was built for nail salons. Pabau for GP clinics in the UK. IVPilot is the first software designed exclusively for IV hydration therapy — formula builder, adverse reaction logs, and compliance tools included from day one.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
            <button onClick={onGetStarted} style={{ padding: '14px 32px', borderRadius: 11, background: C.pri, color: C.white, border: 'none', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 16, cursor: 'pointer', boxShadow: `0 4px 20px ${C.pri}40` }}>Start Free Trial — 14 Days</button>
            <button onClick={onSignIn} style={{ padding: '14px 28px', borderRadius: 11, background: T.s2, color: T.tx, border: `1px solid ${T.b1}`, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>Sign In →</button>
          </div>
          <p style={{ fontSize: 13, color: T.txD }}>No credit card required · Cancel anytime · Switch from Mangomint in minutes</p>
        </div>
        {/* Trust strip */}
        <div style={{ maxWidth: 700, margin: '52px auto 0', paddingTop: 32, borderTop: `1px solid ${T.b1}` }}>
          <p style={{ fontSize: 12, color: T.txD, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Trusted by clinics offering</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {['Myers Cocktail', 'NAD+', 'Hangover Relief', 'Beauty Glow', 'Immunity Boost', 'Athletic Recovery'].map(t => (
              <span key={t} style={{ padding: '5px 14px', borderRadius: 100, background: T.s2, border: `1px solid ${T.b1}`, fontSize: 13, color: T.txM, fontWeight: 500 }}>💉 {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={{ padding: isMobile ? '40px 20px' : '72px 80px', background: T.s1 }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ display: 'inline-block', padding: '5px 14px', borderRadius: 100, background: C.priDim, border: `1px solid ${C.pri}30`, fontSize: 12, fontWeight: 700, color: C.pri, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>BUILT FOR IV CLINICS</div>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? 22 : 30, fontWeight: 800, color: T.tx, lineHeight: 1.2, marginBottom: 14 }}>Every feature your clinic needs.<br /><em style={{ color: C.pri }}>Zero add-on fees. Ever.</em></h2>
            <p style={{ fontSize: 16, color: T.txM, maxWidth: 500, margin: '0 auto' }}>Mangomint charges separately for forms, texting, and payroll. We include everything in one flat price.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 16 }}>
            {FEATURES.map(f => (
              <div key={f.title} style={{ background: T.bg, border: `1px solid ${T.b1}`, borderRadius: 14, padding: 24, transition: 'all 0.2s' }}>
                <div style={{ width: 46, height: 46, borderRadius: 12, background: T.s2, border: `1px solid ${T.b1}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 14 }}>{f.icon}</div>
                <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.tx, marginBottom: 8 }}>{f.title}</div>
                <div style={{ fontSize: 13, color: T.txM, lineHeight: 1.65, marginBottom: 12 }}>{f.desc}</div>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.ok, background: C.okDim, padding: '3px 9px', borderRadius: 100 }}>{f.badge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" style={{ padding: isMobile ? '40px 20px' : '72px 80px', background: T.bg }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ display: 'inline-block', padding: '5px 14px', borderRadius: 100, background: C.accDim || C.s2, border: `1px solid ${C.acc}30`, fontSize: 12, fontWeight: 700, color: C.acc, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>HOW IT WORKS</div>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? 22 : 30, fontWeight: 800, color: T.tx, lineHeight: 1.2 }}>From booking to billing<br /><em style={{ color: C.acc }}>in one platform.</em></h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 20 }}>
            {[
              { step: '01', title: 'Client books appointment', desc: 'Client receives branded confirmation email with intake form link and chat link. No phone numbers shared.', color: C.pri },
              { step: '02', title: 'Nurse records the visit', desc: 'Pre-treatment vitals, drip formula selected, adverse reactions logged — all stored permanently in the client profile.', color: C.acc },
              { step: '03', title: 'Owner sees the revenue', desc: 'Real-time dashboard shows revenue by formula, nurse, and time period. Export any report as CSV.', color: C.ok },
            ].map(s => (
              <div key={s.step} style={{ background: T.s1, border: `1px solid ${T.b1}`, borderRadius: 16, padding: 28, position: 'relative', overflow: 'hidden' }}>
                <div style={{ fontSize: 48, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: s.color + '18', position: 'absolute', top: 16, right: 20, lineHeight: 1 }}>{s.step}</div>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: s.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, color: s.color, fontWeight: 800, fontSize: 16, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.step}</div>
                <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.tx, marginBottom: 10 }}>{s.title}</div>
                <div style={{ fontSize: 14, color: T.txM, lineHeight: 1.65 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VS COMPETITORS ── */}
      <section style={{ padding: isMobile ? '40px 20px' : '72px 80px', background: T.s1 }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ display: 'inline-block', padding: '5px 14px', borderRadius: 100, background: C.errDim || C.s2, border: `1px solid ${C.err}30`, fontSize: 12, fontWeight: 700, color: C.err, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>COMPARISON</div>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? 22 : 30, fontWeight: 800, color: T.tx, lineHeight: 1.2 }}>Why clinics are <em style={{ color: C.pri }}>switching to IVPilot</em></h2>
          </div>
          <div style={{ background: T.bg, border: `1px solid ${T.b1}`, borderRadius: 16, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: T.s2 }}>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: 13, color: T.txM, fontWeight: 600 }}>Feature</th>
                  <th style={{ padding: '14px 20px', textAlign: 'center', fontSize: 14, fontWeight: 700, color: C.pri, background: C.priDim }}>IVPilot</th>
                  <th style={{ padding: '14px 20px', textAlign: 'center', fontSize: 13, color: T.txM, fontWeight: 500 }}>Mangomint</th>
                  <th style={{ padding: '14px 20px', textAlign: 'center', fontSize: 13, color: T.txM, fontWeight: 500 }}>Pabau</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['IV Formula Builder', '✓', '✗', '✗'],
                  ['Adverse Reaction Log', '✓', '✗', '✗'],
                  ['Digital Waivers', '✓ Included', '$100/mo add-on', '✓'],
                  ['SMS Reminders', '✓ Included', '$75/mo add-on', '$62/user/mo'],
                  ['Client Chat', '✓ Included', '✗', '✗'],
                  ['Starting Price', '$149/mo flat', '$165–$500+/mo', '$62/user/mo'],
                ].map(([label, ihq, mango, pabau]) => (
                  <tr key={label} style={{ borderTop: `1px solid ${T.b1}20` }}>
                    <td style={{ padding: '13px 20px', fontSize: 14, color: T.tx, fontWeight: 500 }}>{label}</td>
                    <td style={{ padding: '13px 20px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: C.ok, background: C.priDim + '40' }}>{ihq}</td>
                    <td style={{ padding: '13px 20px', textAlign: 'center', fontSize: 13, color: T.txM }}>{mango}</td>
                    <td style={{ padding: '13px 20px', textAlign: 'center', fontSize: 13, color: T.txM }}>{pabau}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" style={{ padding: isMobile ? '40px 20px' : '72px 80px', background: T.bg }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ display: 'inline-block', padding: '5px 14px', borderRadius: 100, background: C.okDim, border: `1px solid ${C.ok}30`, fontSize: 12, fontWeight: 700, color: C.ok, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>PRICING</div>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? 22 : 30, fontWeight: 800, color: T.tx, marginBottom: 12 }}>Start free. Upgrade when ready.</h2>
            <p style={{ fontSize: 16, color: T.txM }}>Every plan includes a 14-day free trial. No charge until your trial ends. Cancel anytime.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 20, alignItems: 'start' }}>
            {PLANS.map(p => (
              <div key={p.name} style={{ background: p.highlight ? `linear-gradient(135deg, ${C.priDim}, ${T.s1})` : T.s1, border: `2px solid ${p.highlight ? C.pri : T.b1}`, borderRadius: 18, padding: '28px 24px', position: 'relative', transform: p.highlight ? 'scale(1.02)' : 'none' }}>
                {p.highlight && <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: C.pri, color: C.white, fontSize: 11, fontWeight: 800, padding: '4px 16px', borderRadius: 100 }}>MOST POPULAR</div>}
                <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: p.highlight ? C.pri : T.tx, marginBottom: 6 }}>{p.name}</div>
                <div style={{ marginBottom: 14 }}>
                  <span style={{ fontSize: 36, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.tx }}>${p.price}</span>
                  <span style={{ fontSize: 14, color: T.txM }}>/mo</span>
                </div>
                <p style={{ fontSize: 13, color: T.txM, marginBottom: 20, lineHeight: 1.6 }}>{p.desc}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 24 }}>
                  {p.features.map(f => (
                    <div key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: T.txM }}>
                      <span style={{ color: C.ok, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>{f}
                    </div>
                  ))}
                </div>
                <button onClick={onGetStarted} style={{ width: '100%', padding: '12px', borderRadius: 10, background: p.highlight ? C.pri : T.s3, color: p.highlight ? C.white : T.tx, border: `1px solid ${p.highlight ? C.pri : T.b1}`, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>Start Free Trial</button>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', fontSize: 13, color: T.txD, marginTop: 24 }}>All plans include every feature. No add-on fees. Cancel inside the app — no emails, no friction.</p>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" style={{ padding: isMobile ? '40px 20px' : '72px 80px', background: T.s1 }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ display: 'inline-block', padding: '5px 14px', borderRadius: 100, background: C.purDim || C.s2, border: `1px solid ${C.pur}30`, fontSize: 12, fontWeight: 700, color: C.pur, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>TESTIMONIALS</div>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? 22 : 30, fontWeight: 800, color: T.tx }}>What clinic owners are saying.</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 20 }}>
            {TESTIMONIALS.map(t => (
              <div key={t.name} style={{ background: T.bg, border: `1px solid ${T.b1}`, borderRadius: 16, padding: 28 }}>
                <div style={{ fontSize: 32, color: C.pri, marginBottom: 12, lineHeight: 1 }}>"</div>
                <p style={{ fontSize: 14, color: T.txM, lineHeight: 1.75, marginBottom: 20, fontStyle: 'italic' }}>{t.quote}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontWeight: 800, fontSize: 14, flexShrink: 0 }}>{t.initials}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: T.txD }}>{t.title}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ padding: isMobile ? '40px 20px' : '72px 80px', background: `linear-gradient(135deg, ${C.pri}18, ${C.pur}18)`, borderTop: `1px solid ${C.pri}20` }}>
        <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <svg width="22" height="22" viewBox="0 0 38 38" fill="none"><rect x="11" y="3" width="16" height="13" rx="4" fill="white" opacity="0.95"/><rect x="13" y="5" width="5" height="3" rx="1.5" fill="white" opacity="0.4"/><line x1="19" y1="16" x2="19" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/><rect x="15.5" y="22" width="7" height="5" rx="2" fill="white" opacity="0.8"/><line x1="19" y1="27" x2="19" y2="33" stroke="white" strokeWidth="2" strokeLinecap="round"/><path d="M16 33 Q19 36 22 33" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/><ellipse cx="19" cy="19.5" rx="1.2" ry="1.5" fill="#0ea88a"/></svg>
          </div>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? 24 : 34, fontWeight: 800, color: T.tx, lineHeight: 1.2, marginBottom: 16 }}>Ready to run your clinic<br /><em style={{ color: C.pri }}>the right way?</em></h2>
          <p style={{ fontSize: 16, color: T.txM, marginBottom: 32, lineHeight: 1.7 }}>Join IV hydration clinics using IVPilot to book more clients, stay compliant, and grow faster — all in one platform built exclusively for IV therapy.</p>
          <button onClick={onGetStarted} style={{ padding: '16px 40px', borderRadius: 12, background: C.pri, color: C.white, border: 'none', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 17, cursor: 'pointer', boxShadow: `0 6px 28px ${C.pri}40` }}>Start Free Trial — 14 Days</button>
          <p style={{ fontSize: 13, color: T.txD, marginTop: 16 }}>Free account · No credit card required · Cancel anytime</p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ padding: isMobile ? '36px 20px' : '48px 40px', background: T.s1, borderTop: `1px solid ${T.b1}` }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr 1fr', gap: 32, marginBottom: 40 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="22" height="22" viewBox="0 0 38 38" fill="none"><rect x="11" y="3" width="16" height="13" rx="4" fill="white" opacity="0.95"/><rect x="13" y="5" width="5" height="3" rx="1.5" fill="white" opacity="0.4"/><line x1="19" y1="16" x2="19" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/><rect x="15.5" y="22" width="7" height="5" rx="2" fill="white" opacity="0.8"/><line x1="19" y1="27" x2="19" y2="33" stroke="white" strokeWidth="2" strokeLinecap="round"/><path d="M16 33 Q19 36 22 33" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/><ellipse cx="19" cy="19.5" rx="1.2" ry="1.5" fill="#0ea88a"/></svg>
                </div>
                <span style={{ fontSize: 17, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.tx }}>IVPilot</span>
              </div>
              <p style={{ fontSize: 13, color: T.txM, lineHeight: 1.7, maxWidth: 240 }}>The first software designed exclusively for IV hydration therapy clinics.</p>
            </div>
            {[
              { title: 'Product', links: ['Features', 'Pricing', 'How It Works', 'Testimonials'] },
              { title: 'Compare', links: ['vs Mangomint', 'vs Pabau', 'vs Jane App'] },
              { title: 'Company', links: ['Contact Us', 'Privacy Policy', 'Terms of Service'] },
            ].map(col => (
              <div key={col.title}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.txM, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>{col.title}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {col.links.map(l => <span key={l} style={{ fontSize: 14, color: T.txD, cursor: 'default' }}>{l}</span>)}
                </div>
              </div>
            ))}
          </div>
          <div style={{ paddingTop: 24, borderTop: `1px solid ${T.b1}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <p style={{ fontSize: 13, color: T.txD }}>© 2025 IVPilot. All rights reserved.</p>
            <p style={{ fontSize: 13, color: T.txD }}>hello@ivpilot.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
// ═══════════════════════════════════════════════════════

function ClientPortalView({ clientId, clinicId, clinicName }) {
  const toCamel = obj => { if (!obj) return obj; const r = {}; for (const [k,v] of Object.entries(obj)) r[k.replace(/_([a-z])/g,(_,l)=>l.toUpperCase())]=v; return r; };
  const [client, setClient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [visits, setVisits] = useState([]);
  const [messages, setMessages] = useState([]);
  const [existingForm, setExistingForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('intake');
  const bottomRef = useRef(null);
  const sigRef = useRef(null);
  const [intake, setIntake] = useState({ healthConditions:'', medications:'', allergies:'', bloodPressure:'', pregnant:false, smoker:false, heartCondition:false, kidneyDisease:false, diabetes:false });
  const [intakeSaving, setIntakeSaving] = useState(false);
  const [intakeSaved, setIntakeSaved] = useState(false);
  const [intakeError, setIntakeError] = useState('');
  const [signed, setSigned] = useState(false);
  const [sigData, setSigData] = useState('');
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: cl }, { data: appts }, { data: vis }, { data: msgs }, { data: form }] = await Promise.all([
          supabase.from('clients').select('*').eq('id', clientId).single(),
          supabase.from('appointments').select('*').eq('client_id', clientId).eq('clinic_id', clinicId).order('scheduled_at', { ascending: false }),
          supabase.from('visits').select('*').eq('client_id', clientId).eq('clinic_id', clinicId).order('started_at', { ascending: false }),
          supabase.from('messages').select('*').eq('client_id', clientId).eq('clinic_id', clinicId).order('sent_at', { ascending: true }),
          supabase.from('forms').select('*').eq('client_id', clientId).eq('clinic_id', clinicId).maybeSingle(),
        ]);
        setClient(toCamel(cl));
        setAppointments((appts||[]).map(toCamel));
        setVisits((vis||[]).map(toCamel));
        setMessages((msgs||[]).map(toCamel));
        if (form) { const f = toCamel(form); setExistingForm(f); setIntake({ healthConditions:f.healthConditions||'', medications:f.medications||'', allergies:f.allergies||'', bloodPressure:f.bloodPressure||'', pregnant:!!f.pregnant, smoker:!!f.smoker, heartCondition:!!f.heartCondition, kidneyDisease:!!f.kidneyDisease, diabetes:!!f.diabetes }); if (f.signed) { setSigned(true); setSigData(f.signatureData||''); } }
      } catch(e) { console.error('Portal load error:', e); }
      finally { setLoading(false); }
    };
    load();
  }, [clientId, clinicId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const startDraw = e => { setDrawing(true); const cv = sigRef.current; const r = cv.getBoundingClientRect(); const ctx = cv.getContext('2d'); ctx.beginPath(); ctx.moveTo((e.clientX||e.touches[0].clientX)-r.left,(e.clientY||e.touches[0].clientY)-r.top); };
  const draw = e => { if (!drawing) return; e.preventDefault(); const cv = sigRef.current; const r = cv.getBoundingClientRect(); const ctx = cv.getContext('2d'); ctx.lineWidth=2; ctx.lineCap='round'; ctx.strokeStyle='#0ea5e9'; ctx.lineTo((e.clientX||e.touches[0].clientX)-r.left,(e.clientY||e.touches[0].clientY)-r.top); ctx.stroke(); };
  const endDraw = () => { setDrawing(false); if (sigRef.current) setSigData(sigRef.current.toDataURL()); };
  const clearSig = () => { const cv = sigRef.current; if (cv) { cv.getContext('2d').clearRect(0,0,cv.width,cv.height); setSigData(''); setSigned(false); } };

  const saveIntake = async () => {
    if (!sigData) { setIntakeError('Please sign the consent waiver before submitting.'); return; }
    setIntakeSaving(true); setIntakeError('');
    try {
      const payload = { clinic_id:clinicId, client_id:clientId, health_conditions:intake.healthConditions, medications:intake.medications, allergies:intake.allergies, blood_pressure:intake.bloodPressure, pregnant:intake.pregnant, smoker:intake.smoker, heart_condition:intake.heartCondition, kidney_disease:intake.kidneyDisease, diabetes:intake.diabetes, signed:true, signed_at:new Date().toISOString(), signature_data:sigData };
      if (existingForm && existingForm.id) { await supabase.from('forms').update(payload).eq('id', existingForm.id); }
      else { const { data } = await supabase.from('forms').insert(payload).select().single(); if (data) setExistingForm(toCamel(data)); }
      setSigned(true); setIntakeSaved(true);
    } catch(e) { setIntakeError('Failed to save. Please try again.'); console.error(e); }
    finally { setIntakeSaving(false); }
  };

  const sendMsg = async () => {
    if (!msgText.trim() || sending) return;
    setSending(true);
    const msg = { clinic_id:clinicId, client_id:clientId, sender:'client', sender_name:client ? clientId : 'Client', text:msgText.trim(), sent_at:new Date().toISOString(), read:false };
    try { const { data } = await supabase.from('messages').insert([msg]).select().single(); if (data) setMessages(p=>[...p, toCamel(data)]); setMsgText(''); }
    catch(e) { console.error(e); } finally { setSending(false); }
  };

  const fmtDate = s => { if (!s) return '—'; return new Date(s).toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'}); };
  const fmtTime = s => { if (!s) return '—'; return new Date(s).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}); };
  const statusColor = s => ({ scheduled:'#0ea5e9','in-progress':'#f59e0b',completed:'#10b981',cancelled:'#f43f5e','no-show':'#6b7280' }[s]||'#6b7280');
  const iField = { width:'100%', background:'#172035', border:'1px solid #1a2b40', borderRadius:8, padding:'10px 12px', color:'#dce8ff', fontSize:14, boxSizing:'border-box', fontFamily:'Arial,sans-serif' };

  if (loading) return <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#060c18'}}><div style={{color:'#7a8fad',fontSize:14}}>Loading your portal...</div></div>;

  return (
    <div style={{minHeight:'100vh',background:'#060c18',color:'#dce8ff',fontFamily:'Arial,sans-serif'}}>
      <div style={{background:'#0b1321',borderBottom:'1px solid #1a2b40',padding:'16px 24px',display:'flex',alignItems:'center',gap:12}}>
        <div style={{width:40,height:40,borderRadius:10,background:'linear-gradient(135deg,#0ea5e9,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>💉</div>
        <div><div style={{fontSize:16,fontWeight:700,color:'#dce8ff'}}>{clinicName}</div><div style={{fontSize:13,color:'#7a8fad'}}>Client Portal · {client ? client.firstName + ' ' + client.lastName : ''}</div></div>
      </div>
      <div style={{maxWidth:680,margin:'0 auto',padding:'24px 16px'}}>
        <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap'}}>
          {[['intake','📋 Intake Form'],['appointments','📅 Appointments'],['visits','💉 Visit History'],['messages','💬 Messages']].map(([id,label]) => (
            <button key={id} onClick={()=>setTab(id)} style={{padding:'8px 16px',borderRadius:8,border:'none',cursor:'pointer',fontSize:13,fontWeight:600,background:tab===id?'#0ea5e9':'#0b1321',color:tab===id?'#fff':'#7a8fad'}}>{label}{id==='intake'&&signed?' ✓':''}</button>
          ))}
        </div>
        {tab==='intake' && (
          <div style={{display:'flex',flexDirection:'column',gap:16}}>
            {intakeSaved && <div style={{background:'#10b98122',border:'1px solid #10b98144',borderRadius:8,padding:'12px 16px',color:'#10b981',fontSize:14}}>✓ Intake form submitted successfully.</div>}
            {signed && !intakeSaved && <div style={{background:'#0ea5e922',border:'1px solid #0ea5e944',borderRadius:8,padding:'12px 16px',color:'#0ea5e9',fontSize:14}}>✓ Intake form already on file. You can update and resubmit below.</div>}
            {intakeError && <div style={{background:'#f43f5e22',border:'1px solid #f43f5e44',borderRadius:8,padding:'12px 16px',color:'#f43f5e',fontSize:14}}>{intakeError}</div>}
            <div style={{background:'#0b1321',border:'1px solid #1a2b40',borderRadius:12,padding:20}}>
              <h3 style={{color:'#dce8ff',fontSize:16,fontWeight:700,margin:'0 0 16px'}}>Medical History</h3>
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>Current Health Conditions</label><textarea value={intake.healthConditions} onChange={e=>setIntake(p=>({...p,healthConditions:e.target.value}))} rows={2} placeholder="e.g. migraines, fatigue..." style={{...iField,resize:'none'}}/></div>
                <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>Current Medications</label><textarea value={intake.medications} onChange={e=>setIntake(p=>({...p,medications:e.target.value}))} rows={2} placeholder="e.g. Lisinopril 10mg..." style={{...iField,resize:'none'}}/></div>
                <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>Known Allergies</label><input value={intake.allergies} onChange={e=>setIntake(p=>({...p,allergies:e.target.value}))} placeholder="e.g. Penicillin..." style={iField}/></div>
                <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>Blood Pressure (if known)</label><input value={intake.bloodPressure} onChange={e=>setIntake(p=>({...p,bloodPressure:e.target.value}))} placeholder="e.g. 120/80" style={iField}/></div>
              </div>
            </div>
            <div style={{background:'#0b1321',border:'1px solid #1a2b40',borderRadius:12,padding:20}}>
              <h3 style={{color:'#dce8ff',fontSize:16,fontWeight:700,margin:'0 0 16px'}}>Health Screening</h3>
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {[['pregnant','Are you pregnant or breastfeeding?'],['heartCondition','Do you have a heart condition?'],['kidneyDisease','Do you have kidney disease?'],['diabetes','Do you have diabetes?'],['smoker','Do you smoke?']].map(([key,label]) => (
                  <label key={key} style={{display:'flex',alignItems:'center',gap:12,cursor:'pointer',padding:'10px 12px',background:'#172035',borderRadius:8}}>
                    <input type="checkbox" checked={intake[key]} onChange={e=>setIntake(p=>({...p,[key]:e.target.checked}))} style={{width:18,height:18,accentColor:'#0ea5e9'}}/>
                    <span style={{fontSize:14,color:'#dce8ff'}}>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div style={{background:'#0b1321',border:'1px solid #1a2b40',borderRadius:12,padding:20}}>
              <h3 style={{color:'#dce8ff',fontSize:16,fontWeight:700,margin:'0 0 8px'}}>Consent & Waiver</h3>
              <p style={{color:'#7a8fad',fontSize:13,lineHeight:1.6,margin:'0 0 16px'}}>I consent to receive IV therapy services. I confirm the above information is accurate. I understand IV therapy carries inherent risks and I voluntarily consent to treatment.</p>
              <div style={{marginBottom:8}}>
                <div style={{fontSize:12,color:'#7a8fad',marginBottom:6}}>Sign below:</div>
                <canvas ref={sigRef} width={600} height={120} style={{width:'100%',height:120,background:'#172035',borderRadius:8,border:'1px solid #1a2b40',cursor:'crosshair',touchAction:'none'}}
                  onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}/>
                <button onClick={clearSig} style={{fontSize:12,color:'#7a8fad',background:'none',border:'none',cursor:'pointer',padding:'4px 0',marginTop:4}}>Clear signature</button>
              </div>
              {sigData && <div style={{fontSize:12,color:'#10b981',marginBottom:8}}>✓ Signature captured</div>}
            </div>
            <button onClick={saveIntake} disabled={intakeSaving} style={{background:'#10b981',color:'#fff',border:'none',borderRadius:10,padding:'14px 24px',fontSize:16,fontWeight:700,cursor:'pointer'}}>{intakeSaving?'Submitting...':signed&&!intakeSaved?'Update & Resubmit':'Submit Intake Form →'}</button>
          </div>
        )}
        {tab==='appointments' && <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {appointments.length===0 && <div style={{color:'#7a8fad',textAlign:'center',padding:40}}>No appointments found.</div>}
          {appointments.map(a=>(<div key={a.id} style={{background:'#0b1321',border:'1px solid #1a2b40',borderRadius:12,padding:16}}>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:4,alignSelf:'stretch',background:statusColor(a.status),borderRadius:2}}/>
              <div style={{flex:1}}><div style={{fontSize:15,fontWeight:600,color:'#dce8ff'}}>{fmtDate(a.scheduledAt)}</div><div style={{fontSize:13,color:'#7a8fad'}}>{fmtTime(a.scheduledAt)}</div></div>
              <div style={{padding:'4px 10px',borderRadius:6,fontSize:12,fontWeight:600,background:statusColor(a.status)+'22',color:statusColor(a.status)}}>{a.status}</div>
            </div>
          </div>))}
        </div>}
        {tab==='visits' && <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {visits.length===0 && <div style={{color:'#7a8fad',textAlign:'center',padding:40}}>No visits recorded yet.</div>}
          {visits.map(v=>(<div key={v.id} style={{background:'#0b1321',border:'1px solid #1a2b40',borderRadius:12,padding:16}}>
            <div style={{fontSize:15,fontWeight:600,color:'#dce8ff',marginBottom:4}}>{v.formulaName||'IV Session'}</div>
            <div style={{fontSize:13,color:'#7a8fad'}}>{fmtDate(v.startedAt)}</div>
            {v.amountCharged && <div style={{fontSize:13,color:'#10b981',marginTop:4}}>${v.amountCharged}</div>}
          </div>))}
        </div>}
        {tab==='messages' && <div>
          <div style={{background:'#0b1321',border:'1px solid #1a2b40',borderRadius:12,padding:16,minHeight:300,maxHeight:400,overflowY:'auto',marginBottom:12,display:'flex',flexDirection:'column',gap:10}}>
            {messages.length===0 && <div style={{color:'#7a8fad',textAlign:'center',padding:40}}>No messages yet.</div>}
            {messages.map(m=>(<div key={m.id} style={{display:'flex',flexDirection:'column',alignItems:m.sender==='client'?'flex-end':'flex-start'}}>
              <div style={{maxWidth:'80%',background:m.sender==='client'?'#0ea5e9':'#172035',borderRadius:10,padding:'10px 14px',fontSize:14,color:'#dce8ff',lineHeight:1.5}}>{m.text}</div>
              <div style={{fontSize:11,color:'#3d5068',marginTop:2}}>{m.sender==='client'?'You':clinicName} · {fmtTime(m.sentAt)}</div>
            </div>))}
            <div ref={bottomRef}/>
          </div>
          <div style={{display:'flex',gap:8}}>
            <textarea value={msgText} onChange={e=>setMsgText(e.target.value)} placeholder="Type a message..." rows={2} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMsg();}}} style={{flex:1,background:'#0b1321',border:'1px solid #1a2b40',borderRadius:8,padding:'10px 14px',color:'#dce8ff',fontSize:14,resize:'none',fontFamily:'Arial,sans-serif'}}/>
            <button onClick={sendMsg} disabled={sending||!msgText.trim()} style={{padding:'0 20px',background:'#0ea5e9',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:14}}>{sending?'...':'Send'}</button>
          </div>
        </div>}
      </div>
    </div>
  );
}

function BookingView({ clinicId }) {
  const [clinic, setClinic] = useState(null);
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('form');
  const [error, setError] = useState('');
  const [form, setForm] = useState({firstName:'',lastName:'',email:'',phone:'',formulaId:'',date:'',time:'10:00',notes:''});
  const set = k => v => setForm(f=>({...f,[k]:v}));

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: cl },{ data: fmls }] = await Promise.all([
          supabase.from('clinics').select('*').eq('id',clinicId).single(),
          supabase.from('formulas').select('*').eq('clinic_id',clinicId).order('name'),
        ]);
        setClinic(cl); setFormulas(fmls||[]);
      } catch(e) { console.error(e); } finally { setLoading(false); }
    };
    load();
  }, [clinicId]);

  const selectedFormula = formulas.find(f=>f.id===form.formulaId);

  const handleSubmit = async () => {
    if (!form.firstName||!form.lastName||!form.email||!form.formulaId||!form.date) { setError('Please fill in all required fields.'); return; }
    setSubmitting(true); setError('');
    try {
      let clientId;
      const { data: existing } = await supabase.from('clients').select('id').eq('clinic_id',clinicId).eq('email',form.email).maybeSingle();
      if (existing) { clientId = existing.id; }
      else { const { data: nc } = await supabase.from('clients').insert({clinic_id:clinicId,first_name:form.firstName,last_name:form.lastName,email:form.email,phone:form.phone}).select().single(); clientId = nc && nc.id; }
      await supabase.from('appointments').insert({clinic_id:clinicId,client_id:clientId,scheduled_at:new Date(form.date+'T'+form.time+':00').toISOString(),duration:60,formula_id:form.formulaId,status:'scheduled',notes:form.notes||'Booked online by '+form.firstName+' '+form.lastName});
      await fetch('/api/send-email',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({type:'appointment_confirmation',to:form.email,clientName:form.firstName+' '+form.lastName,clinicName:clinic&&clinic.name,appointmentDate:new Date(form.date+'T'+form.time).toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'}),appointmentTime:new Date(form.date+'T'+form.time).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}),formula:selectedFormula&&selectedFormula.name,portalLink:clientId?window.location.origin+'?portal='+clientId+'&c='+clinicId+'&cl='+encodeURIComponent((clinic&&clinic.name)||'Your IV Clinic'):null})});
      setStep('success');
    } catch(e) { setError('Booking failed. Please try again.'); console.error(e); } finally { setSubmitting(false); }
  };

  if (loading) return <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#060c18'}}><div style={{color:'#7a8fad',fontSize:14}}>Loading...</div></div>;
  if (step==='success') return <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#060c18',padding:24}}><div style={{background:'#0b1321',border:'1px solid #1a2b40',borderRadius:16,padding:40,textAlign:'center',maxWidth:440}}><div style={{fontSize:48,marginBottom:16}}>✅</div><h2 style={{color:'#dce8ff',fontSize:22,fontWeight:800,margin:'0 0 8px'}}>Appointment Booked!</h2><p style={{color:'#7a8fad',fontSize:15,margin:0}}>Check your email for confirmation and your client portal link.</p></div></div>;

  return (
    <div style={{minHeight:'100vh',background:'#060c18',color:'#dce8ff',fontFamily:'Arial,sans-serif'}}>
      <div style={{background:'#0b1321',borderBottom:'1px solid #1a2b40',padding:'16px 24px',display:'flex',alignItems:'center',gap:12}}>
        <div style={{width:40,height:40,borderRadius:10,background:'linear-gradient(135deg,#0ea5e9,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>💉</div>
        <div><div style={{fontSize:16,fontWeight:700,color:'#dce8ff'}}>{clinic&&clinic.name||'IV Clinic'}</div><div style={{fontSize:13,color:'#7a8fad'}}>Online Booking</div></div>
      </div>
      <div style={{maxWidth:560,margin:'0 auto',padding:'32px 16px'}}>
        <h2 style={{fontSize:22,fontWeight:800,color:'#dce8ff',marginBottom:24}}>Book Your IV Session</h2>
        {error && <div style={{background:'#f43f5e22',border:'1px solid #f43f5e44',borderRadius:8,padding:'10px 14px',color:'#f43f5e',marginBottom:16,fontSize:14}}>{error}</div>}
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>First Name *</label><input value={form.firstName} onChange={e=>set('firstName')(e.target.value)} style={{width:'100%',background:'#0b1321',border:'1px solid #1a2b40',borderRadius:8,padding:'10px 12px',color:'#dce8ff',fontSize:14,boxSizing:'border-box'}}/></div>
            <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>Last Name *</label><input value={form.lastName} onChange={e=>set('lastName')(e.target.value)} style={{width:'100%',background:'#0b1321',border:'1px solid #1a2b40',borderRadius:8,padding:'10px 12px',color:'#dce8ff',fontSize:14,boxSizing:'border-box'}}/></div>
          </div>
          <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>Email *</label><input type="email" value={form.email} onChange={e=>set('email')(e.target.value)} style={{width:'100%',background:'#0b1321',border:'1px solid #1a2b40',borderRadius:8,padding:'10px 12px',color:'#dce8ff',fontSize:14,boxSizing:'border-box'}}/></div>
          <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>Phone</label><input type="tel" value={form.phone} onChange={e=>set('phone')(e.target.value)} style={{width:'100%',background:'#0b1321',border:'1px solid #1a2b40',borderRadius:8,padding:'10px 12px',color:'#dce8ff',fontSize:14,boxSizing:'border-box'}}/></div>
          <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>IV Formula *</label>
            <select value={form.formulaId} onChange={e=>set('formulaId')(e.target.value)} style={{width:'100%',background:'#0b1321',border:'1px solid #1a2b40',borderRadius:8,padding:'10px 12px',color:form.formulaId?'#dce8ff':'#7a8fad',fontSize:14,boxSizing:'border-box'}}>
              <option value="">Select a formula...</option>
              {formulas.map(f=><option key={f.id} value={f.id}>{f.name} — ${f.price}</option>)}
            </select>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>Date *</label><input type="date" value={form.date} onChange={e=>set('date')(e.target.value)} min={new Date().toISOString().slice(0,10)} style={{width:'100%',background:'#0b1321',border:'1px solid #1a2b40',borderRadius:8,padding:'10px 12px',color:'#dce8ff',fontSize:14,boxSizing:'border-box'}}/></div>
            <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>Time</label><input type="time" value={form.time} onChange={e=>set('time')(e.target.value)} style={{width:'100%',background:'#0b1321',border:'1px solid #1a2b40',borderRadius:8,padding:'10px 12px',color:'#dce8ff',fontSize:14,boxSizing:'border-box'}}/></div>
          </div>
          <div><label style={{fontSize:12,color:'#7a8fad',display:'block',marginBottom:4}}>Notes</label><textarea value={form.notes} onChange={e=>set('notes')(e.target.value)} rows={3} placeholder="Any special requests or health notes..." style={{width:'100%',background:'#0b1321',border:'1px solid #1a2b40',borderRadius:8,padding:'10px 12px',color:'#dce8ff',fontSize:14,resize:'none',fontFamily:'Arial,sans-serif',boxSizing:'border-box'}}/></div>
          {selectedFormula && <div style={{background:'#172035',borderRadius:8,padding:'12px 16px',fontSize:14,color:'#dce8ff'}}>💉 <strong>{selectedFormula.name}</strong> — ${selectedFormula.price}</div>}
          <button onClick={handleSubmit} disabled={submitting} style={{background:'#0ea5e9',color:'#fff',border:'none',borderRadius:10,padding:'14px 24px',fontSize:16,fontWeight:700,cursor:'pointer',marginTop:8}}>{submitting?'Booking...':'Book Appointment →'}</button>
        </div>
      </div>
    </div>
  );
}
export default function IVPilot() {
  useGlobalStyles();
  const isMobile = useIsMobile();

  // Global theme — applies to entire app via CSS variables on body
  const [theme, setTheme] = useState(() => localStorage.getItem('ivp_theme') || 'dark');
  const isLight = theme === 'light';

  useEffect(() => {
    const body = document.body;
    if (isLight) {
      body.style.background = '#f8fafc';
      body.style.color = '#0f172a';
      // Override CSS vars for light mode
      body.setAttribute('data-theme', 'light');
    } else {
      body.style.background = '#060c18';
      body.style.color = '#dce8ff';
      body.setAttribute('data-theme', 'dark');
    }
  }, [isLight]);

  // Light theme color overrides
  const LC = isLight ? {
    ...C,
    bg: '#f8fafc', s1: '#ffffff', s2: '#f1f5f9', s3: '#e8edf4',
    b1: '#cbd5e1', b2: '#94a3b8',
    tx: '#0f172a', txM: '#475569', txD: '#94a3b8', txDark: '#64748b',
    white: '#0f172a',
  } : C;

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('ivp_theme', next);
  };

  const portalParams = (new URLSearchParams(window.location.search)).get('portal')
    ? { clientId: new URLSearchParams(window.location.search).get('portal'), clinicId: new URLSearchParams(window.location.search).get('c'), clinicName: new URLSearchParams(window.location.search).get('cl') || 'Your IV Clinic' }
    : null;
  const bookingClinicId = new URLSearchParams(window.location.search).get('book') || null;
  const chatParams = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('chat') === '1') return { isClientChat: true, clinicId: p.get('c'), clientId: p.get('p'), clientName: p.get('n') || 'Client', clinicName: p.get('cl') || 'Your IV Clinic' };
    return { isClientChat: false };
  }, []);

  const [auth, setAuth] = useState(() => DB.get('session'));
  const [authPage, setAuthPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('invite') === '1') return 'register';
    if (params.get('signin') === '1') return 'login';
    if (params.get('register') === '1') return 'register';
    const ref = params.get('ref');
    if (ref) DB.set('referralCode', ref);
    return 'login';
  });
  
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [page, setPage] = useState(() => sessionStorage.getItem('ivp_page') || 'dashboard');
  const [pageParams, setPageParams] = useState({});

  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [visits, setVisits] = useState([]);
  const [formulas, setFormulas] = useState(DEFAULT_FORMULAS);
  const [staff, setStaff] = useState([]);
  const [forms, setForms] = useState([]);
  const [clinic, setClinic] = useState(() => auth ? (auth.clinic || null) : null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Reload data from Supabase on page refresh if session exists
  const isValidUUID = id => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  useEffect(() => {
    const clinicId = auth?.user?.clinicId;
    if (clinicId && isValidUUID(clinicId)) {
      setClinic(auth.clinic || null);
      (async () => {
        try {
          if (auth?.access_token) {
            await supabase.auth.setSession({ access_token: auth.access_token, refresh_token: auth.refresh_token || '' });
          }
          const { data: refreshed } = await supabase.auth.refreshSession();
          if (refreshed?.session) {
            const updated = { ...auth, access_token: refreshed.session.access_token, refresh_token: refreshed.session.refresh_token };
            DB.set('session', updated);
            setAuth(updated);
          }
        } catch(e) { console.warn('Session refresh failed:', e); }
        loadClinicData(clinicId);
      })();
    } else if (auth && (!clinicId || !isValidUUID(clinicId))) {
      // Bad session — clear it and force re-login
      DB.del('session');
      setAuth(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setPaymentSuccess(true);
      window.history.replaceState({}, '', '/');
      setTimeout(() => setPaymentSuccess(false), 6000);
    }
    if (params.get('payment') === 'cancelled') window.history.replaceState({}, '', '/');
  }, []);

  const go = useCallback((p, params = {}) => { setPage(p); setPageParams(params); sessionStorage.setItem('ivp_page', p); }, []);
  const handleSetPage = useCallback((p) => { setPage(p); setPageParams({}); sessionStorage.setItem('ivp_page', p); }, []);

  // Helper to convert camelCase to snake_case for Supabase
  const toSnake = obj => { const r = {}; for (const [k, v] of Object.entries(obj || {})) r[k.replace(/[A-Z]/g, l => '_' + l.toLowerCase())] = v; return r; };
  const toCamel = obj => { const r = {}; for (const [k, v] of Object.entries(obj || {})) r[k.replace(/_([a-z])/g, (_, l) => l.toUpperCase())] = v; return r; };

  const sbSave = async (table, data, setter) => {
    setter(data); // Optimistic update
    try {
      const { supabase } = await import('./supabase');
      // Upsert the changed item (last item if array)
      if (Array.isArray(data) && data.length > 0) {
        const last = data[data.length - 1];
        if (last && !last._deleted) await supabase.from(table).upsert(toSnake(last));
      }
    } catch (e) { console.error('Supabase save error:', table, e); }
  };

  const sbDelete = async (table, id) => {
    try {
      const { supabase } = await import('./supabase');
      await supabase.from(table).delete().eq('id', id);
    } catch (e) { console.error('Supabase delete error:', table, e); }
  };

  const sbUpdate = async (table, item) => {
    try {
      const { supabase } = await import('./supabase');
      const { id, ...rest } = toSnake(item);
      await supabase.from(table).update(rest).eq('id', item.id);
    } catch (e) { console.error('Supabase update error:', table, e); }
  };

  const loadClinicData = async (clinicId) => {
    try {
      const [
        { data: cls }, { data: appts }, { data: vis },
        { data: fms }, { data: fmls }, { data: stf },
      ] = await Promise.all([
        supabase.from('clients').select('*').eq('clinic_id', clinicId).order('created_at', { ascending: false }),
        supabase.from('appointments').select('*').eq('clinic_id', clinicId).order('scheduled_at', { ascending: true }),
        supabase.from('visits').select('*').eq('clinic_id', clinicId).order('started_at', { ascending: false }),
        supabase.from('forms').select('*').eq('clinic_id', clinicId),
        supabase.from('formulas').select('*').eq('clinic_id', clinicId),
        supabase.from('staff_members').select('*').eq('clinic_id', clinicId),
      ]);
      setClients((cls || []).map(toCamel));
      setAppointments((appts || []).map(toCamel));
      setVisits((vis || []).map(toCamel));
      setForms((fms || []).map(toCamel));
      setFormulas((fmls || []).length > 0 ? fmls.map(toCamel) : DEFAULT_FORMULAS);
      setStaff((stf || []).map(toCamel));
    } catch (e) { console.error('loadClinicData error:', e); }
  };

  const save = {
    clients: d => { setClients(d); },
    appts: d => { setAppointments(d); },
    visits: d => { setVisits(d); },
    formulas: d => { setFormulas(d); },
    staff: d => { setStaff(d); },
    forms: d => { setForms(d); },
  };

  const handleLogin = async (data) => {
    const clinicId = data.clinic?.id || data.staff_member?.clinic_id;
    const session = { user: { ...data.user, clinicId }, clinic: data.clinic, access_token: data.session?.access_token, refresh_token: data.session?.refresh_token };
    DB.set('session', session);
    setAuth(session);
    setClinic(data.clinic);
    if (data.session?.access_token) {
      await supabase.auth.setSession({ access_token: data.session.access_token, refresh_token: data.session.refresh_token || '' });
    }
    await loadClinicData(clinicId);
    setPage('dashboard');
    sessionStorage.setItem('ivp_page', 'dashboard');
  };

  const handleLogout = async () => { DB.del('session'); await supabase.auth.signOut(); setAuth(null); setClinic(null); setClients([]); setStaff([]); setVisits([]); setForms([]); setAppointments([]); setFormulas([]); setAuthPage('login'); sessionStorage.removeItem('ivp_page'); };
  const handleLogoClick = () => { window.location.href = 'https://ivpilot.com'; };
  const handleUpdateClinic = async (updates) => { const updated = { ...clinic, ...updates }; setClinic(updated); DB.set('session', { ...auth, clinic: updated }); try { await supabase.from('clinics').update(toSnake(updates)).eq('id', clinic.id); } catch (e) { console.error('updateClinic error:', e); } };

  if (chatParams.isClientChat) return <ClientChatView {...chatParams} />;
  if (portalParams) return <ClientPortalView {...portalParams} />;
  if (bookingClinicId) return <BookingView clinicId={bookingClinicId} />;
  if (!auth) {
    return authPage === 'login'
      ? <LoginView onLogin={handleLogin} onSwitch={() => setAuthPage('register')} />
      : <RegisterView onLogin={handleLogin} onSwitch={() => setAuthPage('login')} />;
  }

  const clinicId = auth.user.clinicId;

  // Apply theme — overrides C globally for all child components
  if (isLight) {
    Object.assign(C, LC);
  } else {
    C.bg = '#060c18'; C.s1 = '#0b1321'; C.s2 = '#101c2e'; C.s3 = '#172035';
    C.b1 = '#1a2b40'; C.b2 = '#243550';
    C.tx = '#dce8ff'; C.txM = '#7a8fad'; C.txD = '#3d5068'; C.white = '#ffffff';
  }

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return <DashboardView clients={clients} visits={visits} appointments={appointments} staff={staff} formulas={formulas} go={go} user={auth.user} />;
      case 'inbox': return <InboxView clients={clients} clinicId={clinicId} clinic={clinic} />;
      case 'clients': return <ClientsView clients={clients} forms={forms} visits={visits} go={go}
        onAddClient={async c => { const tmp=uid(); setClients(p=>[{...c,id:tmp,clinicId},...p]); try { const {data:r,error:e}=await supabase.from('clients').insert(toSnake({...c,clinicId})).select().single(); if(e) throw e; if(r) setClients(p=>p.map(x=>x.id===tmp?toCamel(r):x)); } catch(err) { setClients(p=>p.filter(x=>x.id!==tmp)); await logError({clinicId,userId:auth?.user?.id,action:'add_client',error:err}); alert('Failed to add client. Error logged.'); } }}
        onDeleteClient={async id => { setClients(p=>p.filter(c=>c.id!==id)); await supabase.from('clients').delete().eq('id',id); }} />;
      case 'clientDetail': return <ClientDetailView clientId={pageParams.clientId} clients={clients} visits={visits} forms={forms} appointments={appointments} formulas={formulas} staff={staff} go={go} clinicId={clinicId}
        onUpdateClient={async c => { setClients(p=>p.map(x=>x.id===c.id?c:x)); await supabase.from('clients').update(toSnake(c)).eq('id',c.id); }}
        onSaveForm={async f => { const exists=forms.some(x=>x.clientId===f.clientId); setForms(p=>exists?p.map(x=>x.clientId===f.clientId?f:x):[...p,f]); if(exists){await supabase.from('forms').update(toSnake(f)).eq('client_id',f.clientId);}else{await supabase.from('forms').insert(toSnake(f));} }}
        onAddVisit={async v => { const tmp=uid(); setVisits(p=>[{...v,id:tmp,clinicId},...p]); try { const {data:r,error:e}=await supabase.from('visits').insert(toSnake({...v,clinicId})).select().single(); if(e) throw e; if(r) setVisits(p=>p.map(x=>x.id===tmp?toCamel(r):x)); } catch(err) { setVisits(p=>p.filter(x=>x.id!==tmp)); await logError({clinicId,userId:auth?.user?.id,action:'add_visit',error:err}); alert('Failed to save visit. Error logged.'); } }} />;
      case 'appointments': return <AppointmentsView appointments={appointments} clients={clients} staff={staff} formulas={formulas} go={go} clinicId={clinicId} clinic={clinic}
        onAdd={async a => { const tmp=uid(); setAppointments(p=>[...p,{...a,id:tmp,clinicId}]); try { const {data:r,error:e}=await supabase.from('appointments').insert(toSnake({...a,clinicId})).select().single(); if(e) throw e; if(r) setAppointments(p=>p.map(x=>x.id===tmp?toCamel(r):x)); } catch(err) { setAppointments(p=>p.filter(x=>x.id!==tmp)); await logError({clinicId,userId:auth?.user?.id,action:'add_appointment',error:err}); alert('Failed to book appointment. Error logged.'); } }}
        onUpdate={async a => { setAppointments(p=>p.map(x=>x.id===a.id?a:x)); await supabase.from('appointments').update(toSnake(a)).eq('id',a.id); }}
        initNew={pageParams.new} initClientId={pageParams.prefillClientId} />;
      case 'visits': return <VisitsView visits={visits} clients={clients} staff={staff} formulas={formulas} go={go} clinicId={clinicId}
        onAdd={async v => { const tmp=uid(); setVisits(p=>[{...v,id:tmp,clinicId},...p]); try { const {data:r,error:e}=await supabase.from('visits').insert(toSnake({...v,clinicId})).select().single(); if(e) throw e; if(r) setVisits(p=>p.map(x=>x.id===tmp?toCamel(r):x)); } catch(err) { setVisits(p=>p.filter(x=>x.id!==tmp)); await logError({clinicId,userId:auth?.user?.id,action:'add_visit',error:err}); alert('Failed to save visit. Error logged.'); } }} initNew={pageParams.new} />;
      case 'formulas': return <FormulasView formulas={formulas} visits={visits}
        onAdd={async f => { const tmp=uid(); setFormulas(p=>[...p,{...f,id:tmp,clinicId}]); try { const {data:r,error:e}=await supabase.from('formulas').insert(toSnake({...f,clinicId})).select().single(); if(e) throw e; if(r) setFormulas(p=>p.map(x=>x.id===tmp?toCamel(r):x)); } catch(err) { setFormulas(p=>p.filter(x=>x.id!==tmp)); await logError({clinicId,userId:auth?.user?.id,action:'add_formula',error:err}); alert('Failed to save formula. Error logged.'); } }}
        onDelete={async id => { setFormulas(p=>p.filter(f=>f.id!==id)); await supabase.from('formulas').delete().eq('id',id); }} />;
      case 'staff': return <StaffView staff={staff} visits={visits} clinicId={clinicId} clinic={clinic}
        onAdd={async s => { const tmp=uid(); setStaff(p=>[...p,{...s,id:tmp,clinicId}]); try { const {data:r,error:e}=await supabase.from('staff_members').insert(toSnake({...s,clinicId})).select().single(); if(e) throw e; if(r) setStaff(p=>p.map(x=>x.id===tmp?toCamel(r):x)); } catch(err) { setStaff(p=>p.filter(x=>x.id!==tmp)); await logError({clinicId,userId:auth?.user?.id,action:'add_staff',error:err}); alert('Failed to add staff. Error logged.'); } }}
        onUpdate={async s => { setStaff(p=>p.map(x=>x.id===s.id?s:x)); await supabase.from('staff_members').update(toSnake(s)).eq('id',s.id); }}
        onDelete={async id => { setStaff(p=>p.filter(s=>s.id!==id)); await supabase.from('staff_members').delete().eq('id',id); }} />;
      case 'revenue': return <RevenueView visits={visits} clients={clients} formulas={formulas} />;
      case 'command': return <CommandCenterView user={auth.user} clinic={clinic} />;
      case 'support': return <SupportView user={auth.user} clinic={clinic} />;
      case 'referral': return <ReferralView user={auth.user} clinic={clinic} />;
      case 'import': return <ImportView clinicId={clinicId} onImport={async imported => { const existing=new Set(clients.map(c=>c.email).filter(Boolean)); const newOnes=imported.filter(c=>!existing.has(c.email)).map(c=>({...c,clinicId})); if(newOnes.length>0){ const {data:r}=await supabase.from('clients').insert(newOnes.map(toSnake)).select(); if(r) setClients(p=>[...p,...r.map(toCamel)]); } go('clients'); }} />;
      case 'settings': return <SettingsView clinic={clinic} user={auth.user} onUpdateClinic={handleUpdateClinic} clients={clients} visits={visits} forms={forms} staff={staff} />;
      default: return <DashboardView clients={clients} visits={visits} appointments={appointments} staff={staff} formulas={formulas} go={go} user={auth.user} />;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: LC.bg }}>
      {paymentSuccess && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '16px 24px', background: C.ok, color: C.white, borderRadius: 12, fontSize: 14, fontWeight: 600, boxShadow: `0 8px 32px rgba(16,185,129,0.4)`, display: 'flex', alignItems: 'center', gap: 10, animation: 'fadeIn 0.3s ease' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          🎉 Payment successful! Welcome to IVPilot.
        </div>
      )}
      {/* Desktop sidebar — hidden on mobile */}
      {!isMobile && <Sidebar page={page} setPage={handleSetPage} clinic={clinic} user={auth.user} onLogout={handleLogout} onLogoClick={handleLogoClick} unreadCount={unreadCount} isLight={isLight} toggleTheme={toggleTheme} />}
      {/* Mobile top bar */}
      {isMobile && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: C.s1, borderBottom: `1px solid ${C.b1}`, padding: '0 16px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, #1a6b5c 0%, #0ea88a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 38 38" fill="none"><rect x="11" y="3" width="16" height="13" rx="4" fill="white" opacity="0.95"/><rect x="13" y="5" width="5" height="3" rx="1.5" fill="white" opacity="0.4"/><line x1="19" y1="16" x2="19" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/><rect x="15.5" y="22" width="7" height="5" rx="2" fill="white" opacity="0.8"/><line x1="19" y1="27" x2="19" y2="33" stroke="white" strokeWidth="2" strokeLinecap="round"/><path d="M16 33 Q19 36 22 33" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/><ellipse cx="19" cy="19.5" rx="1.2" ry="1.5" fill="#0ea88a"/></svg>
            </div>
            <span style={{ fontSize: 17, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.tx }}>IVPilot</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={toggleTheme} style={{ width: 32, height: 32, borderRadius: 8, background: C.s3, border: `1px solid ${C.b1}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{isLight ? '🌙' : '☀️'}</button>
            <button onClick={handleLogout} style={{ width: 32, height: 32, borderRadius: 8, background: C.s3, border: `1px solid ${C.b1}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.txM} strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
        </div>
      )}
      <main style={{ flex: 1, overflow: 'auto', minWidth: 0, paddingTop: isMobile ? 56 : 0, paddingBottom: isMobile ? 70 : 0 }}>{renderPage()}</main>
      {/* Mobile bottom tab bar */}
      {isMobile && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: C.s1, borderTop: `1px solid ${C.b1}`, display: 'flex', alignItems: 'center', height: 64, padding: '0 4px' }}>
          {[
            { id: 'dashboard', label: 'Home', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg> },
            { id: 'clients', label: 'Clients', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg> },
            { id: 'appointments', label: 'Schedule', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> },
            { id: 'inbox', label: 'Inbox', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>, badge: unreadCount },
            { id: 'settings', label: 'More', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg> },
          ].map(item => {
            const active = page === item.id;
            return (
              <button key={item.id} onClick={() => handleSetPage(item.id)}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '6px 4px', background: 'none', border: 'none', color: active ? C.pri : C.txM, position: 'relative' }}>
                <span style={{ position: 'relative' }}>
                  {item.icon}
                  {item.badge > 0 && <span style={{ position: 'absolute', top: -4, right: -4, width: 14, height: 14, background: C.err, borderRadius: '50%', fontSize: 8, fontWeight: 800, color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.badge}</span>}
                </span>
                <span style={{ fontSize: 10, fontWeight: active ? 700 : 400 }}>{item.label}</span>
                {active && <span style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 20, height: 3, background: C.pri, borderRadius: 2 }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
